import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Archive,
  CheckCircle2,
  ChevronDown,
  Clipboard,
  Copy,
  Download,
  Eye,
  FileText,
  FolderOpen,
  Image,
  Loader2,
  Map as MapIcon,
  Pin,
  PinOff,
  RefreshCw,
  Save,
  Search,
  Trash2,
  Upload,
  Wand2,
  XCircle,
} from 'lucide-react';
import {
  ProjectExposeCover,
  ProjectExposePage2,
  ProjectExposePage3,
  ProjectExposePage4,
  WirtschaftlichkeitsAnalyse,
} from '../sections/expose/ProjectExposePages';
import {
  CALCULATED_EXPOSE_FIELD_KEYS,
  DEFAULT_EXPOSE_BITRIX_MAPPING,
  evaluateDefaultFormula,
  EXPOSE_FIELD_OPTIONS,
  EXPOSE_PLACEHOLDER_FIELDS,
  isPvsolExposeFieldKey,
  PVSOL_EXPOSE_FIELD_KEYS,
  EXPOSE_FIELD_DEFAULTS_CONTRACT_KEY,
  REMOVED_BITRIX_DATA_KEYS,
  normalizeExposeMappingFieldsKeepEmpty,
} from '../../lib/exposeAutomation';
import { berechneWirtschaftlichkeit, berechneGewichteteEegVerguetung, getDefaultEegStufen } from '../../lib/exposeWirtschaftlichkeit';

const buttonStyle = {
  minHeight: 34,
  padding: '8px 11px',
  borderRadius: 8,
  border: '1px solid #CBD5E1',
  background: '#FFFFFF',
  color: '#334155',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
  whiteSpace: 'nowrap',
};

const BULK_DRAFT_STORAGE_KEY = 'enpower.exposes.rawBulkDraft.v1';
const SAVED_EXPOSE_DRAFTS_STORAGE_KEY = 'enpower.exposes.savedDrafts.v1';
const BULK_DRAFT_VERSION = 1;
const MAX_DRAFT_RAW_TEXT_LENGTH = 35000;
const TURNKEY_VARIANT_KEY = 'expose-turnkey';
const PROJECT_CACHE_TTL_MS = 2 * 24 * 60 * 60 * 1000;

function getLocalStorage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage || null;
  } catch (_) {
    return null;
  }
}

function compactProjectForDraft(project, keepImages = true) {
  if (!project || typeof project !== 'object') return null;
  const variantData = project.variantData && typeof project.variantData === 'object'
    ? Object.fromEntries(Object.entries(project.variantData).map(([key, value]) => [
      key,
      value && typeof value === 'object'
        ? {
          ...value,
          rawText: String(value.rawText || '').slice(0, MAX_DRAFT_RAW_TEXT_LENGTH),
          projectImage: keepImages ? String(value.projectImage || '') : '',
          lageplanImage: keepImages ? String(value.lageplanImage || '') : '',
        }
        : value,
    ]))
    : undefined;
  return {
    ...project,
    rawText: String(project.rawText || '').slice(0, MAX_DRAFT_RAW_TEXT_LENGTH),
    projectImage: keepImages ? String(project.projectImage || '') : '',
    lageplanImage: keepImages ? String(project.lageplanImage || '') : '',
    ...(variantData ? { variantData } : {}),
  };
}

function loadExposeBulkDraft() {
  const storage = getLocalStorage();
  if (!storage) return {};
  try {
    const raw = storage.getItem(BULK_DRAFT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== BULK_DRAFT_VERSION) return {};
    const projects = Array.isArray(parsed.projects)
      ? parsed.projects.filter((project) => project && typeof project === 'object')
      : [];
    const projectIds = new Set(projects.map((project) => String(project.id || '')).filter(Boolean));
    return {
      projects,
      selectedIds: Array.isArray(parsed.selectedIds)
        ? parsed.selectedIds.map(String).filter((id) => projectIds.has(id))
        : [],
      activeId: projectIds.has(String(parsed.activeId || '')) ? String(parsed.activeId || '') : '',
      bulkField: String(parsed.bulkField || 'status'),
      bulkText: String(parsed.bulkText || ''),
      activeVariantKey: String(parsed.activeVariantKey || ''),
      savedAt: String(parsed.savedAt || ''),
      mediaLimited: parsed.mediaLimited === true,
    };
  } catch (_) {
    return {};
  }
}

function removeExposeBulkDraft() {
  const storage = getLocalStorage();
  if (!storage) return;
  try {
    storage.removeItem(BULK_DRAFT_STORAGE_KEY);
  } catch (_) {
    // Ignore storage errors; the editor itself must keep working.
  }
}

function saveExposeBulkDraft(draft) {
  const storage = getLocalStorage();
  if (!storage) return null;

  const hasProjects = Array.isArray(draft.projects) && draft.projects.length > 0;
  const hasText = String(draft.bulkText || '').trim().length > 0;
  if (!hasProjects && !hasText) {
    removeExposeBulkDraft();
    return null;
  }

  const savedAt = new Date().toISOString();
  const buildPayload = (keepImages) => ({
    version: BULK_DRAFT_VERSION,
    savedAt,
    mediaLimited: !keepImages,
    projects: (draft.projects || []).map((project) => compactProjectForDraft(project, keepImages)).filter(Boolean),
    selectedIds: Array.isArray(draft.selectedIds) ? draft.selectedIds : [],
    activeId: draft.activeId || '',
    bulkField: draft.bulkField || 'status',
    bulkText: draft.bulkText || '',
    activeVariantKey: draft.activeVariantKey || '',
  });

  try {
    const payload = buildPayload(true);
    storage.setItem(BULK_DRAFT_STORAGE_KEY, JSON.stringify(payload));
    return { savedAt, mediaLimited: false };
  } catch (_) {
    try {
      const payload = buildPayload(false);
      storage.setItem(BULK_DRAFT_STORAGE_KEY, JSON.stringify(payload));
      return { savedAt, mediaLimited: true };
    } catch (error) {
      console.warn('Expose bulk draft could not be saved.', error);
      return null;
    }
  }
}

function normalizeSavedDraftEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const projects = Array.isArray(entry.projects)
    ? entry.projects.filter((project) => project && typeof project === 'object')
    : [];
  const bulkText = String(entry.bulkText || '');
  if (!projects.length && !bulkText.trim()) return null;
  const projectIds = new Set(projects.map((project) => String(project.id || '')).filter(Boolean));
  return {
    id: String(entry.id || `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`),
    name: String(entry.name || 'Exposé-Entwurf').trim() || 'Exposé-Entwurf',
    savedAt: String(entry.savedAt || new Date().toISOString()),
    mediaLimited: entry.mediaLimited === true,
    projects,
    selectedIds: Array.isArray(entry.selectedIds)
      ? entry.selectedIds.map(String).filter((id) => projectIds.has(id))
      : [],
    activeId: projectIds.has(String(entry.activeId || '')) ? String(entry.activeId || '') : '',
    bulkField: String(entry.bulkField || 'status'),
    bulkText,
    activeVariantKey: String(entry.activeVariantKey || ''),
  };
}

function loadSavedExposeDrafts() {
  const storage = getLocalStorage();
  if (!storage) return [];
  try {
    const raw = storage.getItem(SAVED_EXPOSE_DRAFTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const drafts = Array.isArray(parsed?.drafts) ? parsed.drafts : [];
    return drafts.map(normalizeSavedDraftEntry).filter(Boolean);
  } catch (_) {
    return [];
  }
}

function persistSavedExposeDrafts(drafts) {
  const storage = getLocalStorage();
  if (!storage) return false;
  try {
    storage.setItem(SAVED_EXPOSE_DRAFTS_STORAGE_KEY, JSON.stringify({
      version: BULK_DRAFT_VERSION,
      drafts: drafts.map(normalizeSavedDraftEntry).filter(Boolean),
    }));
    return true;
  } catch (error) {
    console.warn('Saved expose drafts could not be persisted.', error);
    return false;
  }
}

function stampProject(project) {
  if (!project || typeof project !== 'object') return project;
  return { cachedAt: new Date().toISOString(), pinned: false, ...project };
}

function filterExpiredProjects(projects) {
  if (!Array.isArray(projects) || !projects.length) return projects;
  const now = Date.now();
  return projects.filter((project) => {
    if (project.pinned) return true;
    const cachedAt = project.cachedAt ? new Date(project.cachedAt).getTime() : 0;
    if (!cachedAt || Number.isNaN(cachedAt)) return true;
    return now - cachedAt < PROJECT_CACHE_TTL_MS;
  });
}

const primaryButtonStyle = {
  ...buttonStyle,
  borderColor: '#122A5C',
  background: '#122A5C',
  color: '#FFFFFF',
};

const inputStyle = {
  width: '100%',
  minWidth: 0,
  border: '1px solid #CBD5E1',
  borderRadius: 7,
  background: '#FFFFFF',
  color: '#0F172A',
  fontSize: 12,
  padding: '7px 8px',
  boxSizing: 'border-box',
};

const tableInputStyle = {
  ...inputStyle,
  borderColor: 'transparent',
  background: 'transparent',
  borderRadius: 6,
  padding: '7px 8px',
  fontSize: 12,
  color: '#0F172A',
  fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
};

const STATUS_FIELD_OPTIONS = EXPOSE_FIELD_OPTIONS;

const FIXED_UNIT_FIELD_CONFIG = {
  kwp_leistung: { suffix: 'kWp', aliases: ['kWp', 'MWp'] },
  leistung_kwp: { suffix: 'kWp', aliases: ['kWp', 'MWp'] },
  leistung: { suffix: 'kWp', aliases: ['kWp', 'MWp'] },
  dachflaeche: { suffix: 'm²', aliases: ['m²', 'm2', 'qm'] },
  belegbare_flaeche: { suffix: 'm²', aliases: ['m²', 'm2', 'qm'] },
  pvsol_generatorflaeche: { suffix: 'm²', aliases: ['m²', 'm2', 'qm'] },
  spezifischertrag: { suffix: 'kWh/kWp/a', aliases: ['kWh/kWp/a', 'kWh/kWp', 'kwh/kwp/a', 'kwh/kwp'] },
  pvsol_jahresertrag_spez: { suffix: 'kWh/kWp', aliases: ['kWh/kWp', 'kWh/kWp/a', 'kwh/kwp', 'kwh/kwp/a'] },
  jahresertrag: { suffix: 'kWh/a', aliases: ['kWh/a', 'kWh/Jahr', 'kwh/a', 'kwh/jahr'] },
  pvsol_netzeinspeisung: { suffix: 'kWh/a', aliases: ['kWh/a', 'kWh/Jahr', 'kwh/a', 'kwh/jahr'] },
  pvsol_generatorleistung: { suffix: 'kWp', aliases: ['kWp', 'MWp'] },
  pvsol_co2: { suffix: 'kg/Jahr', aliases: ['kg/Jahr', 'kg/a'] },
  preis_projektrecht: { suffix: '€/kWp', aliases: ['€/kWp', 'EUR/kWp', '€ pro kWp', 'EUR pro kWp'] },
  turnkey_preis_kwp: { suffix: '€/kWp', aliases: ['€/kWp', 'EUR/kWp', '€ pro kWp', 'EUR pro kWp'] },
  pacht_einmal_kwp: { suffix: '€/kWp', aliases: ['€/kWp', 'EUR/kWp', '€ pro kWp', 'EUR pro kWp'] },
  projektrecht_kaufpreis: { suffix: '€', aliases: ['€', 'EUR'] },
  turnkey_kaufpreis: { suffix: '€', aliases: ['€', 'EUR'] },
  kaufpreis: { suffix: '€', aliases: ['€', 'EUR'] },
  gesamtpreis: { suffix: '€', aliases: ['€', 'EUR'] },
  pacht_einmal_gesamt: { suffix: '€', aliases: ['€', 'EUR'] },
  netto_erloes_25j: { suffix: '€', aliases: ['€', 'EUR'] },
  jahreserloes: { suffix: '€ p.a.', aliases: ['€ p.a.', '€ p.a', 'EUR p.a.', 'EUR p.a', '€ / Jahr', 'EUR / Jahr'] },
  jaehrliche_zahlung: { suffix: '€ p.a.', aliases: ['€ p.a.', '€ p.a', 'EUR p.a.', 'EUR p.a', '€ / Jahr', 'EUR / Jahr'] },
  dachpacht: { suffix: '€ p.a.', aliases: ['€ p.a.', '€ p.a', 'EUR p.a.', 'EUR p.a', '€ / Jahr', 'EUR / Jahr'] },
  versicherung: { suffix: '€ p.a.', aliases: ['€ p.a.', '€ p.a', 'EUR p.a.', 'EUR p.a', '€ / Jahr', 'EUR / Jahr'] },
  wartung: { suffix: '€ p.a.', aliases: ['€ p.a.', '€ p.a', 'EUR p.a.', 'EUR p.a', '€ / Jahr', 'EUR / Jahr'] },
  opex: { suffix: '€ p.a.', aliases: ['€ p.a.', '€ p.a', 'EUR p.a.', 'EUR p.a', '€ / Jahr', 'EUR / Jahr'] },
  eeg_verguetung: { suffix: 'ct/kWh', aliases: ['ct/kWh', 'ct / kWh'] },
  netto_verguetung: { suffix: 'ct/kWh', aliases: ['ct/kWh', 'ct / kWh'] },
  eeg_anteil: { suffix: '%', aliases: ['%'] },
  irr: { suffix: '%', aliases: ['%'] },
  vl_prozent: { suffix: '%', aliases: ['%'] },
  anlagenpreis: { suffix: '€', aliases: ['€', 'EUR'] },
  pachtkosten: { suffix: '€', aliases: ['€', 'EUR'] },
  kapitalwert: { suffix: '€', aliases: ['€', 'EUR'] },
  stromgestehungskosten: { suffix: 'ct/kWh', aliases: ['ct/kWh', 'ct / kWh'] },
  // betrachtungszeitraum: rein numerisch, kein Suffix
};

function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function unitPattern(unit) {
  return escapeRegExp(unit).replace(/\\ /g, '\\s*').replace(/\s+/g, '\\s*');
}

function fixedUnitConfigForField(fieldKey) {
  const key = String(fieldKey || '');
  if (/^cashflow_\d+_ertrag$/.test(key)) return { suffix: 'kWh', aliases: ['kWh'] };
  if (/^cashflow_\d+_(erloes|opex|netto)$/.test(key)) return { suffix: '€', aliases: ['€', 'EUR'] };
  return FIXED_UNIT_FIELD_CONFIG[key] || null;
}

function stripFixedUnit(value, config) {
  if (!config) return String(value ?? '');
  let text = String(value ?? '').trim();
  const units = [config.suffix, ...(config.aliases || [])]
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  units.forEach((unit) => {
    const pattern = new RegExp(`\\s*${unitPattern(unit)}\\s*$`, 'i');
    text = text.replace(pattern, '').trim();
  });

  return text;
}

function normalizeFixedUnitFieldValue(fieldKey, value) {
  const config = fixedUnitConfigForField(fieldKey);
  if (!config) return value;
  const bareValue = stripFixedUnit(value, config).trim();
  return bareValue ? `${bareValue} ${config.suffix}` : '';
}

// „Grundbuch belastet" wird u. U. direkt auf ein rohes 0/1-Bitrix-Feld gemappt und kommt dann
// als „0"/„1" (bzw. N/Y, true/false) an. In Klartext Ja/Nein übersetzen; bereits klartextliche
// oder unbekannte Werte unverändert lassen. Konsistent mit _grundbuch_belastet_raw-Logik.
function normalizeGrundbuchBelastetValue(value) {
  const raw = String(value == null ? '' : value).trim().toLowerCase();
  if (!raw) return '';
  if (['1', 'y', 'yes', 'ja', 'true'].includes(raw)) return 'Ja';
  if (['0', 'n', 'no', 'nein', 'false'].includes(raw)) return 'Nein';
  return String(value).trim();
}

// Key-bewusste Import-Normalisierung: erst Feld-spezifische Sonderfälle (Grundbuch belastet),
// sonst die reguläre Fixed-Unit-Normalisierung. Wird auf allen Import-Pfaden verwendet
// (Bitrix-Payload, Supabase-Cache, Draft-Restore), damit z. B. „0" überall zu „Nein" wird.
function normalizeImportedFieldValue(fieldKey, value) {
  if (fieldKey === 'grundbuch_belastet') return normalizeGrundbuchBelastetValue(value);
  return normalizeFixedUnitFieldValue(fieldKey, value);
}

function UnitAwareInput({
  id,
  fieldKey,
  value,
  onChange,
  style,
  onClick,
  onPaste,
  placeholder,
  disabled = false,
}) {
  const [suffixFocused, setSuffixFocused] = useState(false);
  const config = fixedUnitConfigForField(fieldKey);

  if (!config) {
    return (
      <input
        id={id}
        value={value || ''}
        onClick={onClick}
        onPaste={onPaste}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        style={style}
      />
    );
  }

  const displayValue = stripFixedUnit(value, config);
  const wrapperStyle = {
    width: style?.width || '100%',
    minWidth: style?.minWidth ?? 0,
    minHeight: style?.minHeight,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'center',
    border: style?.border || '1px solid #CBD5E1',
    borderColor: style?.borderColor,
    borderRadius: style?.borderRadius ?? 7,
    background: style?.background ?? '#FFFFFF',
    opacity: style?.opacity,
    boxSizing: 'border-box',
    overflow: 'hidden',
    transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
  };
  const innerInputStyle = {
    width: '100%',
    minWidth: 0,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: style?.color || '#0F172A',
    fontSize: style?.fontSize || 12,
    fontWeight: style?.fontWeight,
    fontFamily: style?.fontFamily,
    textAlign: style?.textAlign,
    fontVariantNumeric: style?.fontVariantNumeric,
    padding: style?.padding || '7px 8px',
    paddingRight: 4,
    boxSizing: 'border-box',
  };

  return (
    <span className="epg-uai" style={wrapperStyle}>
      <input
        id={id}
        value={displayValue}
        onClick={onClick}
        onPaste={onPaste}
        onFocus={() => setSuffixFocused(true)}
        onBlur={() => setSuffixFocused(false)}
        onChange={(event) => onChange(normalizeFixedUnitFieldValue(fieldKey, event.target.value))}
        placeholder={placeholder}
        disabled={disabled}
        style={innerInputStyle}
      />
      <span
        aria-hidden="true"
        style={{
          userSelect: 'none',
          pointerEvents: 'none',
          marginRight: 8,
          marginLeft: 2,
          fontSize: 9,
          fontWeight: 600,
          color: '#94A3B8',
          letterSpacing: '0.01em',
          whiteSpace: 'nowrap',
          lineHeight: 1.2,
          opacity: displayValue || suffixFocused ? 1 : 0,
          transition: 'opacity 0.15s',
        }}
      >
        {config.suffix}
      </span>
    </span>
  );
}

const TABLE_FIELDS = [
  { key: 'projekt_id', label: 'Projekt-ID', required: true },
  { key: 'status', label: 'Status' },
  { key: 'projektname', label: 'Projektname', required: true },
  { key: 'gemeinde', label: 'Gemeinde' },
  { key: 'kwp_leistung', label: 'kWp', required: true },
  { key: 'spezifischertrag', label: 'Spez. Ertrag' },
  { key: 'standort', label: 'Standort', required: true },
  { key: 'pachtart', label: 'Art der Pacht' },
  { key: 'pacht_einmal_kwp', label: 'Einmalzahlung Pacht pro kWp' },
  { key: 'pacht_einmal_gesamt', label: 'Einmalzahlung Pacht Gesamt' },
  { key: 'jaehrliche_zahlung', label: 'jährliche Zahlung' },
  { key: 'preis_projektrecht', label: 'Preis Projektrecht' },
  { key: 'turnkey_preis_kwp', label: 'Kaufpreis Turnkey All in pro kWp' },
  { key: 'turnkey_kaufpreis', label: 'Kaufpreis Turnkey' },
  { key: 'verlaengerungsoption', label: 'Verlängerungsoption' },
  { key: 'vl_prozent', label: '% in VL' },
];

const NUMERIC_TABLE_FIELDS = new Set(['kwp_leistung', 'spezifischertrag', 'pacht_einmal_kwp', 'pacht_einmal_gesamt', 'jaehrliche_zahlung', 'preis_projektrecht', 'turnkey_preis_kwp', 'turnkey_kaufpreis', 'vl_prozent']);

// Feste Spaltenbreiten für das Projektlisten-Grid (table-layout: fixed + colgroup).
// Die letzte Spalte (vl_prozent) bleibt im colgroup ohne Breite und nimmt Restbreite
// auf — so bleiben die Sticky-Offsets (0 / 44 / 98) bei jeder Viewportbreite exakt.
const TABLE_COLUMN_WIDTHS = {
  projekt_id: 100,
  status: 108,
  projektname: 200,
  gemeinde: 112,
  kwp_leistung: 100,
  spezifischertrag: 122,
  standort: 216,
  pachtart: 104,
  pacht_einmal_kwp: 130,
  pacht_einmal_gesamt: 130,
  jaehrliche_zahlung: 118,
  preis_projektrecht: 120,
  turnkey_preis_kwp: 150,
  turnkey_kaufpreis: 124,
  verlaengerungsoption: 148,
  vl_prozent: 82,
};
const TABLE_STICKY_WIDTHS = [44, 54, 148];
const TABLE_MIN_WIDTH = TABLE_STICKY_WIDTHS.reduce((sum, w) => sum + w, 0)
  + TABLE_FIELDS.reduce((sum, field) => sum + (TABLE_COLUMN_WIDTHS[field.key] || 132), 0); // = 2310

const EXPOSE_FIELD_GROUPS = [
  {
    title: 'Deckblatt',
    fields: [
      { key: 'projekt_id', label: 'Exposé-Nr. / Projekt-ID' },
      { key: 'datum', label: 'Datum' },
      { key: 'projektname', label: 'Projektname' },
      { key: 'status', label: 'Projektstatus' },
      { key: 'kundenname', label: 'Kundenname / Eigentümer' },
      { key: 'kundenadresse', label: 'Adresse im Exposé' },
      { key: 'standort', label: 'Projektstandort' },
      { key: 'email', label: 'E-Mail im Deckblatt' },
    ],
  },
  {
    title: 'Standort & Gebäudedaten',
    fields: [
      { key: 'gemeinde', label: 'Gemeinde' },
      { key: 'bundesland', label: 'Bundesland' },
      { key: 'gemarkung', label: 'Gemarkung' },
      { key: 'flurstueck', label: 'Flurstück' },
      { key: 'grundbuchblatt', label: 'Grundbuchblatt' },
      { key: 'dachtyp', label: 'Dachtyp' },
      { key: 'dachflaeche', label: 'Dachfläche m²' },
      { key: 'belegbare_flaeche', label: 'Belegbare Fläche' },
    ],
  },
  {
    title: 'Wirtschaft & Pacht',
    fields: [
      { key: 'kwp_leistung', label: 'kWp' },
      { key: 'spezifischertrag', label: 'Spez. Ertrag' },
      { key: 'jahresertrag', label: 'Jahresertrag kWh/a' },
      { key: 'projektrecht_kaufpreis', label: 'Kaufpreis Projektrecht Gesamt' },
      { key: 'preis_projektrecht', label: 'Preis Projektrecht pro kWp' },
      { key: 'turnkey_kaufpreis', label: 'Kaufpreis Turnkey' },
      { key: 'anlagenpreis', label: 'Anlagenpreis (ohne Pacht)' },
      { key: 'turnkey_preis_kwp', label: 'Kaufpreis Turnkey pro kWp' },
      { key: 'preiseingabe_modus', label: 'Preiseingabe-Modus' },
      { key: 'eeg_verguetung', label: 'EEG-Vergütung' },
      { key: 'jahreserloes', label: 'Jahreserlös' },
      { key: 'pachtart', label: 'Art der Pacht' },
      { key: 'pachtvertrag_status', label: 'Pachtvertrag Status' },
      { key: 'pachtlaufzeit', label: 'Pachtlaufzeit' },
      { key: 'pachtbeginn', label: 'Beginn Pachtzahlung' },
      { key: 'pachtkosten', label: 'Pachtkosten' },
      { key: 'pacht_einmal_kwp', label: 'Einmalzahlung Pacht pro kWp' },
      { key: 'pacht_einmal_gesamt', label: 'Einmalzahlung Pacht Gesamt' },
      { key: 'jaehrliche_zahlung', label: 'jährliche Zahlung' },
      { key: 'verlaengerungsoption', label: 'Verlängerungsoption' },
      { key: 'vl_prozent', label: '% in VL' },
    ],
  },
  {
    title: 'Vertrags- & Genehmigungsstatus',
    fields: [
      { key: 'netzstatus', label: 'Netzanschlusszusage' },
      { key: 'statikgutachten_status', label: 'Statikgutachten' },
      { key: 'grundbucheintragung_status', label: 'Grundbucheintragung' },
      { key: 'gestattungsvertraege_status', label: 'Gestattungsverträge' },
      { key: 'inbetriebnahme', label: 'Inbetriebnahme' },
    ],
  },
  {
    title: 'Technik & Netz',
    fields: [
      { key: 'modultyp', label: 'Modultyp' },
      { key: 'modulanzahl', label: 'Modulanzahl' },
      { key: 'wechselrichter', label: 'Wechselrichter' },
      { key: 'netzbetreiber', label: 'Netzbetreiber' },
      { key: 'netzanschluss', label: 'Netzanschluss / Anschlussleistung' },
      { key: 'netz_ep_nummer', label: 'EP-Nummer' },
      { key: 'netz_ea_nummer', label: 'EA-Nummer / Anlagen-ID' },
      { key: 'netz_malo_id', label: 'MaLo-ID' },
      { key: 'netz_tr_id', label: 'TR-ID' },
      { key: 'netz_sr_id', label: 'SR-ID' },
      { key: 'messung', label: 'Messung' },
      { key: 'direktvermarktung', label: 'Direktvermarktung' },
      { key: 'redispatch_status', label: 'Redispatch 2.0' },
      { key: 'netz_zusage_gueltig_bis', label: 'Gültigkeit Anschlusszusage' },
      { key: 'spannungsebene', label: 'Spannungsebene' },
      { key: 'netzverknuepfungspunkt', label: 'Netzverknüpfungspunkt' },
      { key: 'netzentfernung', label: 'Entfernung Netzanschluss' },
    ],
  },
  {
    title: 'PVSol, Statik & Grundbuch',
    fields: [
      { key: 'pvsol_standort', label: 'PVSol Standort' },
      { key: 'pvsol_quelle', label: 'PVSol Quelle der Werte' },
      { key: 'pvsol_generatorleistung', label: 'PVSol Generatorleistung' },
      { key: 'pvsol_jahresertrag_spez', label: 'PVSol spez. Jahresertrag' },
      { key: 'pvsol_netzeinspeisung', label: 'PVSol Netzeinspeisung' },
      { key: 'pvsol_co2', label: 'PVSol vermiedene CO₂-Emissionen' },
      { key: 'pvsol_generatorflaeche', label: 'PVSol Generatorfläche' },
      { key: 'pvsol_module', label: 'PVSol Anzahl Module' },
      { key: 'pvsol_wechselrichter', label: 'PVSol Anzahl Wechselrichter' },
      { key: 'statik_vorpruefung', label: 'Statik-Vorprüfung' },
      { key: 'sanierungsbedarf', label: 'Sanierungsbedarf vorhanden' },
      { key: 'statikgutachten_angefordert', label: 'Statik-Gutachten angefordert' },
      { key: 'statiker_freigabe', label: 'Freigabe Statiker' },
      { key: 'amtsgericht', label: 'Amtsgericht' },
      { key: 'grundbuch_abdruck_vom', label: 'Grundbuch Abdruck vom' },
      { key: 'grundbuch_belastet', label: 'Grundbuch belastet' },
      { key: 'rangruecktritt', label: 'Rangrücktritt' },
      { key: 'grundstuecksart', label: 'Grundstücksart' },
      { key: 'grundstuecksgroesse', label: 'Grundstücksgröße' },
      { key: 'grundbuch_abteilung_ii', label: 'Abteilung II' },
      { key: 'grundbuch_abteilung_iii', label: 'Abteilung III' },
    ],
  },
  {
    title: 'Turnkey Finanzen',
    fields: [
      { key: 'rendite', label: 'Rendite p.a.' },
      { key: 'irr', label: 'IRR' },
      { key: 'payback', label: 'Amortisation' },
      { key: 'netto_erloes_25j', label: 'Netto-Erlös 20 Jahre' },
      { key: 'dachpacht', label: 'Dachpacht' },
      { key: 'versicherung', label: 'Versicherung' },
      { key: 'wartung', label: 'Wartung & O&M' },
      { key: 'opex', label: 'Gesamt OPEX p.a.' },
      { key: 'eeg_anteil', label: 'EEG-Anteil' },
      { key: 'netto_verguetung', label: 'Netto-Vergütung' },
      { key: 'om_vertrag', label: 'O&M Vertrag' },
      ...[1, 2, 3, 4, 5, 6].flatMap((rowIndex) => ([
        { key: `cashflow_${rowIndex}_jahr`, label: `Cashflow ${rowIndex} Jahr` },
        { key: `cashflow_${rowIndex}_ertrag`, label: `Cashflow ${rowIndex} Ertrag` },
        { key: `cashflow_${rowIndex}_erloes`, label: `Cashflow ${rowIndex} Erlös` },
        { key: `cashflow_${rowIndex}_opex`, label: `Cashflow ${rowIndex} OPEX` },
        { key: `cashflow_${rowIndex}_netto`, label: `Cashflow ${rowIndex} Netto` },
      ])),
    ],
  },
  {
    title: 'Kontakt & Medien',
    fields: [
      { key: 'ansprechpartner', label: 'Ansprechpartner' },
      { key: 'position', label: 'Position' },
      { key: 'telefonnummer', label: 'Telefonnummer' },
      { key: 'website', label: 'Website' },
      { key: 'projectImage', label: 'Titelbild' },
      { key: 'lageplanImage', label: 'Lageplan' },
    ],
  },
  {
    title: 'Design & Schriftfarben',
    fields: [
      { key: 'expose_heading_color', label: 'Schriftfarbe Überschriften' },
      { key: 'expose_text_color', label: 'Schriftfarbe Text' },
      { key: 'expose_muted_text_color', label: 'Schriftfarbe Nebentext' },
      { key: 'expose_accent_color', label: 'Akzentfarbe Text' },
      { key: 'expose_cover_text_color', label: 'Schriftfarbe Deckblatt' },
    ],
  },
];

const HIDDEN_FIELD_ALIAS_GROUPS = [
  ['projekt_id', 'angebotsnummer', 'turnkeyAngebotsnummer'],
  ['status', 'projektstatus'],
  ['projektname', 'turnkeyProjektname', 'dealTitle'],
  ['kwp_leistung', 'leistung_kwp', 'leistung'],
  ['dachflaeche', 'dachfläche'],
  ['flurstueck', 'flurstück'],
  ['projektrecht_kaufpreis', 'gesamtpreis', 'projektpreis', 'kaufpreis'],
  ['turnkey_kaufpreis', 'gesamtpreis', 'projektpreis', 'kaufpreis'],
  ['preis_projektrecht', 'preis_pro_kwp', 'preis_kwp', 'kaufpreis_pro_kwp'],
  ['turnkey_preis_kwp', 'preis_pro_kwp', 'preis_kwp', 'kaufpreis_pro_kwp'],
  ['telefonnummer', 'telefon'],
  ['projectImage', 'projektbild', 'heroImage', 'image'],
  ['lageplanImage', 'lageplan', 'lageplanBild'],
];

function createHiddenFieldAliasMap(groups) {
  const map = new Map();
  groups.forEach((group) => {
    group.forEach((key) => {
      const current = map.get(key) || [];
      map.set(key, Array.from(new Set([...current, ...group])));
    });
  });
  return map;
}

const HIDDEN_FIELD_ALIAS_MAP = createHiddenFieldAliasMap(HIDDEN_FIELD_ALIAS_GROUPS);

function hiddenFieldAliases(fieldKey) {
  if (['projektrecht_kaufpreis', 'turnkey_kaufpreis', 'preis_projektrecht', 'turnkey_preis_kwp'].includes(fieldKey)) {
    return [fieldKey];
  }
  return HIDDEN_FIELD_ALIAS_MAP.get(fieldKey) || [fieldKey];
}

function isProjectFieldHidden(project, fieldKey) {
  const hidden = new Set(Array.isArray(project?.__hiddenFields) ? project.__hiddenFields : []);
  return hiddenFieldAliases(fieldKey).some((alias) => hidden.has(alias));
}

function uniqueFields(fields) {
  const seen = new Set();
  return fields.filter((field) => {
    if (!field?.key || seen.has(field.key)) return false;
    seen.add(field.key);
    return true;
  });
}

const DETAIL_FIELDS = uniqueFields([
  ...TABLE_FIELDS,
  ...EXPOSE_FIELD_GROUPS.flatMap((group) => group.fields),
]);

const EXPOSE_EXTENDED_FIELD_KEYS = new Set([
  'grundbuchblatt',
  'belegbare_flaeche',
  'turnkey_preis_kwp',
  'pachtart',
  'pacht_einmal_kwp',
  'pacht_einmal_gesamt',
  'jaehrliche_zahlung',
  'verlaengerungsoption',
  'vl_prozent',
  'gestattungsvertraege_status',
  'inbetriebnahme',
  'netz_ep_nummer',
  'netz_ea_nummer',
  'netz_malo_id',
  'netz_tr_id',
  'netz_sr_id',
  'messung',
  'direktvermarktung',
  'redispatch_status',
  'netz_zusage_gueltig_bis',
  'grundstuecksart',
  'grundstuecksgroesse',
  'grundbuch_abteilung_ii',
  'grundbuch_abteilung_iii',
  'om_vertrag',
  'eeg_anteil',
  'netto_verguetung',
]);

const EXTENDED_FIELDS_STORAGE_KEY = 'enpower.exposes.showExtendedFields';
const VISIBILITY_PRESETS_STORAGE_KEY = 'enpower.exposes.visibilityPresets';
const FIELD_SOURCES_STORAGE_KEY = 'enpower.exposes.showFieldSources';
const EXPOSE_PREFERENCES_CONTRACT_KEY = 'expose-user-preferences';

// Anzeige-Konfiguration der Feldquellen-Badges (__fieldSources → Chip).
const FIELD_SOURCE_BADGES = {
  bitrix: { label: 'Bitrix', bg: '#DBEAFE', color: '#1D4ED8', title: 'Wert kommt aus dem Bitrix-Mapping' },
  pvsol: { label: 'PV*SOL', bg: '#FEF3C7', color: '#92400E', title: 'Wert stammt aus der PV*SOL-Extraktion' },
  standard: { label: 'Standard', bg: '#F1F5F9', color: '#475569', title: 'Wert kommt aus den gepflegten Standardwerten' },
  gespeichert: { label: 'Gespeichert', bg: '#DCFCE7', color: '#166534', title: 'Wert stammt aus zuvor gespeicherten Exposé-Daten' },
  manuell: { label: 'Manuell', bg: '#EDE9FE', color: '#6D28D9', title: 'Wert wurde manuell bearbeitet' },
};
// Preset-Namen, die exakt einem Variantenkey entsprechen, erhalten einen freundlichen Anzeigenamen.
const EXPOSE_VARIANT_PRESET_LABELS = {
  'expose-projektverkauf': 'Projektrechte',
  'expose-turnkey': 'Turnkey',
  'expose-wirtschaftlichkeit': 'Wirtschaftlichkeit',
};
function presetDisplayLabel(name) {
  return EXPOSE_VARIANT_PRESET_LABELS[name] || name;
}
const CONTRACTS_AM_URL = process.env.REACT_APP_CONTRACTS_AM_URL
  || 'https://gjouokpgdwlcdjdenpdt.supabase.co/functions/v1/contracts-am';

async function fetchExposeDataFromSupabase(dealId, headers) {
  try {
    const response = await axios.post('/api/contracts/usage', {
      action: 'expose-supabase-load',
      dealId,
    }, { headers });
    const fields = response.data?.mapping?.fields || response.data?.fields;
    if (fields && typeof fields === 'object') {
      const { _pvsol_raw, _pvsol_updated_at, _pvsol_fields, ...exposeFields } = fields;
      // _pvsol_fields kann sein: Array (Altbestand), JSON-String (neu, Edge-Function
      // speichert nur Strings) oder leerer String/undefined (zerstörter Altbestand).
      let pvsolFields = null;
      if (Array.isArray(_pvsol_fields)) {
        pvsolFields = _pvsol_fields;
      } else if (typeof _pvsol_fields === 'string' && _pvsol_fields.trim()) {
        try {
          const parsed = JSON.parse(_pvsol_fields);
          if (Array.isArray(parsed)) pvsolFields = parsed;
        } catch (_) { /* kein gültiges JSON → null */ }
      }
      // _pvsol_raw nachrangig: falls JSON-String, optional zu Objekt parsen.
      let pvsolRaw = _pvsol_raw;
      if (typeof _pvsol_raw === 'string' && _pvsol_raw.trim()) {
        try { pvsolRaw = JSON.parse(_pvsol_raw); } catch (_) { /* Rohstring behalten */ }
      }
      return {
        fields: exposeFields,
        pvsolRaw,
        pvsolUpdatedAt: _pvsol_updated_at,
        pvsolFields,
      };
    }
  } catch (_e) { /* no saved data yet */ }
  return null;
}

async function resolveExposeImagesFromBitrix(dealId, headers) {
  try {
    const resp = await axios.post('/api/contracts/usage', {
      action: 'expose-resolve-images',
      dealId,
    }, { headers });
    const data = resp.data || {};
    // Bitrix liefert die Bilder als Data-URI in Originalauflösung (Drohnenfotos mit mehreren MB).
    // Vor der Übernahme in den State auf Druckauflösung bringen — siehe UPLOAD_IMAGE_TARGET.
    return {
      ...data,
      projectImage: await shrinkUploadedImage(data.projectImage || ''),
      lageplanImage: await shrinkUploadedImage(data.lageplanImage || ''),
    };
  } catch (_e) {
    return {};
  }
}

async function saveExposeDataToSupabase(dealId, fields, headers) {
  try {
    await axios.post('/api/contracts/usage', {
      action: 'expose-supabase-save',
      dealId,
      fields,
    }, { headers });
    return true;
  } catch (_e) {
    return false;
  }
}

// Stößt die PV*SOL-Extraktion für einen Deal an. Ruft den offenen
// pvsol-webhook-Endpoint direkt auf (er verlangt kein Secret) und speichert
// die extrahierten Felder in Supabase unter `expose-deal-{dealId}`.
// Rückgabe: das Response-JSON (z. B. { ok, extracted } oder { ok, skipped, reason })
// oder null bei Netzwerk-/Serverfehler. Kann 10–30s dauern.
async function triggerPvsolExtraction(dealId, headers, { force = false } = {}) {
  if (!dealId) return null;
  try {
    const params = new URLSearchParams({ resource: 'pvsol-webhook', dealId: String(dealId) });
    if (force) params.set('force', '1');
    const response = await axios.post(`/api/automation/status?${params.toString()}`, {}, {
      headers,
      timeout: 90000,
    });
    return response.data || null;
  } catch (_e) {
    return null;
  }
}

// Übersetzt die skip-Gründe des PV*SOL-Endpoints in kurze deutsche Hinweise.
function pvsolSkipLabel(reason) {
  switch (reason) {
    case 'file_unchanged': return 'Datei unverändert – keine neuen Daten';
    case 'not_a_pvprj_file': return 'Datei im PV*SOL-Feld ist keine .pvprj-Projektdatei';
    case 'file_not_downloadable': return 'PV*SOL-Datei konnte nicht geladen werden';
    default: return reason || 'unbekannt';
  }
}

function loadShowExtendedFieldsLocal() {
  try {
    return window.localStorage?.getItem(EXTENDED_FIELDS_STORAGE_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function persistShowExtendedFieldsLocal(value) {
  try {
    if (value) {
      window.localStorage?.setItem(EXTENDED_FIELDS_STORAGE_KEY, '1');
    } else {
      window.localStorage?.removeItem(EXTENDED_FIELDS_STORAGE_KEY);
    }
  } catch (_) { /* ignore */ }
}

// Feldquellen-Badges sind standardmäßig AN; nur der explizite Wert '0' schaltet sie aus.
function loadShowFieldSourcesLocal() {
  try {
    return window.localStorage?.getItem(FIELD_SOURCES_STORAGE_KEY) !== '0';
  } catch (_) {
    return true;
  }
}

function persistShowFieldSourcesLocal(value) {
  try {
    window.localStorage?.setItem(FIELD_SOURCES_STORAGE_KEY, value ? '1' : '0');
  } catch (_) { /* ignore */ }
}

// Bringt ein beliebiges Preset-Rohobjekt in die Form { [name]: string[] }.
// Toleriert JSON-Strings (Gesamtobjekt oder einzelne Listen), da die
// Supabase-Persistenz Werte je nach Pfad als String zurückgeben kann.
function normalizeVisibilityPresets(value) {
  let source = value;
  if (typeof source === 'string') {
    try { source = JSON.parse(source); } catch (_) { return {}; }
  }
  if (!source || typeof source !== 'object' || Array.isArray(source)) return {};
  const result = {};
  Object.entries(source).forEach(([name, list]) => {
    const key = String(name || '').trim();
    if (!key) return;
    if (Array.isArray(list)) {
      result[key] = list.map((entry) => String(entry)).filter(Boolean);
    } else if (typeof list === 'string') {
      try {
        const parsed = JSON.parse(list);
        if (Array.isArray(parsed)) result[key] = parsed.map((entry) => String(entry)).filter(Boolean);
      } catch (_) { /* ignore malformed entry */ }
    }
  });
  return result;
}

function loadVisibilityPresetsLocal() {
  try {
    const raw = window.localStorage?.getItem(VISIBILITY_PRESETS_STORAGE_KEY);
    return raw ? normalizeVisibilityPresets(raw) : {};
  } catch (_) {
    return {};
  }
}

function persistVisibilityPresetsLocal(presets) {
  try {
    window.localStorage?.setItem(VISIBILITY_PRESETS_STORAGE_KEY, JSON.stringify(presets || {}));
  } catch (_) { /* ignore */ }
}

const ALWAYS_VISIBLE_EXPOSE_DETAIL_KEYS = new Set([
  'projectImage',
  'lageplanImage',
  'expose_heading_color',
  'expose_text_color',
  'expose_muted_text_color',
  'expose_accent_color',
  'expose_cover_text_color',
]);

const VARIANT_INDEPENDENT_FIELD_KEYS = new Set([
  ...ALWAYS_VISIBLE_EXPOSE_DETAIL_KEYS,
  'projectImage', 'lageplanImage',
]);

const EXPOSE_TEMPLATE_DETAIL_KEYS = new Set([
  ...EXPOSE_PLACEHOLDER_FIELDS.flatMap((field) => field.dataKeys || []),
  ...ALWAYS_VISIBLE_EXPOSE_DETAIL_KEYS,
]);

// Bildschlüssel samt ihrer Aliase. Bilder werden bewusst NICHT nach Supabase gespeichert
// (Vorgabe des Nutzers) — sie kommen aus Bitrix bzw. werden manuell eingefügt.
const EXPOSE_IMAGE_KEYS = new Set([
  'projectImage', 'projektbild', 'heroImage', 'image',
  'lageplanImage', 'lageplan', 'lageplanBild',
]);

// Was der Autosave nach Supabase schreibt: ALLES, was ins Exposé einfließen kann.
// Vorher lief der Autosave über EXPOSE_DETAIL_FIELDS — das ist aber nur die SCHNITTMENGE aus
// EXPOSE_FIELD_GROUPS (Editor-Gruppen) und den Template-Keys. Dadurch blieben 40 Keys liegen,
// darunter aus PV*SOL stammende Werte wie `wechselrichter_anzahl`, `netzeinspeisung`,
// `modulleistung`, `gesamtverguetung`, `betrachtungszeitraum` und `inbetriebnahme_datum`:
// Genau die Angaben, die fehlen würden, wenn PV*SOL einmal nicht erreichbar ist.
// Deshalb die VEREINIGUNG aus Template-Keys und PV*SOL-Keys (117 → 156 Keys).
const EXPOSE_SUPABASE_SAVE_KEYS = [...new Set([
  ...EXPOSE_TEMPLATE_DETAIL_KEYS,
  ...PVSOL_EXPOSE_FIELD_KEYS,
])].filter((key) => !EXPOSE_IMAGE_KEYS.has(key));

const EXPOSE_DETAIL_FIELD_GROUPS = EXPOSE_FIELD_GROUPS
  .map((group) => ({
    ...group,
    fields: group.fields.filter((field) => EXPOSE_TEMPLATE_DETAIL_KEYS.has(field.key)),
  }))
  .filter((group) => group.fields.length > 0);

const EXPOSE_DETAIL_FIELDS = uniqueFields(EXPOSE_DETAIL_FIELD_GROUPS.flatMap((group) => group.fields));

const ASSISTANT_REQUIRED_FIELD_KEYS = new Set([
  'projekt_id',
  'projektname',
  'standort',
  'kwp_leistung',
  'spezifischertrag',
  'projectImage',
]);

const ASSISTANT_FIELD_GROUPS = [
  {
    key: 'project',
    title: 'Projektdaten',
    description: 'Deal, Kunde und Dokumentgrundlage',
    fields: ['projekt_id', 'projektname', 'status', 'kundenname', 'kundenadresse', 'datum'],
  },
  {
    key: 'site',
    title: 'Standort & Fläche',
    description: 'Adresse, Grundstück und Dachflächenwerte',
    fields: ['standort', 'gemeinde', 'bundesland', 'gemarkung', 'flurstueck', 'dachtyp', 'dachflaeche', 'belegbare_flaeche'],
  },
  {
    key: 'technical',
    title: 'Technik',
    description: 'Leistung, Ertrag und Module',
    fields: ['kwp_leistung', 'spezifischertrag', 'jahresertrag', 'modultyp', 'modulanzahl', 'wechselrichter'],
  },
  {
    key: 'netz',
    title: 'Netzanschluss',
    description: 'Netzbetreiber, Anschluss und Einspeisepunkt',
    fields: ['netzbetreiber', 'netzanschluss', 'netzstatus', 'spannungsebene', 'netzverknuepfungspunkt', 'netzentfernung'],
  },
  {
    key: 'economics',
    title: 'Preise & Erloes',
    description: 'Kauf-/Projektrechtepreise und EEG-Verguetung',
    fields: ['projektrecht_kaufpreis', 'preis_projektrecht', 'eeg_verguetung', 'jahreserloes'],
  },
  {
    key: 'investition',
    title: 'Investition & Preise',
    description: 'Anlagenpreis, Pacht und Betriebskosten',
    fields: ['turnkey_kaufpreis', 'anlagenpreis', 'turnkey_preis_kwp', 'pachtart', 'pachtkosten', 'opex', 'betrachtungszeitraum'],
  },
  {
    key: 'wirtschaftlichkeit',
    title: 'Wirtschaftlichkeit',
    description: 'Rendite, Amortisation und Betriebskosten',
    fields: ['irr', 'payback', 'eigenkapitalrendite', 'stromgestehungskosten', 'netto_erloes_25j', 'kapitalwert',
             'gesamtverguetung', 'netzeinspeisung',
             'verguetungszeitraum', 'eeg_tarif_name', 'inbetriebnahme_datum'],
  },
  {
    key: 'status',
    title: 'Vertrags-/Statusdaten',
    description: 'Status der für das Exposé relevanten Unterlagen',
    fields: ['pachtvertrag_status', 'statikgutachten_status', 'grundbucheintragung_status'],
  },
  {
    key: 'grundbuch',
    title: 'Grundbuch',
    description: 'Grundbuchdaten und Rangrücktritt',
    fields: ['amtsgericht', 'grundbuch_abdruck_vom', 'grundbuch_belastet', 'rangruecktritt'],
  },
  {
    key: 'contact',
    title: 'Ansprechpartner',
    description: 'Kontaktblock im Dokument',
    fields: ['ansprechpartner', 'position', 'telefonnummer', 'email', 'website'],
  },
  {
    key: 'media',
    title: 'Medien/Bilder',
    description: 'Titelbild und Lageplan für die Vorschau',
    fields: ['projectImage', 'lageplanImage'],
  },
  {
    key: 'cashflow',
    title: 'Cashflow-Übersicht',
    description: 'Cashflow-Zeilen für die Turnkey-Kalkulation',
    fields: [
      ...[1, 2, 3, 4, 5, 6].flatMap((i) => [
        `cashflow_${i}_jahr`, `cashflow_${i}_ertrag`, `cashflow_${i}_erloes`, `cashflow_${i}_opex`, `cashflow_${i}_netto`,
      ]),
    ],
  },
];

// Variant-specific default fields: controls which fields appear in the assistant panel
// per document variant. When showExtendedFields is off (default), only these fields are shown.
const VARIANT_DEFAULT_FIELDS = {
  'expose-projektverkauf': new Set([
    'projekt_id', 'projektname', 'status', 'kundenname', 'kundenadresse', 'datum',
    'standort', 'gemeinde', 'bundesland', 'gemarkung', 'flurstueck', 'dachtyp', 'dachflaeche', 'belegbare_flaeche',
    'kwp_leistung', 'spezifischertrag', 'jahresertrag', 'modultyp', 'modulanzahl', 'wechselrichter',
    'netzbetreiber', 'netzanschluss', 'netzstatus', 'spannungsebene', 'netzverknuepfungspunkt', 'netzentfernung',
    'projektrecht_kaufpreis', 'preis_projektrecht', 'eeg_verguetung', 'jahreserloes', 'pachtart', 'pachtkosten',
    'pachtvertrag_status', 'statikgutachten_status', 'grundbucheintragung_status',
    // Statik-Block des Exposés: wird in der Vorschau gerendert, muss daher auch im Detail-Editor bearbeitbar sein.
    'statik_vorpruefung', 'sanierungsbedarf', 'statikgutachten_angefordert', 'statiker_freigabe',
    'amtsgericht', 'grundbuch_abdruck_vom', 'grundbuch_belastet', 'rangruecktritt',
    'ansprechpartner', 'position', 'telefonnummer', 'email', 'website',
    'projectImage', 'lageplanImage',
  ]),
  'expose-turnkey': new Set([
    'projekt_id', 'projektname', 'status', 'kundenname', 'kundenadresse', 'datum',
    'standort', 'gemeinde', 'bundesland', 'gemarkung', 'flurstueck', 'dachtyp', 'dachflaeche', 'belegbare_flaeche',
    'kwp_leistung', 'spezifischertrag', 'jahresertrag', 'modultyp', 'modulanzahl', 'wechselrichter',
    'netzbetreiber', 'netzanschluss', 'netzstatus', 'spannungsebene', 'netzverknuepfungspunkt', 'netzentfernung',
    'turnkey_kaufpreis', 'anlagenpreis', 'turnkey_preis_kwp', 'eeg_verguetung', 'jahreserloes', 'pachtart', 'pachtkosten',
    'irr', 'payback', 'eigenkapitalrendite', 'stromgestehungskosten', 'netto_erloes_25j', 'kapitalwert',
    'opex', 'gesamtverguetung', 'netzeinspeisung',
    'betrachtungszeitraum', 'verguetungszeitraum', 'eeg_tarif_name', 'inbetriebnahme_datum',
    'pachtvertrag_status', 'statikgutachten_status', 'grundbucheintragung_status',
    // Statik-Block des Exposés: wird in der Vorschau gerendert, muss daher auch im Detail-Editor bearbeitbar sein.
    'statik_vorpruefung', 'sanierungsbedarf', 'statikgutachten_angefordert', 'statiker_freigabe',
    'amtsgericht', 'grundbuch_abdruck_vom', 'grundbuch_belastet', 'rangruecktritt',
    'ansprechpartner', 'position', 'telefonnummer', 'email', 'website',
    'projectImage', 'lageplanImage',
    ...[1, 2, 3, 4, 5, 6].flatMap((i) => [
      `cashflow_${i}_jahr`, `cashflow_${i}_ertrag`, `cashflow_${i}_erloes`, `cashflow_${i}_opex`, `cashflow_${i}_netto`,
    ]),
  ]),
  'expose-wirtschaftlichkeit': new Set([
    'projektname', 'datum',
    'kwp_leistung', 'jahresertrag', 'modultyp', 'modulanzahl', 'wechselrichter',
    'turnkey_kaufpreis', 'anlagenpreis', 'turnkey_preis_kwp', 'eeg_verguetung', 'jahreserloes',
    'irr', 'payback', 'eigenkapitalrendite', 'stromgestehungskosten', 'netto_erloes_25j', 'kapitalwert',
    'opex', 'gesamtverguetung', 'netzeinspeisung',
    'betrachtungszeitraum', 'verguetungszeitraum', 'eeg_tarif_name', 'inbetriebnahme_datum', 'pachtart', 'pachtkosten',
    ...[1, 2, 3, 4, 5, 6].flatMap((i) => [
      `cashflow_${i}_jahr`, `cashflow_${i}_ertrag`, `cashflow_${i}_erloes`, `cashflow_${i}_opex`, `cashflow_${i}_netto`,
    ]),
  ]),
};

const CALCULATED_FIELD_KEYS = CALCULATED_EXPOSE_FIELD_KEYS;

const FIELD_CONFIG_BY_KEY = new Map(DETAIL_FIELDS.map((field) => [field.key, field]));

function getExposeFieldConfig(fieldKey) {
  return FIELD_CONFIG_BY_KEY.get(fieldKey) || { key: fieldKey, label: fieldKey };
}

function isExposeFieldFilled(project, fieldKey) {
  return String(project?.[fieldKey] || '').trim().length > 0;
}

function getAssistantGroupReport(project, group) {
  const fields = group.fields.map(getExposeFieldConfig);
  const filled = fields.filter((field) => isExposeFieldFilled(project, field.key)).length;
  const requiredMissing = fields.filter((field) => (
    ASSISTANT_REQUIRED_FIELD_KEYS.has(field.key) && !isExposeFieldFilled(project, field.key)
  ));
  const optionalMissing = fields.filter((field) => (
    !ASSISTANT_REQUIRED_FIELD_KEYS.has(field.key) && !isExposeFieldFilled(project, field.key)
  ));
  const status = !project
    ? 'empty'
    : requiredMissing.length
      ? 'bad'
      : optionalMissing.length
        ? 'warn'
        : 'ok';
  return {
    fields,
    filled,
    total: fields.length,
    requiredMissing,
    optionalMissing,
    missing: [...requiredMissing, ...optionalMissing],
    status,
  };
}

function getBitrixExposeImportFields() {
  const seenPlaceholders = new Set();
  return EXPOSE_PLACEHOLDER_FIELDS.filter((field) => {
    const placeholder = String(field?.placeholder || '').trim();
    if (!placeholder || seenPlaceholders.has(placeholder)) return false;
    seenPlaceholders.add(placeholder);
    return true;
  });
}

function normalizeColumnName(value) {
  return String(value || '')
    .replace(/\u00c3\u00a4/g, 'ä')
    .replace(/\u00c3\u00b6/g, 'ö')
    .replace(/\u00c3\u00bc/g, 'ü')
    .replace(/\u00c3\u009f/g, 'ß')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9%]+/g, ' ')
    .trim();
}

const FIELD_HEADER_ALIASES = [
  ['projekt_id', 'projekt id', 'projektid', 'id', 'expose nr', 'expose nummer', 'projektnummer', 'interne projekt id'],
  ['status', 'projektstatus', 'status'],
  ['projektname', 'projektname', 'projekt', 'name', 'deal name'],
  ['gemeinde', 'gemeinde', 'landkreis', 'kreis'],
  ['kwp_leistung', 'kwp', 'leistung', 'leistung kwp', 'anlagenleistung', 'pv leistung', 'pv leistung kwp', 'generatorleistung'],
  ['spezifischertrag', 'spez ertrag', 'spezifischer ertrag', 'specific yield'],
  ['standort', 'standort', 'adresse', 'lage', 'projektstandort', 'standort adresse'],
  ['pachtart', 'art der pacht', 'pachtart'],
  ['pacht_einmal_kwp', 'einmalzahlung pacht pro kwp', 'pacht pro kwp', 'pacht kwp'],
  ['pacht_einmal_gesamt', 'einmalzahlung pacht gesamt', 'pacht gesamt'],
  ['jaehrliche_zahlung', 'jahrliche zahlung', 'jaehrliche zahlung', 'jahreszahlung', 'jahrespacht'],
  ['preis_projektrecht', 'preis projektrecht', 'preis projektrecht pro kwp', 'projektrecht pro kwp', 'projektrecht preis pro kwp'],
  ['projektrecht_kaufpreis', 'kaufpreis projektrecht', 'projektrecht gesamt', 'preis projektrecht gesamt', 'projektrecht kaufpreis berechnet'],
  ['turnkey_preis_kwp', 'kaufpreis turnkey all in pro kwp', 'kaufpreis turnkey pro kwp', 'turnkey pro kwp', 'turnkey preis pro kwp'],
  ['turnkey_kaufpreis', 'kaufpreis turnkey', 'turnkey kaufpreis', 'turnkey gesamt', 'turnkey kaufpreis berechnet'],
  ['verlaengerungsoption', 'verlangerungsoption', 'verlaengerungsoption', 'vertrag verlangerungsoption'],
  ['vl_prozent', '% in vl', 'vl prozent', 'prozent in vl', 'zahlung verlangerungsoption'],
  ['kundenadresse', 'adresse im expose', 'kundenadresse', 'projektadresse'],
  ['kundenname', 'kundenname', 'eigentumer', 'eigentuemer', 'grundstuckseigentumer', 'grundstueckseigentuemer', 'eigentumer laut grundbuch', 'eigentuemer laut grundbuch'],
  ['eigentuemer_adresse', 'eigentumer adresse', 'eigentuemer adresse', 'eigentumer adresse grundbuch', 'eigentuemer adresse grundbuch'],
  ['bundesland', 'bundesland'],
  ['gemarkung', 'gemarkung'],
  ['flur', 'flur'],
  ['flurstueck', 'flurstuck', 'flurstueck', 'flurstück'],
  ['grundbuchblatt', 'grundbuchblatt'],
  ['dachtyp', 'dachtyp', 'dachart'],
  ['dachflaeche', 'dachflache', 'dachflaeche', 'dachfläche', 'dachflache anlagenflache', 'dachflaeche anlagenflaeche', 'anlagenflache'],
  ['belegbare_flaeche', 'belegbare flache', 'belegbare flaeche'],
  ['netzstatus', 'netzstatus', 'netzanschlusszusage', 'netzanschluss status', 'anschlusszusage status', 'einspeisezusage'],
  ['statikgutachten_status', 'statikgutachten', 'statik status'],
  ['grundbucheintragung_status', 'grundbucheintragung'],
  ['gestattungsvertraege_status', 'gestattungsvertrage', 'gestattungsvertraege'],
  ['inbetriebnahme', 'inbetriebnahme', 'cod'],
  ['netzbetreiber', 'netzbetreiber'],
  ['netzanschluss', 'netzanschluss', 'anschlussleistung', 'netzseitige anschlusszusage', 'anschlusszusage', 'netzanschlusspunkt'],
  ['netz_ep_nummer', 'ep nummer', 'ep nr', 'ep'],
  ['netz_ea_nummer', 'ea nummer', 'ea nr', 'anlagen id', 'ea nummer anlagen id'],
  ['netz_malo_id', 'malo id', 'malo nummer', 'malo'],
  ['netz_tr_id', 'tr id', 'tr nummer'],
  ['netz_sr_id', 'sr id', 'sr nummer'],
  ['messung', 'messung', 'messkonzept'],
  ['direktvermarktung', 'direktvermarktung'],
  ['redispatch_status', 'redispatch', 'redispatch 2 0'],
  ['netz_zusage_gueltig_bis', 'gultigkeit anschlusszusage', 'gueltigkeit anschlusszusage', 'netzzusage gultig bis', 'netzzusage gueltig bis'],
  ['modultyp', 'modultyp', 'module', 'pv module'],
  ['modulanzahl', 'modulanzahl', 'anzahl module'],
  ['wechselrichter', 'wechselrichter'],
  ['jahresertrag', 'jahresertrag', 'jahresertrag netzeinspeisung', 'netzeinspeisung'],
  ['pvsol_co2', 'co2 einsparung', 'co2 emissionen', 'co2'],
  ['eeg_verguetung', 'eeg vergutung', 'eeg verguetung', 'einspeisevergutung', 'einspeiseverguetung', 'eeg einspeisevergutung', 'eeg einspeiseverguetung'],
  ['jahreserloes', 'jahreserlos', 'jahreserloes', 'gesamtvergutung 1 jahr', 'gesamtverguetung 1 jahr', 'jahreserlos gesamtvergutung 1 jahr'],
  ['pachtlaufzeit', 'pachtlaufzeit', 'laufzeit', 'vertragslaufzeit', 'vergutungszeitraum', 'verguetungszeitraum'],
  ['pachtvertrag_status', 'pachtvertrag status'],
  ['amtsgericht', 'grundbuchamt', 'amtsgericht'],
  ['grundbuch_abdruck_vom', 'grundbuchauszug', 'grundbuch abdruck vom', 'ausdruck vom'],
  ['grundbuch_belastet', 'grundbuch belastet'],
  ['grundstuecksart', 'grundstucksart', 'grundstuecksart'],
  ['grundstuecksgroesse', 'grundstucksgrosse', 'grundstuecksgroesse', 'grundstucksgroße', 'grundstücksgröße'],
  ['grundbuch_abteilung_ii', 'abteilung ii', 'abt ii'],
  ['grundbuch_abteilung_iii', 'abteilung iii', 'abt iii'],
  ['dachpacht', 'dachpacht', 'pacht p a', 'pacht pa', 'jahrespacht'],
  ['versicherung', 'versicherung', 'versicherung p a', 'versicherung pa', 'versicherungskosten'],
  ['wartung', 'wartung', 'wartung o m', 'wartung oem', 'o m', 'o&m', 'om kosten', 'oem kosten'],
  ['opex', 'opex', 'gesamt opex', 'gesamt opex p a', 'betriebskosten'],
  ['ansprechpartner', 'ansprechpartner'],
  ['position', 'position'],
  ['telefonnummer', 'telefon', 'telefonnummer'],
  ['email', 'email', 'e mail'],
  ['website', 'website'],
];

const FIELD_BY_HEADER = new Map();
DETAIL_FIELDS.forEach((field) => {
  [field.key, field.label].forEach((candidate) => {
    FIELD_BY_HEADER.set(normalizeColumnName(candidate), field.key);
  });
});
FIELD_HEADER_ALIASES.forEach(([fieldKey, ...aliases]) => {
  aliases.forEach((alias) => FIELD_BY_HEADER.set(normalizeColumnName(alias), fieldKey));
});

function mapHeaderToFieldKey(header) {
  return FIELD_BY_HEADER.get(normalizeColumnName(header)) || '';
}

function filterExposeDetailFieldGroups(groups, query) {
  const normalizedQuery = normalizeColumnName(query);
  if (!normalizedQuery) return groups;
  const tokens = normalizedQuery.split(/\s+/).filter(Boolean);
  if (!tokens.length) return groups;

  return groups
    .map((group) => ({
      ...group,
      fields: group.fields.filter((field) => {
        const haystack = normalizeColumnName(`${group.title} ${field.label} ${field.key}`);
        return tokens.every((token) => haystack.includes(token));
      }),
    }))
    .filter((group) => group.fields.length > 0);
}

function filterExposeDetailFieldGroupsByVisibility(groups, project, mode) {
  if (!['active', 'hidden'].includes(mode)) return groups;
  return groups
    .map((group) => ({
      ...group,
      fields: group.fields.filter((field) => {
        const hidden = isProjectFieldHidden(project, field.key);
        return mode === 'hidden' ? hidden : !hidden;
      }),
    }))
    .filter((group) => group.fields.length > 0);
}

function findEmbeddedHeaderFieldKey(label) {
  const normalized = normalizeColumnName(label);
  if (!normalized) return '';
  let bestMatch = { fieldKey: '', length: 0 };
  FIELD_BY_HEADER.forEach((fieldKey, alias) => {
    if (!alias || alias.length < 4) return;
    const matches = normalized === alias
      || normalized.endsWith(` ${alias}`)
      || normalized.includes(` ${alias} `)
      || normalized.includes(` - ${alias}`);
    if (matches && alias.length > bestMatch.length) {
      bestMatch = { fieldKey, length: alias.length };
    }
  });
  return bestMatch.fieldKey;
}

function detectDelimiter(text) {
  const firstLine = String(text || '').split(/\r?\n/).find((line) => line.trim()) || '';
  const counts = [
    ['\t', (firstLine.match(/\t/g) || []).length],
    [';', (firstLine.match(/;/g) || []).length],
    [',', (firstLine.match(/,/g) || []).length],
  ];
  return counts.sort((a, b) => b[1] - a[1])[0]?.[1] > 0 ? counts.sort((a, b) => b[1] - a[1])[0][0] : '\t';
}

function parseDelimitedText(text, delimiter = '\t') {
  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;
  const input = String(text || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];
    const next = input[index + 1];
    if (char === '"') {
      if (quoted && next === '"') {
        cell += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === delimiter && !quoted) {
      row.push(cell.trim());
      cell = '';
    } else if (char === '\n' && !quoted) {
      row.push(cell.trim());
      if (row.some((value) => value !== '')) rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += char;
    }
  }

  row.push(cell.trim());
  if (row.some((value) => value !== '')) rows.push(row);
  return rows;
}

function ensureProjectDetailFields(project) {
  const normalized = { ...project };
  DETAIL_FIELDS.forEach((field) => {
    if (normalized[field.key] === undefined || normalized[field.key] === null) normalized[field.key] = '';
  });
  normalized.__hiddenFields = Array.isArray(normalized.__hiddenFields) ? normalized.__hiddenFields : [];
  return normalized;
}

function parseTabularProjects(file, rawText) {
  if (!/\.(csv|tsv)$/i.test(file.name || '')) return null;
  const delimiter = /\.csv$/i.test(file.name || '') ? detectDelimiter(rawText) : '\t';
  const rows = parseDelimitedText(rawText, delimiter);
  if (rows.length < 2) return null;

  const header = rows[0];
  const fieldKeys = header.map(mapHeaderToFieldKey);
  if (fieldKeys.filter(Boolean).length < 2) return null;

  const projects = rows.slice(1).map((rowValues, rowIndex) => {
    const values = {};
    rowValues.forEach((cell, cellIndex) => {
      const fieldKey = fieldKeys[cellIndex];
      if (fieldKey) values[fieldKey] = repairMojibakeText(cell);
    });

    if (!Object.values(values).some((value) => String(value || '').trim())) return null;

    const fallbackProject = createProjectFromFile(
      { name: `${cleanBaseName(file.name)} Zeile ${rowIndex + 2}.txt`, type: file.type || 'text/plain' },
      header.map((label, index) => `${label}: ${rowValues[index] || ''}`).join('\n'),
    );
    return ensureProjectDetailFields({
      ...fallbackProject,
      ...values,
      id: `${Date.now()}-${rowIndex}-${Math.random().toString(16).slice(2)}`,
      sourceFileName: `${file.name} · Zeile ${rowIndex + 2}`,
      sourceFileType: file.type || 'Tabelle',
      rawText: header.map((label, index) => `${label}: ${rowValues[index] || ''}`).join('\n'),
      projekt_id: values.projekt_id || fallbackProject.projekt_id,
      projektname: values.projektname || fallbackProject.projektname,
      status: values.status || fallbackProject.status,
      datum: values.datum || fallbackProject.datum,
    });
  }).filter(Boolean);

  return projects.length ? projects : null;
}

function splitMarkdownTableRow(line) {
  const trimmed = String(line || '').trim();
  if (!trimmed.startsWith('|') || !trimmed.endsWith('|')) return null;
  const cells = trimmed
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());
  return cells.some(Boolean) ? cells : null;
}

function isMarkdownSeparatorRow(cells = []) {
  return cells.length > 0 && cells.every((cell) => /^:?-{2,}:?$/.test(String(cell || '').trim()));
}

function findStructuredHeaderColumns(cells = []) {
  const normalizedCells = cells.map((cell) => normalizeColumnName(cell));
  const fieldIndex = normalizedCells.findIndex((cell) => (
    ['feld', 'field', 'spalte', 'key', 'bezeichnung', 'angabe', 'datenpunkt'].includes(cell)
    || /\bfeld\b/.test(cell)
  ));
  const valueIndex = normalizedCells.findIndex((cell, index) => (
    index !== fieldIndex
    && (
      ['wert', 'value', 'inhalt', 'daten'].includes(cell)
      || /\bwert\b/.test(cell)
    )
  ));
  return fieldIndex >= 0 && valueIndex >= 0 ? { fieldIndex, valueIndex } : null;
}

function cellsToStructuredPair(cells = [], tableHeader = null) {
  if (!cells.length) return null;
  if (tableHeader) {
    const label = cleanStructuredLabel(cells[tableHeader.fieldIndex] || '');
    const value = cleanStructuredValue(cells[tableHeader.valueIndex] || '');
    return label && value ? { label, value } : null;
  }

  const mappedIndex = cells.findIndex((cell, index) => (
    index < cells.length - 1 && Boolean(mapHeaderToFieldKey(cell))
  ));
  if (mappedIndex >= 0) {
    const label = cleanStructuredLabel(cells[mappedIndex]);
    const value = cleanStructuredValue(cells.slice(mappedIndex + 1).join(' | '));
    return label && value ? { label, value } : null;
  }

  if (cells.length >= 2) {
    const label = cleanStructuredLabel(cells[0] || '');
    const value = cleanStructuredValue(cells.slice(1).join(' | '));
    return label && value ? { label, value } : null;
  }
  return null;
}

function cleanStructuredLabel(value) {
  return repairMojibakeText(String(value || ''))
    .replace(/^[-*•]\s*/, '')
    .replace(/^\d+\.\s*/, '')
    .replace(/^#+\s*/, '')
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    .trim();
}

function cleanStructuredValue(value) {
  return repairMojibakeText(String(value || ''))
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    .replace(/^[-*•]\s*/, '')
    .trim();
}

function collectStructuredKeyValuePairs(rawText) {
  const lines = String(rawText || '').replace(/\r/g, '').split('\n');
  const pairs = [];
  let markdownTableHeader = null;

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed || /^={3,}$/.test(trimmed)) {
      markdownTableHeader = null;
      return;
    }

    const tableCells = splitMarkdownTableRow(trimmed);
    if (tableCells) {
      if (isMarkdownSeparatorRow(tableCells)) return;
      const header = findStructuredHeaderColumns(tableCells);
      if (header) {
        markdownTableHeader = header;
        return;
      }
      const pair = cellsToStructuredPair(tableCells, markdownTableHeader);
      if (pair) pairs.push(pair);
      return;
    }
    markdownTableHeader = null;

    const tabParts = trimmed.split('\t').map((part) => part.trim()).filter(Boolean);
    if (tabParts.length >= 2) {
      const header = findStructuredHeaderColumns(tabParts);
      if (header) return;
      const pair = cellsToStructuredPair(tabParts);
      if (pair) pairs.push(pair);
      return;
    }

    const colonMatch = trimmed.match(/^[-*•]?\s*(?:\*\*)?([^:=]{2,90}?)(?:\*\*)?\s*[:=]\s*(.+)$/);
    if (colonMatch) {
      pairs.push({ label: cleanStructuredLabel(colonMatch[1]), value: cleanStructuredValue(colonMatch[2]) });
    }
  });

  return pairs.filter((pair) => pair.label && pair.value);
}

function normalizeStructuredFieldValue(fieldKey, value) {
  const text = cleanStructuredValue(value);
  if (!text) return '';

  if (fixedUnitConfigForField(fieldKey)) {
    const firstNumber = text.match(/-?\d[\d.\s]*(?:,\d+)?/);
    return normalizeFixedUnitFieldValue(fieldKey, firstNumber ? firstNumber[0] : text);
  }

  if (fieldKey === 'grundbuch_belastet') {
    if (/\b(nein|nicht|keine)\b/i.test(text)) return 'Nein';
    if (/\b(ja|grundschuld|hypothek|belastet|abteilung\s*iii|abt\.?\s*iii)\b/i.test(text)) return 'Ja';
  }

  if (fieldKey === 'grundbuch_abdruck_vom') {
    const date = text.match(/\b\d{1,2}\.\d{1,2}\.\d{2,4}\b/);
    return date ? date[0] : text;
  }

  return text;
}

function structuredFieldKeyForLabel(label) {
  const mapped = mapHeaderToFieldKey(label);
  if (mapped) return mapped;
  const normalized = normalizeColumnName(label);
  const embedded = findEmbeddedHeaderFieldKey(label);
  if (embedded) return embedded;
  if (normalized.includes('grundbuch') && normalized.includes('blatt')) return 'grundbuchblatt';
  if (normalized.includes('zahlung') && normalized.includes('verlangerungsoption')) return 'vl_prozent';
  if (normalized.includes('pacht') && normalized.includes('einmalzahlung')) return 'pacht_einmal_gesamt';
  if (normalized.includes('eigentumer') || normalized.includes('eigentuemer')) return 'kundenname';
  if (normalized.includes('anschlusszusage') && normalized.includes('gultig')) return 'netz_zusage_gueltig_bis';
  return '';
}

function applyStructuredPair(values, pair) {
  const label = cleanStructuredLabel(pair.label);
  // Zentrales Werte-Cleaning: cleanStructuredValue entfernt Markdown/Bullets, cleanImportedExposeValue
  // zusätzlich umschließende Anführungszeichen/Kommas (z. B. aus JSON-Dump-Zeilen wie "Hofheim",)
  // sowie /bitrix/-URLs. Innen liegende Kommas/Dezimalzahlen bleiben erhalten. So profitiert auch der
  // normale Datei-/CSV-Import, ohne dass doppeltes Cleaning legitime Werte beschädigt.
  const rawValue = cleanImportedExposeValue(cleanStructuredValue(pair.value));
  const normalizedLabel = normalizeColumnName(label);
  const fieldKey = structuredFieldKeyForLabel(label);

  if (normalizedLabel === 'module' || normalizedLabel === 'pv module') {
    const count = rawValue.match(/\b\d{2,5}\b/);
    if (count && !values.modulanzahl) values.modulanzahl = count[0];
    const moduleType = rawValue
      .replace(/^\s*\d+\s*(?:Module|Stk\.?)?\s*,?\s*/i, '')
      .trim();
    if (moduleType && !values.modultyp) values.modultyp = moduleType;
    if (!values.pvsol_module && count) values.pvsol_module = count[0];
    return true;
  }

  if (normalizedLabel === 'grundbuch') {
    const sheet = rawValue.match(/\b(?:Blatt|GB-Blatt)\s*(\d+)\b/i);
    if (sheet && !values.grundbuchblatt) values.grundbuchblatt = sheet[1];
    if (/grundbuch\s+von\s+([^,]+)/i.test(rawValue) && !values.gemarkung) {
      values.gemarkung = rawValue.match(/grundbuch\s+von\s+([^,]+)/i)[1].trim();
    }
    return Boolean(sheet);
  }

  if (!fieldKey) return false;

  const normalizedValue = normalizeStructuredFieldValue(fieldKey, rawValue);
  if (!normalizedValue) return false;
  values[fieldKey] = normalizedValue;

  if (fieldKey === 'kwp_leistung' && !values.pvsol_generatorleistung) values.pvsol_generatorleistung = normalizedValue;
  if (fieldKey === 'spezifischertrag' && !values.pvsol_jahresertrag_spez) values.pvsol_jahresertrag_spez = normalizedValue;
  if (fieldKey === 'jahresertrag' && !values.pvsol_netzeinspeisung) values.pvsol_netzeinspeisung = normalizedValue;
  if (fieldKey === 'dachflaeche' && !values.pvsol_generatorflaeche) values.pvsol_generatorflaeche = normalizedValue;
  if (fieldKey === 'vl_prozent') {
    const percentage = rawValue.match(/\d+(?:,\d+)?\s*%/);
    if (percentage) values.vl_prozent = normalizeStructuredFieldValue('vl_prozent', percentage[0]);
  }
  if (fieldKey === 'netzanschluss' && /kva|kw/i.test(rawValue) && !values.netzstatus) values.netzstatus = 'Anschlusszusage vorhanden';
  return true;
}

function parseStructuredExposeText(rawText) {
  const pairs = collectStructuredKeyValuePairs(rawText);
  if (!pairs.length) return null;
  const values = {};
  const recognizedPairs = [];
  const unrecognizedPairs = [];

  pairs.forEach((pair) => {
    const beforeKeys = new Set(Object.keys(values));
    const recognized = applyStructuredPair(values, pair);
    const changed = Object.keys(values).some((key) => !beforeKeys.has(key) || values[key]);
    if (recognized && changed) {
      recognizedPairs.push(pair);
    } else {
      unrecognizedPairs.push(pair);
    }
  });

  if (!Object.values(values).some((value) => String(value || '').trim())) return null;

  if (!values.projekt_id) {
    values.projekt_id = values.netz_ep_nummer || values.netz_ea_nummer || '';
  }
  if (!values.status) values.status = 'Aus Rohtext erkannt';
  if (!values.datum) values.datum = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  if (!values.kundenname && values.eigentuemer_adresse) values.kundenname = '';
  if (!values.pachtvertrag_status && values.pachtart) values.pachtvertrag_status = 'liegt vor';
  if (values.grundbuch_abteilung_iii && !values.grundbuch_belastet) values.grundbuch_belastet = 'Ja';

  return {
    values,
    recognized: recognizedPairs,
    unrecognized: unrecognizedPairs,
  };
}

function createProjectFromStructuredText(rawText, name = 'Textfeld') {
  const parsed = parseStructuredExposeText(rawText);
  if (!parsed) return null;
  const fallbackProject = createProjectFromFile({ name: `${name}.txt`, type: 'text/plain' }, rawText);
  return ensureProjectDetailFields({
    ...fallbackProject,
    ...parsed.values,
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    sourceFileName: name,
    sourceFileType: 'Strukturierter Text',
    rawText,
    projekt_id: parsed.values.projekt_id || fallbackProject.projekt_id,
    projektname: parsed.values.projektname || fallbackProject.projektname,
    status: parsed.values.status || fallbackProject.status,
    extractionWarning: parsed.unrecognized.length
      ? `${parsed.unrecognized.length} Feld${parsed.unrecognized.length === 1 ? '' : 'er'} nicht zugeordnet`
      : '',
  });
}

function createProjectsFromRawEntry(entry) {
  const tabularProjects = parseTabularProjects(entry.file, entry.rawText);
  if (tabularProjects) {
    return tabularProjects.map((project) => ({
      ...project,
      extractionWarning: entry.warning || project.extractionWarning || '',
    }));
  }

  const structuredProject = createProjectFromStructuredText(entry.rawText, cleanBaseName(entry.file.name || 'Textfeld'));
  if (structuredProject) {
    return [{
      ...structuredProject,
      sourceFileName: entry.file.name || structuredProject.sourceFileName,
      sourceFileType: entry.file.type || structuredProject.sourceFileType || 'Strukturierter Text',
      extractionWarning: entry.warning || structuredProject.extractionWarning || '',
    }];
  }

  return [{
    ...createProjectFromFile(entry.file, entry.rawText),
    extractionWarning: entry.warning || '',
  }];
}

const LOCATION_PRESETS = [
  { match: /hemer/i, standort: 'Hemer', gemeinde: 'Märkischer Kreis', bundesland: 'Nordrhein-Westfalen' },
  { match: /uchte/i, standort: 'Uchte', gemeinde: 'Landkreis Nienburg/Weser', bundesland: 'Niedersachsen' },
  { match: /dierdorf/i, standort: 'Dierdorf', gemeinde: 'Landkreis Neuwied', bundesland: 'Rheinland-Pfalz' },
  { match: /amt\s*neuhaus|neuhaus/i, standort: 'Amt Neuhaus', gemeinde: 'Landkreis Lüneburg', bundesland: 'Niedersachsen' },
  { match: /bad\s*m[üu]nstereifel/i, standort: 'Bad Münstereifel', gemeinde: 'Kreis Euskirchen', bundesland: 'Nordrhein-Westfalen' },
  { match: /biederitz|heyrothsberge/i, standort: 'Biederitz OT Heyrothsberge', gemeinde: 'Landkreis Jerichower Land', bundesland: 'Sachsen-Anhalt' },
];
const STREET_LINE_PATTERN = /(?:\b[\wÄÖÜäöüß.-]*str\.?|\bstraße\b|\b[\wÄÖÜäöüß.-]*(?:weg|platz|allee|gasse|berg|pfad)\b)/i;

function cleanBaseName(name) {
  return String(name || '')
    .replace(/\.[^.]+$/, '')
    .replace(/[_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeNumber(raw) {
  const text = String(raw || '').replace(/[^\d,.-]/g, '').trim();
  if (!text) return '';
  const normalized = text.includes(',')
    ? text.replace(/\./g, '').replace(',', '.')
    : text.replace(/(?<=\d)\.(?=\d{3}(\D|$))/g, '');
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return String(raw || '').trim();
  return parsed.toLocaleString('de-DE', { maximumFractionDigits: 2 });
}

function extractFirst(patterns, text) {
  for (const pattern of patterns) {
    const match = String(text || '').match(pattern);
    if (match?.[1]) return match[1].trim();
  }
  return '';
}

function detectLocation(text) {
  const preset = LOCATION_PRESETS.find((entry) => entry.match.test(text));
  if (preset) return preset;
  const zipMatch = String(text || '').match(/(\d{5}\s+[A-ZÄÖÜ][^\n,;]+)/);
  if (zipMatch?.[1]) return { standort: zipMatch[1].trim(), gemeinde: '', bundesland: '' };
  return { standort: '', gemeinde: '', bundesland: '' };
}

function detectKwp(text) {
  const directKwp = extractFirst([
    /technische\s+Daten\s+PVA:[\s\S]{0,220}?([\d.,]+)\s*kWp\b/i,
    /Ihre\s+PVA-Anlage:[\s\S]{0,80}?([\d.,]+)\s*kWp\b/i,
  ], text);
  if (directKwp) return normalizeNumber(directKwp);

  const mw = extractFirst([
    /(\d+(?:[,.]\d+)?)\s*MWp?\b/i,
    /(\d+(?:[,.]\d+)?)\s*Megawatt/i,
  ], text);
  if (mw) {
    const parsed = Number(mw.replace(',', '.'));
    return Number.isFinite(parsed) ? (parsed * 1000).toLocaleString('de-DE', { maximumFractionDigits: 2 }) : mw;
  }
  return normalizeNumber(extractFirst([
    /(\d+(?:[,.]\d+)?)\s*kWp\b/i,
    /\bPVA[\s_-]*(\d+(?:[,.]\d+)?)/i,
    /\bPV[\s_-]*(\d+(?:[,.]\d+)?)/i,
  ], text));
}

function detectStatus(text) {
  return extractFirst([
    /(Baufreigabe erteilt)/i,
    /(Reservierung gesendet)/i,
    /(Reserviert bis[^\n]*)/i,
    /(Netzanfrage gültig bis[^\n]*)/i,
    /(in Prüfung)/i,
  ], text);
}

const CP1252_REVERSE_BYTES = new Map([
  [0x20AC, 0x80], [0x201A, 0x82], [0x0192, 0x83], [0x201E, 0x84],
  [0x2026, 0x85], [0x2020, 0x86], [0x2021, 0x87], [0x02C6, 0x88],
  [0x2030, 0x89], [0x0160, 0x8A], [0x2039, 0x8B], [0x0152, 0x8C],
  [0x017D, 0x8E], [0x2018, 0x91], [0x2019, 0x92], [0x201C, 0x93],
  [0x201D, 0x94], [0x2022, 0x95], [0x2013, 0x96], [0x2014, 0x97],
  [0x02DC, 0x98], [0x2122, 0x99], [0x0161, 0x9A], [0x203A, 0x9B],
  [0x0153, 0x9C], [0x017E, 0x9E], [0x0178, 0x9F],
]);

const CP1252_MOJIBAKE_TRAIL = '\u0080-\u00bf\u20ac\u201a\u0192\u201e\u2026\u2020\u2021\u02c6\u2030\u0160\u2039\u0152\u017D\u2018\u2019\u201C\u201D\u2022\u2013\u2014\u02dc\u2122\u0161\u203A\u0153\u017E\u0178';
const MOJIBAKE_TEXT_PATTERN = new RegExp(`(?:\\u00c3[${CP1252_MOJIBAKE_TRAIL}]|\\u00c2[${CP1252_MOJIBAKE_TRAIL}]|\\u00e2[\\u0080-\\uffff]{2})+`, 'gu');
const MOJIBAKE_HINT_PATTERN = /[\u00c2\u00c3\u00e2]/u;
const CONTROL_TEXT_PATTERN = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f]/u;

function getCp1252Byte(character) {
  if (!character) return null;
  const code = character.codePointAt(0);
  if (code <= 0xff) return code;
  return CP1252_REVERSE_BYTES.get(code) ?? null;
}

function decodeCp1252MojibakeSegment(segment) {
  const bytes = [];
  for (const character of segment) {
    const byte = getCp1252Byte(character);
    if (byte === null) return segment;
    bytes.push(byte);
  }

  const decoded = new TextDecoder('utf-8', { fatal: false }).decode(new Uint8Array(bytes));
  if (!decoded || decoded === segment || decoded.includes('\uFFFD') || CONTROL_TEXT_PATTERN.test(decoded)) {
    return segment;
  }
  return decoded;
}

function repairMojibakeText(value) {
  if (typeof value !== 'string' || !MOJIBAKE_HINT_PATTERN.test(value)) {
    return value;
  }

  let next = value;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const repaired = next.replace(MOJIBAKE_TEXT_PATTERN, decodeCp1252MojibakeSegment);
    if (repaired === next) break;
    next = repaired;
    if (!MOJIBAKE_HINT_PATTERN.test(next)) break;
  }
  return next;
}

function repairRawText(raw) {
  return repairMojibakeText(String(raw || ''))
    .replace(/\u00ef\u201a\u2013/g, '•')
    .replace(/\u00ef\u0081\u0090/g, '✓')
    .replace(/\uf0d6/g, '•')
    .replace(/\uf050/g, '✓')
    .replace(/Gülzer\s*Weg/gi, 'Gülzer Weg')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function extractNumericText(raw) {
  const match = String(raw || '').replace(/\u2212/g, '-').match(/-?\d[\d.,]*/);
  return match ? match[0].replace(/\s+/g, '') : '';
}

function parseGermanNumber(raw) {
  const text = extractNumericText(raw);
  if (!text) return null;
  const looksGermanThousands = /^-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(text);
  const hasComma = text.includes(',');
  const hasDot = text.includes('.');
  const decimalSeparator = looksGermanThousands
    ? ','
    : (hasComma && (!hasDot || text.lastIndexOf(',') > text.lastIndexOf('.')) ? ',' : '.');
  const normalized = decimalSeparator === ','
    ? text.replace(/\./g, '').replace(',', '.')
    : text.replace(/,/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseCalculationNumber(raw) {
  const text = extractNumericText(raw);
  if (!text) return null;
  const cleaned = text.replace(/\s+/g, '');
  const commaCount = (cleaned.match(/,/g) || []).length;
  const dotCount = (cleaned.match(/\./g) || []).length;
  let normalized = cleaned;

  if (commaCount && dotCount) {
    normalized = cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')
      ? cleaned.replace(/\./g, '').replace(',', '.')
      : cleaned.replace(/,/g, '');
  } else if (commaCount === 1) {
    const [, fraction = ''] = cleaned.split(',');
    normalized = fraction.length === 3 ? cleaned.replace(/,/g, '') : cleaned.replace(',', '.');
  } else if (dotCount === 1) {
    const [, fraction = ''] = cleaned.split('.');
    normalized = fraction.length === 3 ? cleaned.replace(/\./g, '') : cleaned;
  } else if (dotCount > 1) {
    normalized = cleaned.replace(/\./g, '');
  } else if (commaCount > 1) {
    normalized = cleaned.replace(/,/g, '');
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatGermanNumber(value, maxFractionDigits = 2) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return number.toLocaleString('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  });
}

function formatCurrency(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return '';
  const fractionDigits = Number.isInteger(number) ? 0 : 2;
  return `${number.toLocaleString('de-DE', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: 2,
  })} €`;
}

function formatCurrencyAllowZero(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0) return '';
  if (number === 0) return '0 €';
  return formatCurrency(number);
}

function formatCurrencyPerYear(value) {
  const formatted = formatCurrencyAllowZero(value);
  return formatted ? `${formatted} p.a.` : '';
}

function formatPerKwp(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? `${formatGermanNumber(number, 2)} €/kWp` : '';
}

function normalizeCurrency(raw) {
  const parsed = parseGermanNumber(raw);
  return parsed === null ? '' : formatCurrency(parsed);
}

function normalizeCurrencyPerKwp(raw) {
  const parsed = parseGermanNumber(raw);
  return parsed === null ? '' : formatPerKwp(parsed);
}

function multiplyCurrencyPerKwp(perKwp, kwp) {
  const price = parseGermanNumber(perKwp);
  const power = parseGermanNumber(kwp);
  if (price === null || power === null) return '';
  return formatCurrency(price * power);
}

function calculatePriceFromPerKwp(perKwp, kwp) {
  const price = parseCalculationNumber(perKwp);
  const power = parseCalculationNumber(kwp);
  return price !== null && power !== null ? price * power : null;
}

function getCalculationEnergyKwh(project) {
  const netFeedIn = parseCalculationNumber(project.pvsol_netzeinspeisung || project.netzeinspeisung || '');
  if (netFeedIn !== null) return netFeedIn;
  const specificYield = parseCalculationNumber(project.pvsol_jahresertrag_spez || project.spezifischertrag || '');
  const kwp = parseCalculationNumber(project.kwp_leistung || project.leistung_kwp || project.leistung || '');
  if (specificYield !== null && kwp !== null) return specificYield * kwp;
  return parseCalculationNumber(project.jahresertrag || '');
}

function calculateAnnualEegRevenue(project) {
  const energyKwh = getCalculationEnergyKwh(project);
  const eegCt = parseCalculationNumber(project.eeg_verguetung || '');
  return energyKwh !== null && eegCt !== null ? (energyKwh * eegCt) / 100 : null;
}

function shouldSkipAnnualPachtCandidate(key, raw) {
  const text = String(raw || '').toLowerCase();
  if (key !== 'pachtkosten') return false;
  const mentionsAnnual = /\bp\.?\s*a\.?\b|jähr|jaehr|jahr|annual/.test(text);
  const mentionsOneTimeOnly = /einmal|einmalig|one\s*time/.test(text) && !mentionsAnnual;
  return mentionsOneTimeOnly;
}

function getOpexComponentValue(project, componentKey, sourceKeys) {
  if (isProjectFieldHidden(project, componentKey)) return { hidden: true, value: null, raw: '', sourceKey: '' };

  for (const sourceKey of sourceKeys) {
    if (sourceKey !== componentKey && isProjectFieldHidden(project, sourceKey)) continue;
    const raw = project[sourceKey];
    if (!String(raw || '').trim() || shouldSkipAnnualPachtCandidate(sourceKey, raw)) continue;
    const value = parseCalculationNumber(raw);
    if (value !== null) {
      return { hidden: false, value, raw, sourceKey };
    }
  }

  return { hidden: false, value: null, raw: '', sourceKey: '' };
}

function calculateOpex(project) {
  const components = [
    { key: 'versicherung', sourceKeys: ['versicherung', 'versicherungskosten', 'opex_versicherung'] },
    { key: 'wartung', sourceKeys: ['wartung', 'wartung_om', 'wartung_oem', 'o_m', 'om_kosten', 'oem_kosten'] },
  ];
  let hasRelevantComponent = false;
  let total = 0;
  const resolved = {};

  components.forEach((component) => {
    const match = getOpexComponentValue(project, component.key, component.sourceKeys);
    if (match.hidden) {
      hasRelevantComponent = true;
      return;
    }
    if (match.value !== null) {
      hasRelevantComponent = true;
      total += match.value;
      resolved[component.key] = {
        value: match.value,
        sourceKey: match.sourceKey,
      };
    }
  });

  return hasRelevantComponent ? { total, resolved } : null;
}

// Einzige Quelle der Wahrheit für die gewichtete EEG-Vergütung.
// Nutzt die im Tool pflegbaren Stufen (eeg_verguetungsstufen_json); ist keine
// aktive Stufe vorhanden, fällt es auf die Standard-Stufen (getDefaultEegStufen)
// zurück. Liefert den auf 2 Nachkommastellen gerundeten ct/kWh-Wert oder null.
function resolveEegVerguetung(project, kwp) {
  if (!(kwp > 0)) return null;
  let stufen = [];
  try { stufen = JSON.parse(project.eeg_verguetungsstufen_json || '[]'); } catch (_) { /* ignore */ }
  let aktive = Array.isArray(stufen) ? stufen.filter((s) => s.ct_kwh > 0) : [];
  if (aktive.length === 0) {
    aktive = getDefaultEegStufen(kwp).filter((s) => s.ct_kwh > 0);
  }
  if (aktive.length === 0) return null;
  const weighted = berechneGewichteteEegVerguetung(kwp, aktive);
  return weighted > 0 ? Math.round(weighted * 100) / 100 : null;
}

// OPEX (Gesamt-EUR p.a.): expliziter Wert gewinnt nur, wenn opex vom Nutzer manuell
// gesetzt wurde (__fieldSources.opex === 'manuell'); sonst = Summe aus Versicherung +
// Wartung & Betriebsführung. Default (4 + 5 €/kWp) ergibt 9 €/kWp.
function resolveOpex(project) {
  const opexManual = project.__fieldSources?.opex === 'manuell' && String(project.opex || '').trim() !== '';
  if (opexManual) {
    return parseCalculationNumber(project.opex || '') || 0;
  }
  const vers = parseCalculationNumber(project.versicherung || '') || 0;
  const wart = parseCalculationNumber(project.wartung || '') || 0;
  const summe = vers + wart;
  if (summe > 0) return summe;
  return parseCalculationNumber(project.opex || '') || 0;
}

// Jahres-Slots der Cashflow-Übersicht im Exposé (cashflow_1..6_*).
const CASHFLOW_SLOT_JAHRE = [1, 5, 10, 15, 20, 25];

// Einziger Rechenpfad für die Wirtschaftlichkeit: parsed die Projektfelder,
// ruft berechneWirtschaftlichkeit und liefert { result, updates } mit deutsch
// formatierten Anzeige-Feldern. null bei unvollständigen Inputs
// (mind. Einspeisung, kWp und ein Preis erforderlich).
function deriveEconomics(project) {
  const p = project || {};
  const kwp = parseCalculationNumber(p.kwp_leistung || p.pvsol_generatorleistung || '');
  const netzeinspeisung = parseCalculationNumber(p.netzeinspeisung || p.pvsol_netzeinspeisung || '');
  // EEG-Vergütung: EINE Quelle der Wahrheit. Expliziter Feldwert gewinnt; sonst aus
  // den (pflegbaren) Stufen bzw. Standard-Stufen gewichtet — derselbe Wert, den auch
  // applyAutoCalculations ins Feld eeg_verguetung schreibt.
  const eegVerguetung = parseCalculationNumber(p.eeg_verguetung || '')
    ?? resolveEegVerguetung(p, kwp);
  const anlagenpreis = parseCalculationNumber(p.anlagenpreis || '');
  const pachtartRaw = (p.pachtart || '').toLowerCase();
  const pachtart = pachtartRaw.startsWith('einmal') || pachtartRaw === 'ez' ? 'einmal'
    : pachtartRaw.startsWith('jaehr') || pachtartRaw.startsWith('jährl') ? 'jaehrlich'
    : pachtartRaw.includes('sanierung') ? 'sanierung' : pachtartRaw;
  let pachtkosten = parseCalculationNumber(p.pachtkosten || '') || 0;
  if (!pachtkosten) {
    if (pachtart === 'einmal') {
      pachtkosten = parseCalculationNumber(p.pacht_einmal_gesamt || '') || ((parseCalculationNumber(p.pacht_einmal_kwp || '') || 0) * (kwp || 0));
    } else if (pachtart === 'jaehrlich') {
      pachtkosten = parseCalculationNumber(p.jaehrliche_zahlung || '') || 0;
    }
  }
  // OPEX (Gesamt-EUR p.a.): expliziter Wert nur bei manuellem Override; sonst
  // Summe aus Versicherung + Wartung & Betriebsführung.
  const opex = resolveOpex(p);
  const betrachtungszeitraum = parseInt(String(p.betrachtungszeitraum).replace(/\s*Jahre\s*/gi, ''), 10) || 20;
  const degradationRaw = parseCalculationNumber(p.degradation || '');
  const degradation = degradationRaw === null ? 0.5 : degradationRaw;

  const turnkeyRaw = parseCalculationNumber(p.turnkey_kaufpreis || '');

  if (!netzeinspeisung || !kwp || (!anlagenpreis && !turnkeyRaw)) return null;

  const effectiveAnlagenpreis = anlagenpreis || (turnkeyRaw - (pachtart === 'einmal' ? pachtkosten : 0));

  const result = berechneWirtschaftlichkeit({
    netzeinspeisung,
    // eegVerguetung ist bereits die einzige Quelle der Wahrheit (Stufen/Default
    // gewichtet) — es wird bewusst KEIN zweiter Rechenpfad (eegStufen) übergeben.
    eegVerguetung: eegVerguetung || 0,
    kwp,
    anlagenpreis: effectiveAnlagenpreis,
    pachtart,
    pachtkosten,
    opex,
    betrachtungszeitraum,
    degradation,
  });

  const updates = {
    jahreserloes: formatCurrencyPerYear(result.jahreserloes),
    irr: result.irr != null ? formatGermanNumber(result.irr, 2) : '',
    rendite: result.rendite != null ? formatGermanNumber(result.rendite, 2) : '',
    payback: result.payback != null ? formatGermanNumber(result.payback, 1) : '',
    netto_erloes_25j: formatGermanNumber(Math.round(result.netto_erloes_25j), 0),
    stromgestehungskosten: result.stromgestehungskosten != null ? formatGermanNumber(result.stromgestehungskosten, 2) : '',
    kapitalwert: formatGermanNumber(Math.round(result.kapitalwert), 0),
    wirtschaftlichkeit_cashflow_json: JSON.stringify(result.cashflowTable),
  };

  // Cashflow-Slots befüllen; Slots außerhalb des Betrachtungszeitraums komplett
  // leeren, damit keine stalen Zeilen stehen bleiben.
  CASHFLOW_SLOT_JAHRE.forEach((jahr, index) => {
    const slot = index + 1;
    const row = jahr <= result.betrachtungszeitraum
      ? result.cashflowTable.find((r) => r.jahr === jahr)
      : null;
    if (row) {
      updates[`cashflow_${slot}_jahr`] = String(jahr);
      updates[`cashflow_${slot}_ertrag`] = formatGermanNumber(row.ertragKwh, 0);
      updates[`cashflow_${slot}_erloes`] = formatGermanNumber(Math.round(row.erloes), 0);
      updates[`cashflow_${slot}_opex`] = row.betriebskosten ? `-${formatGermanNumber(Math.round(row.betriebskosten), 0)}` : '0';
      updates[`cashflow_${slot}_netto`] = formatGermanNumber(Math.round(row.erloes - row.betriebskosten), 0);
    } else {
      updates[`cashflow_${slot}_jahr`] = '';
      updates[`cashflow_${slot}_ertrag`] = '';
      updates[`cashflow_${slot}_erloes`] = '';
      updates[`cashflow_${slot}_opex`] = '';
      updates[`cashflow_${slot}_netto`] = '';
    }
  });

  return { result, updates };
}

function applyAutoCalculations(project, variantKey) {
  const next = ensureProjectDetailFields({ ...project });
  const kwp = parseCalculationNumber(next.kwp_leistung || '');

  // Datum = immer tagesaktuell
  next.datum = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });

  // Pachtart-Normalisierung (Bitrix-Enum/Abkürzungen → kanonischer Wert)
  if (next.pachtart) {
    const pl = String(next.pachtart).replace(/[,;]+$/, '').replace(/^["'«»""„‟]+|["'«»""„‟]+$/g, '').trim().toLowerCase();
    if (pl === 'ez' || pl.includes('einmal')) next.pachtart = 'einmal';
    else if (pl.includes('jährl') || pl.includes('jaehr')) next.pachtart = 'jaehrlich';
    else if (pl.includes('sanierung')) next.pachtart = 'sanierung';
  }

  // Nicht-numerische Werte aus Pacht-Feldern bereinigen (z.B. "EZ" aus fehlerhaftem Import)
  ['pacht_einmal_kwp', 'pacht_einmal_gesamt', 'pachtkosten'].forEach((k) => {
    const v = String(next[k] || '').trim();
    if (v && parseCalculationNumber(v) === null) next[k] = '';
  });

  // Pachtart "Sanierung gegen Pacht": Es fließt KEIN Geld — die Dachsanierung ist die
  // Gegenleistung. Ohne diesen Zweig blieb das Pachtfeld leer bzw. zeigte eine 0, was im
  // Exposé wie eine fehlende Angabe oder wie "kostenlos" liest.
  // MUSS nach der Bereinigung oben stehen, sonst würde der Text als nicht-numerischer Wert
  // wieder entfernt. Rechnerisch bleibt es bei 0: parseCalculationNumber liefert für den
  // Text null → || 0. Spiegel-Änderung, synchron mit dem Projekt-Cockpit (2026-07-30).
  if (next.pachtart === 'sanierung') {
    next.pachtkosten = 'Sanierung gegen Pacht';
  }

  // PV*SOL → Anzeige-Felder übernehmen wenn leer
  if (!String(next.modulanzahl || '').trim() && String(next.pvsol_module || '').trim()) {
    next.modulanzahl = next.pvsol_module;
  }
  if (!String(next.wechselrichter || '').trim() && String(next.pvsol_wechselrichter || '').trim()) {
    next.wechselrichter = next.pvsol_wechselrichter;
  }
  // Dachfläche: immer aus PV*SOL-Generatorfläche wenn vorhanden
  if (String(next.pvsol_generatorflaeche || '').trim()) {
    next.dachflaeche = next.pvsol_generatorflaeche;
    next._dachflaeche_source = 'generatorflaeche';
  }
  if (!String(next.netzeinspeisung || '').trim() && String(next.pvsol_netzeinspeisung || '').trim()) {
    next.netzeinspeisung = next.pvsol_netzeinspeisung;
  }

  // Pachtvertrag Status: Default "liegt vor"
  if (!String(next.pachtvertrag_status || '').trim()) {
    next.pachtvertrag_status = 'liegt vor';
  }

  // Verlängerungsoption: Default "20J + 2x5J VL"
  if (!String(next.verlaengerungsoption || '').trim()) {
    next.verlaengerungsoption = '20J + 2x5J VL';
  }

  // Netzstatus: automatisch anhand Netzzusage-Dokument
  const netzzusageDok = String(next._netzzusage_dok || '').trim();
  if (netzzusageDok) {
    next.netzstatus = 'Netzzusage liegt vor';
  } else if (!String(next.netzstatus || '').trim()) {
    next.netzstatus = 'Netzanfrage gestellt';
  }

  // EEG-Vergütungsstufen: fehlen sie, Standard-Stufen (nach kWp) sichtbar/pflegbar
  // hinterlegen, damit die Berechnung eine klare Quelle der Wahrheit hat.
  if (!String(next.eeg_verguetungsstufen_json || '').trim() && kwp > 0) {
    next.eeg_verguetungsstufen_json = JSON.stringify(getDefaultEegStufen(kwp));
  }

  // EEG Vergütung aus Stufen (bzw. Standard-Stufen) gewichtet berechnen wenn leer.
  // Ausgabe als deutsche Zahl (z. B. "10,55") — Einheit ct/kWh hängt das Exposé an.
  if (!String(next.eeg_verguetung || '').trim() && kwp > 0) {
    const weighted = resolveEegVerguetung(next, kwp);
    if (weighted !== null) next.eeg_verguetung = formatGermanNumber(weighted, 2);
  }

  // Betriebskosten-Komponenten (Gesamt-EUR p.a.): Standard-Defaults, wenn leer.
  // Versicherung 4 €/kWp, Wartung & Betriebsführung 5 €/kWp → Summe 9 €/kWp.
  if (!String(next.versicherung || '').trim() && kwp > 0) {
    next.versicherung = String(Math.round(4 * kwp));
  }
  if (!String(next.wartung || '').trim() && kwp > 0) {
    next.wartung = String(Math.round(5 * kwp));
  }

  // Gültig bis: immer tagesaktuell (heute + 4 Wochen). Beim Nachbessern eines
  // Angebots darf kein gespeichertes altes Datum stehen bleiben — Vorgabe 21.08.2026.
  {
    const gueltig = new Date();
    gueltig.setDate(gueltig.getDate() + 28);
    next.gueltig_bis = gueltig.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  }

  // Spannungsebene: <300kWp → Niederspannung, ≥500kWp → Mittelspannung
  if (!String(next.spannungsebene || '').trim() && kwp > 0) {
    if (kwp < 300) next.spannungsebene = 'Niederspannung';
    else if (kwp >= 500) next.spannungsebene = 'Mittelspannung';
  }

  // OPEX = Summe aus Versicherung + Wartung, außer explizit (manuell) überschrieben.
  const opexManual = next.__fieldSources?.opex === 'manuell' && String(next.opex || '').trim() !== '';
  if (!opexManual && kwp > 0) {
    const vers = parseCalculationNumber(next.versicherung || '') || 0;
    const wart = parseCalculationNumber(next.wartung || '') || 0;
    const summe = vers + wart;
    if (summe > 0) next.opex = String(Math.round(summe));
  }

  // Betrachtungszeitraum: Standard 20 Jahre
  if (!String(next.betrachtungszeitraum || '').trim()) {
    next.betrachtungszeitraum = '20';
  }

  // Degradation: Standard 0,5 % pro Jahr
  if (!String(next.degradation || '').trim()) {
    next.degradation = '0,5';
  }

  const energyKwh = getCalculationEnergyKwh(next);
  const annualRevenue = calculateAnnualEegRevenue(next);
  const opex = calculateOpex(next);
  // Kein `projectRightsPrice` mehr: Der Projektrechtspreis wird NICHT hochgerechnet — siehe
  // Begründung unten an der entfernten Zuweisung.
  const turnkeyPrice = calculatePriceFromPerKwp(next.turnkey_preis_kwp, next.kwp_leistung);

  if (energyKwh !== null) {
    const formattedEnergy = `${formatGermanNumber(Math.round(energyKwh), 0)} kWh/a`;
    next.jahresertrag = formattedEnergy;
    next.pvsol_netzeinspeisung = next.pvsol_netzeinspeisung || formattedEnergy;
  }

  if (annualRevenue !== null) {
    next.jahreserloes = formatCurrencyPerYear(annualRevenue);
  }

  // ENTFERNT: Hochrechnung `projektrecht_kaufpreis` = `preis_projektrecht` × kWp.
  // `preis_projektrecht` trägt bei EN:POWER faktisch den PACHTSATZ je kWp, nicht den
  // Verkaufspreis der Projektrechte. Der so errechnete „Kaufpreis Projektrecht" war deshalb
  // centgenau die Pachtzahlung an den Eigentümer (Deal 4338: 200 €/kWp → 32.396 €
  // = pacht_einmal_gesamt) und stand im Investorenportal als Verkaufspreis.
  // Vorgabe des Nutzers (30.07.2026): kein Standardpreis für Projektrechte — der Wert wird
  // ausschließlich manuell im Projektpiloten gepflegt. Ist nichts eingetragen, bleibt die
  // Zeile leer, statt einen erfundenen Preis zu zeigen.
  // Bezeichner nicht umbenennen — beide Tools spiegeln diese Datei byte-genau.
  // Turnkey bleibt bewusst unberührt: `turnkey_preis_kwp` ist ein echter Preis je kWp.

  if (turnkeyPrice !== null && !String(next.turnkey_kaufpreis || '').trim()) {
    next.turnkey_kaufpreis = formatCurrency(turnkeyPrice);
  }

  if (!String(next.turnkey_preis_kwp || '').trim() && kwp > 0) {
    const tk = parseCalculationNumber(next.turnkey_kaufpreis || '');
    if (tk !== null) next.turnkey_preis_kwp = formatGermanNumber(Math.round((tk / kwp) * 100) / 100, 2);
  }
  if (!String(next.preis_projektrecht || '').trim() && kwp > 0) {
    const pk = parseCalculationNumber(next.projektrecht_kaufpreis || '');
    if (pk !== null) next.preis_projektrecht = formatGermanNumber(Math.round((pk / kwp) * 100) / 100, 2);
  }

  if (variantKey === TURNKEY_VARIANT_KEY && !String(next.kaufpreis || '').trim()) {
    const tp = parseCalculationNumber(next.turnkey_kaufpreis || '');
    if (tp !== null) { next.kaufpreis = formatCurrency(tp); next.gesamtpreis = formatCurrency(tp); }
  } else if (!String(next.kaufpreis || '').trim()) {
    const pp = parseCalculationNumber(next.projektrecht_kaufpreis || '');
    if (pp !== null) { next.kaufpreis = formatCurrency(pp); next.gesamtpreis = formatCurrency(pp); }
  }

  if (opex !== null) {
    if (!String(next.opex || '').trim()) {
      next.opex = formatCurrencyPerYear(opex.total);
    }
  }

  // Abgeleitete Kennzahlen immer überschreiben (berechnete Felder, kein fill-only-empty);
  // bei unvollständigen Inputs (null) bleiben die vorhandenen Werte unverändert.
  const economics = deriveEconomics(next);
  if (economics) {
    Object.assign(next, economics.updates);
  }
  return next;
}

function toLines(text) {
  const lines = repairRawText(text)
    .split(/\r?\n/)
    .map((line) => line.replace(/^[•\uf000-\uf8ff\s-]+/, '').replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  const merged = [];
  for (let index = 0; index < lines.length; index += 1) {
    const current = lines[index];
    const next = lines[index + 1] || '';
    if (/^\d{1,2}$/.test(current) && /^\d{3,4}\s+\S+/.test(next)) {
      merged.push(`${current}${next}`);
      index += 1;
    } else {
      merged.push(current);
    }
  }
  return merged;
}

function findLine(lines, pattern) {
  return lines.find((line) => pattern.test(line)) || '';
}

function findLineValue(lines, labelPattern) {
  const index = lines.findIndex((line) => labelPattern.test(line));
  if (index < 0) return '';
  const current = lines[index].replace(labelPattern, '').replace(/^[:\s-]+/, '').trim();
  if (current) return current;
  for (let offset = 1; offset <= 3; offset += 1) {
    const next = lines[index + offset] || '';
    if (next && !/^(NVP|Einspeisepunkt|Netzanfrage|technische Daten|Ertragsprognose|Checkliste)\b/i.test(next)) {
      return next;
    }
  }
  return '';
}

function compactAddressFromLiegenschaft(lines) {
  const index = lines.findIndex((line) => /^Liegenschaft:?$/i.test(line));
  if (index < 0) return '';
  const candidates = lines.slice(index + 1, index + 5);
  const zipLine = candidates.find((line) => /\b\d{5}\b/.test(line)) || '';
  const streetLine = candidates.find((line) => STREET_LINE_PATTERN.test(line)) || '';
  if (streetLine && zipLine) return `${streetLine}, ${zipLine}`;
  return zipLine || streetLine || '';
}

function detectCustomerName(lines, fallback) {
  const index = lines.findIndex((line) => /^Liegenschaft:?$/i.test(line));
  if (index < 0) return fallback;
  const candidates = lines.slice(index + 1, index + 6);
  const customer = candidates.find((line) => (
    !/\b\d{5}\b/.test(line)
    && !STREET_LINE_PATTERN.test(line)
    && !/\bkWp\b/i.test(line)
    && !/^Ihre PVA-Anlage/i.test(line)
  ));
  return customer || fallback;
}

function detectProjectRightsPrice(lines, sourceText) {
  const rightsLine = findLine(lines, /Projektrechte/i);
  const raw = extractFirst([
    /Projektrechte[^\d]{0,20}([\d.,]+)/i,
  ], rightsLine || sourceText);
  return normalizeCurrencyPerKwp(raw);
}

function detectPacht(lines, kwp) {
  const pachtLine = findLine(lines, /^Pacht\s*:/i) || findLine(lines, /Einmalzahlung\s+Pacht/i);
  const rawPrice = extractFirst([
    /Pacht[^\d]{0,30}([\d.,]+)\s*(?:€|EUR)?\s*\/?\s*kWp/i,
    /Einmalzahlung\s+Pacht\s+pro\s+kWp[^\d]{0,30}([\d.,]+)/i,
  ], pachtLine);
  const pachtPerKwp = normalizeCurrencyPerKwp(rawPrice);
  const pachtType = pachtLine ? pachtLine.replace(/^Pacht\s*:?\s*/i, '').trim() : '';
  return {
    pachtart: pachtPerKwp ? 'Einmalzahlung Pacht' : pachtType,
    pacht_einmal_kwp: pachtPerKwp,
    pacht_einmal_gesamt: pachtPerKwp ? multiplyCurrencyPerKwp(pachtPerKwp, kwp) : '',
  };
}

function detectTurnkeyPrice(lines, sourceText, kwp) {
  const saleIndex = lines.findIndex((line) => /Verkaufspreis\s+der\s+Anlage/i.test(line));
  const saleText = saleIndex >= 0 ? lines.slice(saleIndex, saleIndex + 3).join(' ') : sourceText;
  const bracketPerKwp = extractFirst([/\(([\d.,]+)\s*(?:€|EUR)?\s*\/?\s*kWp\)/i], saleText);
  const directPerKwp = extractFirst([
    /([\d.,]+)\s*(?:€|EUR)?\s*pro\s+kWp/i,
    /([\d.,]+)\s*(?:€|EUR)?\s*\/\s*kWp/i,
  ], saleText);
  const total = extractFirst([
    /Verkaufspreis\s+der\s+Anlage[\s\S]{0,90}?([\d.]+(?:,\d{2})?)\s*(?:€|EUR)\s+pro\s+kWp\s*\(/i,
  ], saleText);
  const perKwp = normalizeCurrencyPerKwp(bracketPerKwp || directPerKwp);
  return {
    turnkey_preis_kwp: perKwp,
    turnkey_kaufpreis: normalizeCurrency(total) || (perKwp ? multiplyCurrencyPerKwp(perKwp, kwp) : ''),
  };
}

function detectTechnicalData(lines, sourceText) {
  const dachflaeche = extractFirst([
    /([\d.]+(?:,\d+)?)\s*m²\s+Fläche-Dachanlage/i,
    /([\d.]+(?:,\d+)?)\s*m2\s+Fläche-Dachanlage/i,
  ], sourceText);
  const specificYield = extractFirst([/([\d.]+(?:,\d+)?)\s*kWh\s*\/\s*kWp/i], sourceText);
  const yearlyYield = extractFirst([
    /([\d.]+(?:,\d+)?)\s*kWh\s*\/\s*Jahr/i,
    /([\d.]+(?:,\d+)?)\s*kWh\s*a/i,
  ], sourceText);
  const moduleLineIndex = lines.findIndex((line) => /Module/i.test(line));
  const moduleCount = extractFirst([/([\d.]+)\s*Module/i], lines[moduleLineIndex] || sourceText);
  const moduleType = moduleLineIndex >= 0 ? (lines[moduleLineIndex + 1] || '') : '';
  const inverterLineIndex = lines.findIndex((line) => /\bWechselrichter\b/i.test(line));
  const inverterLine = lines[inverterLineIndex] || '';
  const inverterType = inverterLineIndex >= 0 ? (lines[inverterLineIndex + 1] || '') : '';

  return {
    dachflaeche: dachflaeche ? `${normalizeNumber(dachflaeche)} m²` : '',
    spezifischertrag: normalizeNumber(specificYield),
    jahresertrag: yearlyYield ? `${normalizeNumber(yearlyYield)} kWh/a` : '',
    modulanzahl: moduleCount ? `${normalizeNumber(moduleCount)} Module` : '',
    modultyp: moduleType,
    wechselrichter: [inverterLine, inverterType].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim(),
    pvsol_jahresertrag_spez: normalizeNumber(specificYield),
    pvsol_netzeinspeisung: yearlyYield ? `${normalizeNumber(yearlyYield)} kWh/a` : '',
  };
}

function detectRevenue(sourceText) {
  const annualRevenue = extractFirst([
    /Gesamtvergütung\s+im\s+ersten(?:\s+vollen)?\s+Jahr[\s\S]{0,60}?([\d.]+(?:,\d{2})?)\s*(?:€|EUR)/i,
  ], sourceText);
  const eeg = extractFirst([
    /Spezifische\s+Einspeisevergütung[\s\S]{0,60}?([\d.,]+)\s*Cent\s*\/\s*kWh/i,
  ], sourceText);
  return {
    jahreserloes: annualRevenue ? `${normalizeNumber(annualRevenue)} € p.a.` : '',
    eeg_verguetung: eeg ? `${normalizeNumber(eeg)} ct/kWh` : '',
  };
}

function detectNetwork(lines) {
  const netzbetreiber = findLineValue(lines, /^Netzbetreiber\s*:?\s*/i);
  const netzanfrage = findLineValue(lines, /^Netzanfrage\s+gültig\s+bis\s*:?\s*/i);
  const reserviert = findLineValue(lines, /^Reserviert\s+bis\s*:?\s*/i);
  const nvp = findLineValue(lines, /^NVP-PVA?\s*:?\s*/i) || findLineValue(lines, /^NVP-PV\s*:?\s*/i);
  const einspeisepunkt = findLineValue(lines, /^Einspeisepunkt\s*:?\s*/i);
  const distance = findLine(lines, /\b(?:ca\.\s*)?\d+(?:,\d+)?\s*(?:Meter|m(?![A-Za-zÄÖÜäöüß²]))\b/i);
  const netzausbau = findLine(lines, /Netzausbau/i);
  return {
    netzbetreiber,
    netzstatus: [netzanfrage ? `Netzanfrage gültig bis ${netzanfrage}` : '', reserviert ? `Reserviert bis ${reserviert}` : '', netzausbau].filter(Boolean).join(' · '),
    netzanschluss: [nvp ? `NVP ${nvp}` : '', einspeisepunkt ? `Einspeisepunkt ${einspeisepunkt}` : '', distance].filter(Boolean).join(' · '),
  };
}

function createProjectFromFile(file, rawText = '') {
  const baseName = cleanBaseName(file.name);
  const sourceText = repairRawText(`${baseName}\n${rawText || ''}`);
  const lines = toLines(sourceText);
  const location = detectLocation(sourceText);
  const kwp = detectKwp(sourceText);
  const projektname = baseName
    .replace(/^Angebot\s+/i, '')
    .replace(/\s*\(\d+\)\s*$/i, '')
    .trim();
  const customerAddress = compactAddressFromLiegenschaft(lines) || location.standort;
  const customerName = detectCustomerName(lines, projektname);
  const pacht = detectPacht(lines, kwp);
  const projectRightsPrice = detectProjectRightsPrice(lines, sourceText);
  const turnkey = detectTurnkeyPrice(lines, sourceText, kwp);
  const technical = detectTechnicalData(lines, sourceText);
  const revenue = detectRevenue(sourceText);
  const network = detectNetwork(lines);
  const annualLease = extractFirst([
    /(?:jährliche\s*Zahlung|Jahrespacht|Pacht\s*p\.?a\.?)[^\d]{0,40}([\d.]+(?:,\d{2})?)/i,
  ], sourceText);
  const specificYield = extractFirst([
    /(?:Spez\.?\s*Ertrag|spezifischer\s*Ertrag)[^\d]{0,35}([\d.,]+)/i,
    /([\d.,]+)\s*kWh\s*\/\s*kWp/i,
  ], sourceText);
  const pachtlaufzeit = extractFirst([
    /(20\s+Jahre[^\n]*)/i,
    /(zzgl\.\s+Verlängerung\s+durch\s+Stromspitzengesetz)/i,
  ], sourceText);

  return ensureProjectDetailFields({
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    sourceFileName: file.name,
    sourceFileType: file.type || 'Datei',
    rawText: sourceText,
    projekt_id: baseName.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 46).toUpperCase(),
    status: (detectStatus(sourceText) && !/:$/.test(detectStatus(sourceText))) ? detectStatus(sourceText) : 'In Prüfung',
    datum: new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' }),
    projektname,
    gemeinde: location.gemeinde,
    kwp_leistung: kwp,
    spezifischertrag: technical.spezifischertrag || normalizeNumber(specificYield),
    standort: customerAddress || location.standort,
    pachtart: pacht.pachtart,
    pachtvertrag_status: pacht.pachtart ? 'liegt vor' : '',
    pacht_einmal_kwp: pacht.pacht_einmal_kwp,
    pacht_einmal_gesamt: pacht.pacht_einmal_gesamt,
    jaehrliche_zahlung: annualLease ? normalizeCurrency(annualLease) : '',
    preis_projektrecht: projectRightsPrice,
    turnkey_preis_kwp: turnkey.turnkey_preis_kwp,
    turnkey_kaufpreis: turnkey.turnkey_kaufpreis,
    verlaengerungsoption: pachtlaufzeit,
    vl_prozent: '',
    // Kein Hochrechnen aus dem €/kWp-Satz: der trägt faktisch die Pacht, nicht den
    // Verkaufspreis der Projektrechte. Wird ausschließlich manuell im Projektpiloten gepflegt.
    projektrecht_kaufpreis: '',
    kundenname: customerName,
    kundenadresse: customerAddress || location.standort,
    bundesland: location.bundesland,
    gemarkung: '',
    flurstueck: '',
    grundbuchblatt: '',
    dachtyp: '',
    dachflaeche: technical.dachflaeche,
    jahresertrag: technical.jahresertrag,
    eeg_verguetung: revenue.eeg_verguetung,
    jahreserloes: revenue.jahreserloes,
    pachtlaufzeit,
    netzbetreiber: network.netzbetreiber,
    netzstatus: network.netzstatus,
    netzanschluss: network.netzanschluss,
    modultyp: technical.modultyp,
    modulanzahl: technical.modulanzahl,
    wechselrichter: technical.wechselrichter,
    pvsol_standort: customerAddress || location.standort,
    pvsol_jahresertrag_spez: technical.pvsol_jahresertrag_spez || normalizeNumber(specificYield),
    pvsol_netzeinspeisung: technical.pvsol_netzeinspeisung,
    statik_vorpruefung: '',
    statikgutachten_status: '',
    grundbucheintragung_status: '',
    gestattungsvertraege_status: '',
    projectImage: '',
    lageplanImage: '',
    __hiddenFields: [],
  });
}

function missingLabels(project) {
  return TABLE_FIELDS
    .filter((field) => !String(project[field.key] || '').trim())
    .map((field) => field.label);
}

function completeness(project) {
  const filled = TABLE_FIELDS.filter((field) => String(project[field.key] || '').trim()).length;
  return { filled, total: TABLE_FIELDS.length, missing: missingLabels(project) };
}

function mapExposeFieldToProjectField(fieldKey, variantKey) {
  const key = String(fieldKey || '');
  if (key === 'angebotsnummer' || key === 'turnkeyAngebotsnummer') return 'projekt_id';
  if (key === 'projektstatus') return 'status';
  if (key === 'turnkeyProjektname') return 'projektname';
  if (key === 'leistung_kwp' || key === 'leistung') return 'kwp_leistung';
  if (key === 'dachfläche') return 'dachflaeche';
  if (key === 'lageplan' || key === 'lageplanBild') return 'lageplanImage';
  if (key === 'telefon') return 'telefonnummer';
  if (key === 'gesamtpreis' || key === 'kaufpreis') {
    return variantKey === 'expose-turnkey' ? 'turnkey_kaufpreis' : 'projektrecht_kaufpreis';
  }
  if (key === 'projektpreis') return 'projektrecht_kaufpreis';
  if (key === 'preis_pro_kwp' || key === 'preis_kwp' || key === 'kaufpreis_pro_kwp') {
    return variantKey === 'expose-turnkey' ? 'turnkey_preis_kwp' : 'preis_projektrecht';
  }
  return key;
}

function buildExposeData(project, variantKey) {
  if (!project) return {};
  const variantValues = project?.variantData?.[variantKey] && typeof project.variantData[variantKey] === 'object'
    ? project.variantData[variantKey]
    : {};
  const effectiveProject = applyAutoCalculations({ ...project, ...variantValues }, variantKey);
  return buildExposeDataFromEffectiveProject(effectiveProject, variantKey);
}

function buildExposeDataFromEffectiveProject(project, variantKey) {
  const kwpNum = parseCalculationNumber(project.kwp_leistung);
  const rawProjectRightsPricePerKwp = project.preis_projektrecht || '';
  const projectRightsPricePerKwp = rawProjectRightsPricePerKwp;
  // Kein Rückfall auf `preis_projektrecht` × kWp: der €/kWp-Satz trägt faktisch die Pacht,
  // nicht den Verkaufspreis der Projektrechte. Ohne manuellen Eintrag bleibt der Gesamtpreis
  // leer (Vorgabe 30.07.2026). Turnkey darunter bleibt bewusst hochgerechnet.
  const projectRightsTotal = project.projektrecht_kaufpreis || '';
  const projectPrice = projectRightsTotal;
  const rawTurnkeyPricePerKwp = project.turnkey_preis_kwp || '';
  const turnkeyPrice = project.turnkey_kaufpreis
    || (rawTurnkeyPricePerKwp ? multiplyCurrencyPerKwp(rawTurnkeyPricePerKwp, project.kwp_leistung) : '');
  const turnkeyPricePerKwp = rawTurnkeyPricePerKwp;
  const stripRaw = (v) => {
    let s = String(v || '').trim();
    s = s.replace(/[,;]+$/, '').trim();
    s = s.replace(/^["'«»“”„‟]+|["'«»“”„‟]+$/g, '').trim();
    return s;
  };
  const cleanMoney = (v) => {
    const s = String(v || '').trim();
    const m = s.match(/^(-?\d+(?:[.,]\d+)?)\|([A-Z]{3})$/);
    if (!m) return s;
    const num = parseFloat(m[1].replace(',', '.'));
    const cur = m[2] === 'EUR' ? '€' : m[2];
    return Number.isFinite(num) ? `${num.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${cur}` : s;
  };
  const pachtkosten = [
    project.pachtart,
    cleanMoney(project.pacht_einmal_kwp),
    project.jaehrliche_zahlung ? `${cleanMoney(project.jaehrliche_zahlung)} p.a.` : '',
    project.pacht_einmal_gesamt ? `Einmalig ${cleanMoney(project.pacht_einmal_gesamt)}` : '',
  ].filter(Boolean).join(' · ');

  return {
    ...project,
    __hiddenFields: Array.isArray(project.__hiddenFields) ? project.__hiddenFields : [],
    angebotsnummer: project.projekt_id,
    turnkeyAngebotsnummer: project.projekt_id,
    datum: project.datum,
    gueltig_bis: project.gueltig_bis,
    projektname: project.projektname,
    turnkeyProjektname: project.turnkeyProjektname || project.projektname,
    kundenname: project.kundenname || project.projektname,
    kundenadresse: project.kundenadresse || project.standort,
    standort: project.standort,
    gemeinde: project.gemeinde,
    bundesland: project.bundesland,
    kwp_leistung: project.kwp_leistung,
    leistung_kwp: project.kwp_leistung,
    spezifischertrag: project.spezifischertrag,
    jahresertrag: project.jahresertrag,
    dachflaeche: project.dachflaeche,
    _dachflaeche_source: project._dachflaeche_source,
    projektrecht_kaufpreis: projectPrice,
    preis_projektrecht: projectRightsPricePerKwp,
    turnkey_kaufpreis: turnkeyPrice,
    turnkey_preis_kwp: turnkeyPricePerKwp,
    kaufpreis: variantKey === 'expose-turnkey' ? turnkeyPrice : projectPrice,
    gesamtpreis: variantKey === 'expose-turnkey' ? turnkeyPrice : projectPrice,
    projektpreis: projectPrice,
    preis_pro_kwp: variantKey === 'expose-turnkey' ? turnkeyPricePerKwp : projectRightsPricePerKwp,
    eeg_verguetung: project.eeg_verguetung,
    jahreserloes: project.jahreserloes,
    status: stripRaw(project.status),
    pachtvertrag_status: project.pachtvertrag_status || project.pachtart,
    pachtart: project.pachtart,
    pachtlaufzeit: project.pachtlaufzeit || project.laufzeit || project.verlaengerungsoption || '20J + 2x5J',
    pachtkosten: cleanMoney(project.pachtkosten) || pachtkosten,
    netzbetreiber: project.netzbetreiber,
    netzstatus: project.netzstatus,
    netzanschluss: project.netzanschluss,
    modultyp: project.modultyp,
    modulanzahl: project.modulanzahl,
    wechselrichter: project.wechselrichter,
    pvsol_standort: project.pvsol_standort || project.standort,
    pvsol_jahresertrag_spez: project.pvsol_jahresertrag_spez || project.spezifischertrag,
    pvsol_netzeinspeisung: project.pvsol_netzeinspeisung || project.jahresertrag,
    statik_vorpruefung: project.statik_vorpruefung,
    statikgutachten_angefordert: project.statikgutachten_angefordert,
    sanierungsbedarf: project.sanierungsbedarf,
    statiker_freigabe: project.statiker_freigabe,
    grundbuch_belastet: normalizeGrundbuchBelastetValue(stripRaw(project.grundbuch_belastet)),
    rangruecktritt: project.rangruecktritt,
    grundbucheintragung_status: project.grundbucheintragung_status,
    versicherung: project.versicherung,
    wartung: project.wartung,
    projectImage: project.projectImage,
    lageplanImage: project.lageplanImage,
  };
}

function hasTurnkeyDataset(project) {
  return project?.turnkeyDatasetEnabled === true && project?.variantData?.[TURNKEY_VARIANT_KEY];
}

function getEditableProject(project, variantKey) {
  if (!project) return null;
  if (variantKey === TURNKEY_VARIANT_KEY && hasTurnkeyDataset(project)) {
    return applyAutoCalculations(ensureProjectDetailFields({ ...project, ...project.variantData[TURNKEY_VARIANT_KEY] }), variantKey);
  }
  return applyAutoCalculations(ensureProjectDetailFields(project), variantKey);
}

// Befüllt jedes leere Feld (kein Wert oder '') mit dem passenden Standardwert
// (defaults: dataKey→Wert) und markiert diese Keys in __fieldSources als 'standard'.
// Gibt eine Kopie zurück; das Original bleibt unverändert.
function applyFieldDefaults(project, defaults) {
  if (!project || !defaults || typeof defaults !== 'object') return project;
  const next = { ...project };
  const nextSources = { ...(project.__fieldSources || {}) };
  const plain = {};
  const formulas = {};
  Object.entries(defaults).forEach(([key, value]) => {
    if (value === undefined || value === null || String(value) === '') return;
    if (String(value).startsWith('=')) { formulas[key] = value; } else { plain[key] = value; }
  });
  Object.entries(plain).forEach(([key, value]) => {
    const current = next[key];
    if (current === undefined || current === null || String(current) === '') {
      next[key] = value;
      nextSources[key] = 'standard';
    }
  });
  Object.entries(formulas).forEach(([key, formula]) => {
    const current = next[key];
    if (current === undefined || current === null || String(current) === '') {
      const result = evaluateDefaultFormula(formula, next);
      if (result !== null) {
        next[key] = result;
        nextSources[key] = 'standard';
      }
    }
  });
  next.__fieldSources = nextSources;
  return next;
}

function cloneProjectValuesForVariant(project, sourceVariantKey) {
  const effective = getEditableProject(project, sourceVariantKey) || project || {};
  const clone = {};
  DETAIL_FIELDS.forEach((field) => {
    const value = effective[field.key];
    // Leere Felder nicht klonen, damit später nachgeladene Werte (PV*SOL/Supabase)
    // nicht von ''-Einträgen maskiert werden. getEditableProject overlayt zwar ALLE
    // vorhandenen variantData-Keys – fehlt ein Key hier, greift automatisch der Top-Level-Wert.
    if (value != null && String(value) !== '') clone[field.key] = value;
  });
  [
    'rawText',
    'projectImage',
    'lageplanImage',
    'extractionWarning',
    'sourceFileName',
    'sourceFileType',
    '__hiddenFields',
  ].forEach((key) => {
    if (effective[key] !== undefined) clone[key] = Array.isArray(effective[key]) ? [...effective[key]] : effective[key];
  });
  return clone;
}

// Füllt fehlende/leere Projektfelder aus `updates`, ohne vorhandene Werte zu überschreiben.
// - Top-Level: jeder updates-Key wird gesetzt, wenn dort leer ('') oder fehlend.
// - variantData[TURNKEY_VARIANT_KEY] (falls vorhanden): repariert leere ('')/fehlende Einträge,
//   damit das Turnkey-Overlay (getEditableProject spreadt ALLE variantData-Keys) später
//   nachgeladene Top-Level-Werte (PV*SOL/Supabase) nicht durch ''-Masken verdeckt. So werden
//   auch BESTEHENDE Alt-Datensätze mit ''-Masken repariert.
// Immutabel: gibt eine neue Projekt-Kopie zurück; das Original bleibt unverändert.
function getProjectProtectedKeys(project) {
  // Kein impliziter Schutz mehr über __fieldSources === 'manuell': Felder sind grundsätzlich NICHT
  // gesperrt. Schutz gilt nur noch für Keys, die der Nutzer explizit pro Exposé über die
  // 🔒-Buttons gesetzt hat (project.__lockedFields). Das Fill-Empty-Only-Verhalten der Merges
  // (fillEmptyProjectFields überschreibt vorhandene Werte ohnehin nie) bleibt unberührt.
  return new Set(Array.isArray(project?.__lockedFields) ? project.__lockedFields : []);
}

function fillEmptyProjectFields(project, updates) {
  if (!project || !updates || typeof updates !== 'object') return project;
  const isEmpty = (value) => value == null || String(value) === '';
  const protectedKeys = getProjectProtectedKeys(project);
  const next = { ...project };
  const turnkey = project.variantData?.[TURNKEY_VARIANT_KEY];
  let nextTurnkey = null;
  Object.entries(updates).forEach(([key, value]) => {
    if (isEmpty(value)) return;
    if (protectedKeys.has(key)) return;
    if (isEmpty(next[key])) next[key] = value;
    if (turnkey && isEmpty(turnkey[key])) {
      if (!nextTurnkey) nextTurnkey = { ...turnkey };
      nextTurnkey[key] = value;
    }
  });
  if (nextTurnkey) {
    next.variantData = { ...(project.variantData || {}), [TURNKEY_VARIANT_KEY]: nextTurnkey };
  }
  return next;
}

// Überschreibt die Keys aus `updates` zusätzlich in JEDER vorhandenen variantData-Variante,
// damit frische Recalc-Ergebnisse nicht von alten Varianten-Werten maskiert werden
// (getEditableProject overlayt variantData über top-level). Nur Keys anfassen, die in der
// Variante bereits existieren – fehlende Keys greifen automatisch auf top-level zurück.
// Immutabel: gibt eine neue Projekt-Kopie zurück, wenn sich etwas ändert.
function overwriteFieldsInAllVariants(project, updates) {
  const variantData = project?.variantData;
  if (!variantData || typeof variantData !== 'object' || !updates) return project;
  let changed = false;
  const nextVariantData = Object.fromEntries(Object.entries(variantData).map(([variantKey, values]) => {
    if (!values || typeof values !== 'object') return [variantKey, values];
    const nextValues = { ...values };
    Object.entries(updates).forEach(([key, value]) => {
      if (key in nextValues && nextValues[key] !== value) {
        nextValues[key] = value;
        changed = true;
      }
    });
    return [variantKey, nextValues];
  }));
  return changed ? { ...project, variantData: nextVariantData } : project;
}

function cleanImportedExposeValue(value) {
  let text = String(value ?? '').trim();
  if (!text || /^\{\{\s*[^}]+?\s*\}\}$/.test(text)) return '';
  text = text.replace(/^[",\s]+|[",\s]+$/g, '').trim();
  if (/^\/bitrix\//i.test(text)) return '';
  if (text.includes('"')) text = text.replace(/"/g, '').trim();
  if (/\/bitrix\//i.test(text)) {
    text = text.split(/,\s*/).filter((part) => !/\/bitrix\//i.test(part)).join(', ').trim();
  }
  return text;
}

function normalizeImportedExposePayload(payload = {}) {
  const source = payload && typeof payload === 'object' ? payload : {};
  const cleaned = {};

  Object.entries(source).forEach(([key, value]) => {
    const normalizedValue = cleanImportedExposeValue(value);
    if (normalizedValue) cleaned[key] = normalizedValue;
  });

  // Bitrix-Geldfelder enthalten '3140|EUR' → vor Unit-Normalisierung bereinigen
  Object.keys(cleaned).forEach((key) => {
    const v = cleaned[key];
    if (typeof v === 'string') {
      const moneyMatch = v.match(/^(-?\d+(?:[.,]\d+)?)\|[A-Z]{3}$/);
      if (moneyMatch) cleaned[key] = moneyMatch[1];
    }
  });

  DETAIL_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(cleaned, field.key)) {
      cleaned[field.key] = normalizeFixedUnitFieldValue(field.key, cleaned[field.key]);
    }
  });

  // Bitrix-Enum 'Art der Pacht' → normalisierter Klartext
  if (cleaned.pachtart) {
    const PACHTART_ENUM = { '480': 'jaehrlich', '482': 'einmal', '484': 'PV statt Pacht', '486': 'sanierung', '514': 'Kein PV', '532': 'sanierung' };
    const mapped = PACHTART_ENUM[String(cleaned.pachtart)];
    if (mapped) {
      cleaned.pachtart = mapped;
    } else {
      const lower = String(cleaned.pachtart).toLowerCase();
      if (lower.includes('jährl') || lower.includes('jaehr') || lower === 'jährliche zahlung') cleaned.pachtart = 'jaehrlich';
      else if (lower === 'ez' || lower.includes('einmal')) cleaned.pachtart = 'einmal';
      else if (lower.includes('sanierung')) cleaned.pachtart = 'sanierung';
    }
  }

  // Pachtkosten je nach Pachtart aus dem RICHTIGEN Feld befuellen.
  // 'pachtkosten' kommt via Mapping aus UF_CRM_1750205748 = "PV Pacht jaehrlich".
  // Bei Einmalzahlung ist das der falsche Wert → durch einmalige Pacht ersetzen.
  if (cleaned.pachtart === 'einmal') {
    const gesamt = parseCalculationNumber(cleaned.pacht_einmal_gesamt || '');
    const perKwp = parseCalculationNumber(cleaned.pacht_einmal_kwp || '');
    const kwp = parseCalculationNumber(cleaned.kwp_leistung || '');
    if (gesamt) cleaned.pachtkosten = String(gesamt);
    else if (perKwp && kwp) cleaned.pachtkosten = String(Math.round(perKwp * kwp));
    else if (!cleaned.pacht_einmal_gesamt && !cleaned.pacht_einmal_kwp) { /* kein Einmal-Feld vorhanden → pachtkosten behalten */ }
  } else if (cleaned.pachtart === 'jaehrlich') {
    if (cleaned.jaehrliche_zahlung && !cleaned.pachtkosten) cleaned.pachtkosten = cleaned.jaehrliche_zahlung;
  }

  // Netzbetreiber-Fallback: In Bitrix wird der Netzbetreiber manchmal im Netzstatus-Feld gepflegt
  if (!String(cleaned.netzbetreiber || '').trim() && String(cleaned.netzstatus || '').trim()) {
    cleaned.netzbetreiber = cleaned.netzstatus;
  }

  // Grundbucheintragung: Wenn Bitrix-Dokument vorhanden → "liegt vor"
  if (String(cleaned._grundbucheintragung_dok || '').trim()) {
    cleaned.grundbucheintragung_status = 'liegt vor';
  }

  // Netzstatus: Wenn Netzzusage-Dokument vorhanden → "Netzzusage erteilt", sonst "Netzzusage angefragt"
  if (!String(cleaned.netzstatus || '').trim()) {
    cleaned.netzstatus = String(cleaned._netzzusage_dok || '').trim() ? 'Netzzusage erteilt' : 'Netzzusage angefragt';
  }

  // Grundbuch belastet: Ja/Nein aus dediziertem Bitrix-Rohfeld (_grundbuch_belastet_raw)…
  if (String(cleaned._grundbuch_belastet_raw || '').trim()) {
    cleaned.grundbuch_belastet = normalizeGrundbuchBelastetValue(cleaned._grundbuch_belastet_raw);
  }
  // …oder aus einem direkt auf ein rohes 0/1-Feld gemappten „Grundbuch belastet" (0 → Nein).
  if (String(cleaned.grundbuch_belastet || '').trim()) {
    cleaned.grundbuch_belastet = normalizeGrundbuchBelastetValue(cleaned.grundbuch_belastet);
  }

  // Rangrücktritt: Nur relevant wenn Grundbuch belastet
  if (String(cleaned.grundbuch_belastet || '').toLowerCase() === 'ja') {
    cleaned.rangruecktritt = String(cleaned._rangruecktritt_dok || '').trim() ? 'liegt vor' : 'in Bearbeitung';
  }

  // „Statik-Gutachten angefordert" spiegelt immer den Statikgutachten-Status —
  // beide Angaben sollen im Exposé nie auseinanderlaufen.
  if (String(cleaned.statikgutachten_status || '').trim()) {
    cleaned.statikgutachten_angefordert = cleaned.statikgutachten_status;
  }

  return cleaned;
}

function firstImportedValue(source, keys) {
  for (const key of keys) {
    const value = cleanImportedExposeValue(source?.[key]);
    if (value) return value;
  }
  return '';
}

function createProjectFromBitrixPayload(payload, dealId, variantKey, summary = {}, resolvedFields = {}) {
  const imported = normalizeImportedExposePayload(payload);
  const safeDealId = String(dealId || '').trim();
  const projectName = firstImportedValue(imported, ['projektname', 'turnkeyProjektname', 'dealTitle'])
    || summary.dealTitle
    || `Deal #${safeDealId}`;
  const projectNumber = firstImportedValue(imported, ['projekt_id', 'angebotsnummer', 'turnkeyAngebotsnummer'])
    || safeDealId;
  const customerName = firstImportedValue(imported, ['kundenname'])
    || summary.companyTitle
    || summary.contactName
    || '';
  const customerAddress = firstImportedValue(imported, ['kundenadresse', 'standort']);
  const kwp = firstImportedValue(imported, ['kwp_leistung', 'leistung_kwp', 'leistung']);
  const pricePerKwp = firstImportedValue(imported, ['preis_projektrecht', 'preis_pro_kwp', 'preis_kwp', 'kaufpreis_pro_kwp']);
  const totalPrice = firstImportedValue(imported, ['projektrecht_kaufpreis', 'kaufpreis', 'gesamtpreis', 'projektpreis']);
  const turnkeyPricePerKwp = firstImportedValue(imported, ['turnkey_preis_kwp', 'preis_pro_kwp', 'preis_kwp', 'kaufpreis_pro_kwp']);
  const turnkeyPrice = firstImportedValue(imported, ['turnkey_kaufpreis', 'kaufpreis', 'gesamtpreis', 'projektpreis']);
  const normalizedDate = firstImportedValue(imported, ['datum']);
  const parsedDate = normalizedDate ? new Date(normalizedDate) : null;
  const dateLabel = parsedDate && !Number.isNaN(parsedDate.getTime())
    ? parsedDate.toLocaleDateString('de-DE')
    : new Date().toLocaleDateString('de-DE');

  const base = ensureProjectDetailFields({
    ...imported,
    id: `bitrix-${safeDealId || Date.now()}-${Math.random().toString(16).slice(2)}`,
    dealId: safeDealId,
    sourceFileName: `Bitrix Deal #${safeDealId}`,
    sourceFileType: 'Bitrix',
    rawText: JSON.stringify({
      dealId: safeDealId,
      summary,
      resolvedFields,
    }, null, 2),
    projekt_id: projectNumber,
    status: firstImportedValue(imported, ['status', 'projektstatus']) || 'Aus Bitrix geladen',
    datum: dateLabel,
    projektname: projectName,
    kundenname: customerName,
    kundenadresse: customerAddress,
    standort: customerAddress,
    kwp_leistung: normalizeFixedUnitFieldValue('kwp_leistung', kwp),
    preis_projektrecht: normalizeFixedUnitFieldValue('preis_projektrecht', pricePerKwp),
    projektrecht_kaufpreis: normalizeFixedUnitFieldValue('projektrecht_kaufpreis', totalPrice),
    turnkey_preis_kwp: normalizeFixedUnitFieldValue('turnkey_preis_kwp', imported.turnkey_preis_kwp || ''),
    turnkey_kaufpreis: normalizeFixedUnitFieldValue('turnkey_kaufpreis', imported.turnkey_kaufpreis || ''),
    __hiddenFields: Array.isArray(imported.__hiddenFields) ? imported.__hiddenFields : [],
  });

  if (variantKey !== TURNKEY_VARIANT_KEY) {
    return base;
  }

  const turnkeyBase = ensureProjectDetailFields({
    ...base,
    turnkey_preis_kwp: normalizeFixedUnitFieldValue('turnkey_preis_kwp', turnkeyPricePerKwp || base.turnkey_preis_kwp),
    turnkey_kaufpreis: normalizeFixedUnitFieldValue('turnkey_kaufpreis', turnkeyPrice || base.turnkey_kaufpreis),
  });

  return {
    ...base,
    turnkeyDatasetEnabled: true,
    variantData: {
      ...(base.variantData || {}),
      [TURNKEY_VARIANT_KEY]: cloneProjectValuesForVariant(turnkeyBase, TURNKEY_VARIANT_KEY),
    },
  };
}

// Erkennt einen aus Bitrix erzeugten Rohtext. createProjectFromBitrixPayload legt rawText als
// JSON.stringify({ dealId, summary, resolvedFields }, null, 2) ab. Gibt das geparste Objekt
// zurück, wenn es sich um einen solchen Dump handelt (Merkmal: resolvedFields-Objekt), sonst null.
function parseBitrixRawTextDump(rawText) {
  const text = String(rawText || '').trim();
  if (!text.startsWith('{')) return null;
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object' && parsed.resolvedFields && typeof parsed.resolvedFields === 'object') {
      return parsed;
    }
  } catch (error) {
    return null;
  }
  return null;
}

// Baut aus den gespeicherten resolvedFields (Platzhalter → Wert) denselben normalisierten
// Feld-Datensatz auf wie beim Deal-Laden: Platzhalter → kanonischer dataKey → dieselbe
// Import-Pipeline (normalizeImportedExposePayload) inkl. Pachtkosten-Konsistenz, Geld-/Enum-
// Normalisierung, Grundbuch- und Netzbetreiber-Ableitungen. Felder, deren dataKey in
// REMOVED_BITRIX_DATA_KEYS steht (u. a. ansprechpartner, telefonnummer, netzstatus, netzanschluss,
// datum, status), werden NICHT wiederbelebt – weder aus dem Payload noch als Pipeline-Default.
// Rückgabe: { fields: dataKey→bereinigter Wert, bitrixKeys: Set der echt aufgelösten dataKeys }.
function rebuildImportedFieldsFromResolved(resolvedFields = {}) {
  const source = resolvedFields && typeof resolvedFields === 'object' ? resolvedFields : {};
  const payload = {};
  const bitrixKeys = new Set();
  EXPOSE_PLACEHOLDER_FIELDS.forEach((field) => {
    const resolved = source[field.placeholder];
    if (resolved == null || String(resolved).trim() === '') return;
    const dataKey = (field.dataKeys || [])[0];
    if (!dataKey || REMOVED_BITRIX_DATA_KEYS.has(dataKey)) return;
    payload[dataKey] = resolved;
    bitrixKeys.add(dataKey);
  });
  const cleaned = normalizeImportedExposePayload(payload);
  // Pipeline-Defaults, die trotzdem als „aus Bitrix entfernt" gelten (z. B. netzstatus-Default
  // 'Netzzusage angefragt'), dürfen einen vorhandenen Wert beim Reparse nicht überschreiben.
  Object.keys(cleaned).forEach((key) => {
    if (REMOVED_BITRIX_DATA_KEYS.has(key)) delete cleaned[key];
  });
  return { fields: cleaned, bitrixKeys };
}

function formatTsv(projects, variantKey = 'expose-projektverkauf') {
  const header = TABLE_FIELDS.map((field) => field.label).join('\t');
  const rows = projects.map((project) => {
    const computedProject = getEditableProject(project, variantKey) || project;
    return TABLE_FIELDS.map((field) => computedProject[field.key] || '').join('\t');
  });
  return [header, ...rows].join('\n');
}

function sanitizeFileName(name) {
  return String(name || 'Expose')
    .replace(/[<>:"/\\|?*]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120) || 'Expose';
}

function cleanExposeSubjectName(project = {}) {
  const rawName = [
    project.expose_name,
    project.expose_title,
    project.projektname,
    project.turnkeyProjektname,
    project.dealTitle,
    project.standort,
    project.kundenname,
    project.projekt_id,
  ].map((value) => String(value || '').trim()).find(Boolean) || 'Projekt';

  const cleaned = rawName
    .replace(/^EN\s*:?\s*POWER\s+Expos[ée]\s*/i, '')
    .replace(/^Expos[ée]\s*/i, '')
    .replace(/^Projekt\s*Expos[ée]\s*/i, '')
    .replace(/^\s*(?:PVA|PV[-\s]*Anlage|Photovoltaik(?:anlage)?)\s*[-_:]?\s*/i, '')
    .replace(/^\s*\d{1,3}(?:[.\s]\d{3})*(?:,\d+)?\s*(?:kWp|MWp|MW)?\s*[-_/]?\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim();

  return cleaned || rawName || 'Projekt';
}

function getExposeVariantDocumentLabel(variantKey) {
  return variantKey === TURNKEY_VARIANT_KEY ? 'Turnkey PV' : 'Projektrechte PV';
}

function getExposeDocumentTitle(project = {}, variantKey = 'expose-projektverkauf') {
  return `${getExposeVariantDocumentLabel(variantKey)} - ${cleanExposeSubjectName(project)}`;
}

function getSavedDraftDisplayName(draft = {}, fallbackVariantKey = 'expose-projektverkauf') {
  const rawName = String(draft.name || '').trim();
  const firstProject = Array.isArray(draft.projects) ? draft.projects[0] : null;
  const derivedName = firstProject
    ? getExposeDocumentTitle(firstProject, draft.activeVariantKey || fallbackVariantKey)
    : '';
  if (!rawName) return derivedName || 'Exposé-Vorgang';
  const looksGeneratedFromProjectName = /^PVA\b/i.test(rawName)
    || /\s-\s(?:Projektrechte|Turnkey)\s*$/i.test(rawName)
    || /^Expos[ée]-Vorgang\b/i.test(rawName);
  return looksGeneratedFromProjectName && derivedName ? derivedName : rawName;
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 500);
}

async function getAxiosErrorDetail(error, fallback) {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      return parsed?.detail || parsed?.message || fallback;
    } catch (_) {
      return fallback;
    }
  }
  return data?.detail || data?.message || error?.message || fallback;
}

// Vercel weist Request-Bodies über 4,5 MB mit HTTP 413 ab. Exposé-HTML trägt die Fotos als
// Base64 in sich und reißt die Grenze schnell. Zwei Maßnahmen greifen nacheinander:
// 1. Die Bilder werden vor dem Versand STUFENWEISE verkleinert — nur so stark wie nötig, damit
//    die Qualität möglichst hoch bleibt. Jede Stufe setzt neu auf dem Original-HTML auf.
// 2. Reicht das nicht (oder lassen sich Bilder nicht verkleinern), geht die Payload in Teilen
//    raus und wird serverseitig zusammengesetzt. Der Export bricht also nie an der Größe ab.
// Der HTML-Export bleibt unangetastet und behält die Originalauflösung.
const PDF_SHRINK_STEPS = [
  { maxDim: 1800, quality: 0.82, threshold: 150 * 1024 }, // A4 bei ~200 dpi
  { maxDim: 1400, quality: 0.75, threshold: 120 * 1024 },
  { maxDim: 1100, quality: 0.70, threshold: 80 * 1024 },
  { maxDim: 850, quality: 0.64, threshold: 40 * 1024 },
  { maxDim: 650, quality: 0.58, threshold: 20 * 1024 }, // Notstufe: lieber weicher als gar kein PDF
];
const PDF_MAX_PAYLOAD_BYTES = 4 * 1024 * 1024; // Sicherheitsabstand zu Vercels 4,5 MB

function shrinkDataUriImage(dataUri, { maxDim, quality }) {
  return new Promise((resolve) => {
    try {
      const image = new Image();
      image.onload = () => {
        try {
          const longestSide = Math.max(image.width, image.height);
          const scale = longestSide > maxDim ? maxDim / longestSide : 1;
          const width = Math.max(1, Math.round(image.width * scale));
          const height = Math.max(1, Math.round(image.height * scale));
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const context = canvas.getContext('2d');
          // Weißer Grund, damit transparente Bereiche im JPEG nicht schwarz werden.
          context.fillStyle = '#FFFFFF';
          context.fillRect(0, 0, width, height);
          context.drawImage(image, 0, 0, width, height);
          const shrunk = canvas.toDataURL('image/jpeg', quality);
          resolve(shrunk && shrunk.length < dataUri.length ? shrunk : dataUri);
        } catch (_error) {
          resolve(dataUri);
        }
      };
      image.onerror = () => resolve(dataUri);
      image.src = dataUri;
    } catch (_error) {
      resolve(dataUri);
    }
  });
}

// Bilder werden bereits BEIM EINFÜGEN auf Druckauflösung reduziert. Andernfalls wandert ein
// 4000x3000-Foto als ~28-MB-Data-URI durch den localStorage-Draft und die PDF-Payload: der
// Draft sprengt das localStorage-Kontingent und fällt auf die Variante OHNE Bilder zurück
// (mediaLimited) — genau deshalb waren die Bilder nach dem Neuladen verschwunden.
// 1600 px entsprechen auf A4-Breite rund 214 dpi (drucktauglich). Gemessen an einem
// 4000x3000-Original: fotoähnlich 155 KB, unkomprimierbares Rauschen 419 KB — damit bleiben
// auch vier Projekte mit je zwei Bildern im Worst Case bei 3,3 MB und damit unter dem
// 5-MB-Kontingent. Bei 2000 px waren es 8,0 MB, also wieder zu viel.
const UPLOAD_IMAGE_TARGET = { maxDim: 1600, quality: 0.78 };

async function shrinkUploadedImage(dataUri) {
  const value = String(dataUri || '');
  if (!value.startsWith('data:image/')) return value;
  if (/^data:image\/svg\+xml/i.test(value)) return value; // Vektorgrafik bleibt verlustfrei
  return shrinkDataUriImage(value, UPLOAD_IMAGE_TARGET);
}

// Verkleinert Titelbild und Lageplan eines frisch geladenen Projekts (inkl. Variantendaten),
// bevor es in den State wandert.
async function shrinkProjectImages(project) {
  if (!project || typeof project !== 'object') return project;
  for (const key of ['projectImage', 'lageplanImage']) {
    if (typeof project[key] === 'string' && project[key].startsWith('data:image/')) {
      // eslint-disable-next-line no-await-in-loop
      project[key] = await shrinkUploadedImage(project[key]);
    }
    if (project.variantData && typeof project.variantData === 'object') {
      for (const variant of Object.values(project.variantData)) {
        if (!variant || typeof variant !== 'object') continue;
        if (typeof variant[key] === 'string' && variant[key].startsWith('data:image/')) {
          // eslint-disable-next-line no-await-in-loop
          variant[key] = await shrinkUploadedImage(variant[key]);
        }
      }
    }
  }
  return project;
}

async function shrinkHtmlImagesForPdf(html, step, report) {
  if (typeof html !== 'string' || !html) return html;
  const candidates = [...new Set(html.match(/data:image\/[a-zA-Z0-9.+-]+;base64,[A-Za-z0-9+/=]+/g) || [])]
    .filter((uri) => uri.length > step.threshold);
  if (report) {
    report.total = (html.match(/data:image\/[a-zA-Z0-9.+-]+;base64,[A-Za-z0-9+/=]+/g) || []).length;
    report.candidates = candidates.length;
    report.unchanged = 0;
  }
  if (!candidates.length) return html;
  let result = html;
  for (const uri of candidates) {
    // eslint-disable-next-line no-await-in-loop
    const shrunk = await shrinkDataUriImage(uri, step);
    if (shrunk !== uri) result = result.split(uri).join(shrunk);
    else if (report) report.unchanged += 1;
  }
  return result;
}

// Verkleinert so lange stufenweise, bis das HTML unter die Grenze passt.
// Gibt immer das kleinstmögliche Ergebnis zurück — der Aufrufer entscheidet über den Rest.
async function prepareExposeHtmlForPdf(html) {
  const size = (text) => new Blob([text]).size;
  if (size(html) <= PDF_MAX_PAYLOAD_BYTES) return { html, bytes: size(html), step: null, report: null };
  let best = { html, bytes: size(html), step: null, report: null };
  for (const step of PDF_SHRINK_STEPS) {
    const report = {};
    // eslint-disable-next-line no-await-in-loop
    const candidate = await shrinkHtmlImagesForPdf(html, step, report);
    const bytes = size(candidate);
    if (bytes < best.bytes) best = { html: candidate, bytes, step, report };
    else if (!best.report) best.report = report;
    if (bytes <= PDF_MAX_PAYLOAD_BYTES) break;
  }
  return best;
}

// Das Exposé-HTML wird gzip-komprimiert übertragen. Der Inhalt besteht überwiegend aus
// Inline-Styles und SVG und schrumpft dadurch typischerweise auf ein Zehntel — damit ist
// Vercels 4,5-MB-Grenze praktisch kein Thema mehr. Base64 kostet 33 %, bleibt netto weit
// günstiger. Kann der Browser kein gzip, geht unkomprimiertes HTML wie bisher raus.
async function gzipToBase64(text) {
  if (typeof CompressionStream === 'undefined') return null;
  try {
    const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'));
    const bytes = new Uint8Array(await new Response(stream).arrayBuffer());
    let binary = '';
    const CHUNK = 0x8000; // in Blöcken, sonst sprengt fromCharCode den Stack
    for (let i = 0; i < bytes.length; i += CHUNK) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
    }
    return btoa(binary);
  } catch (_error) {
    return null;
  }
}

// Passt die Payload nicht in einen Request, wird sie in Teile zerlegt hochgeladen. Der Server
// legt die Teile zwischen und setzt sie wieder zusammen — dadurch gibt es für das Exposé keine
// Größenbegrenzung mehr. Jeder Teil bleibt sicher unter Vercels 4,5-MB-Request-Grenze.
const PDF_CHUNK_BYTES = 3 * 1024 * 1024;

function createExposeUploadId() {
  const raw = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `u${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  return raw.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
}

async function uploadExposePayloadInChunks(apiBase, authHeaders, payload, onProgress) {
  const uploadId = createExposeUploadId();
  const chunks = Math.ceil(payload.length / PDF_CHUNK_BYTES);
  for (let index = 0; index < chunks; index += 1) {
    // eslint-disable-next-line no-await-in-loop
    await axios.post(`${apiBase}/contracts/usage`, {
      action: 'expose-pdf-chunk',
      uploadId,
      index,
      total: chunks,
      data: payload.slice(index * PDF_CHUNK_BYTES, (index + 1) * PDF_CHUNK_BYTES),
    }, { headers: authHeaders });
    onProgress?.(index + 1, chunks);
  }
  return { uploadId, chunks };
}

async function requestExposePdfBlob(apiBase, authHeaders, html, filename) {
  const { html: compactHtml, bytes: rawBytes, step, report } = await prepareExposeHtmlForPdf(html);

  // Diagnose: zeigt, ob die Größe von Bildern oder vom übrigen Markup kommt und ob die
  // Verkleinerung überhaupt gegriffen hat (unchanged > 0 heißt: Bilder ließen sich nicht laden).
  const imageBytes = (compactHtml.match(/data:image\/[a-zA-Z0-9.+-]+;base64,[A-Za-z0-9+/=]+/g) || [])
    .reduce((sum, uri) => sum + uri.length, 0);
  const mb = (n) => (n / 1024 / 1024).toFixed(2);

  const gzipB64 = await gzipToBase64(compactHtml);
  const useGzip = Boolean(gzipB64) && gzipB64.length < compactHtml.length;
  const payloadBytes = useGzip ? gzipB64.length : rawBytes;

  console.info(
    `[expose-pdf] HTML ${mb(rawBytes)} MB (davon Bilder ${mb(imageBytes)} MB`
    + `${step ? `, verkleinert auf max. ${step.maxDim}px` : ''}) → Versand ${mb(payloadBytes)} MB`
    + `${useGzip ? ' (gzip)' : ' (unkomprimiert)'}`
    + (report ? ` · Bilder gesamt ${report.total}, davon ${report.candidates} zu groß, ${report.unchanged} nicht verkleinerbar` : ''),
  );

  const body = { action: 'expose-pdf', filename };
  if (payloadBytes > PDF_MAX_PAYLOAD_BYTES) {
    // Zu groß für einen Request → in Teilen hochladen statt abzubrechen.
    const payload = useGzip ? gzipB64 : compactHtml;
    const toastId = toast.loading(`Exposé wird übertragen (0 / ${Math.ceil(payload.length / PDF_CHUNK_BYTES)})…`);
    try {
      const { uploadId, chunks } = await uploadExposePayloadInChunks(
        apiBase,
        authHeaders,
        payload,
        (done, total) => toast.loading(`Exposé wird übertragen (${done} / ${total})…`, { id: toastId }),
      );
      Object.assign(body, { uploadId, chunks, encoding: useGzip ? 'gzip' : 'plain' });
      toast.dismiss(toastId);
    } catch (error) {
      toast.dismiss(toastId);
      throw error;
    }
  } else if (useGzip) {
    body.html_gzip_b64 = gzipB64;
  } else {
    body.html = compactHtml;
  }

  const response = await axios.post(`${apiBase}/contracts/usage`, body, {
    headers: authHeaders,
    responseType: 'blob',
  });
  return response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: 'application/pdf' });
}

function isReadableTextFile(file) {
  return file.type.startsWith('text/') || /\.(txt|csv|tsv|json|md)$/i.test(file.name);
}

function isPdfFile(file) {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result || '');
    reader.onerror = () => reject(reader.error || new Error('Datei konnte nicht gelesen werden.'));
    reader.readAsDataURL(file);
  });
}

async function readRawTextEntries(files, apiBase, authHeaders) {
  const entries = await Promise.all(files.map(async (file) => {
    if (isReadableTextFile(file)) {
      return { file, rawText: await file.text(), warning: '' };
    }
    if (isPdfFile(file)) {
      return { file, dataUrl: await readFileAsDataUrl(file), rawText: '', warning: '' };
    }
    return { file, rawText: '', warning: 'Dateityp wird ohne Textextraktion übernommen.' };
  }));

  const pdfEntries = entries.filter((entry) => entry.dataUrl);
  if (!pdfEntries.length) return entries;

  const response = await axios.post(`${apiBase}/exposes/extract`, {
    files: pdfEntries.map((entry) => ({
      name: entry.file.name,
      type: entry.file.type || 'application/pdf',
      dataUrl: entry.dataUrl,
    })),
  }, { headers: authHeaders });
  const extracted = Array.isArray(response.data?.files) ? response.data.files : [];

  return entries.map((entry) => {
    if (!entry.dataUrl) return entry;
    const match = extracted.find((item) => item.name === entry.file.name) || {};
    return {
      ...entry,
      rawText: match.text || '',
      warning: match.warning || '',
    };
  });
}

function buildStandaloneHtml(project, innerHtml, variantKey = 'expose-projektverkauf') {
  const origin = window.location.origin;
  const fixedHtml = String(innerHtml || '')
    .replace(/src="\/(?!\/)/g, `src="${origin}/`)
    .replace(/href="\/(?!\/)/g, `href="${origin}/`);
  return `<!doctype html>
<html lang="de">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${sanitizeFileName(getExposeDocumentTitle(project, variantKey))}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700;800;900&family=Outfit:wght@500;700;800;900&display=swap" rel="stylesheet" />
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; background: #e5e7eb; color: #0f172a; font-family: Manrope, Arial, sans-serif; }
    .proposal-pages { display: flex; flex-direction: column; align-items: center; gap: 0; padding: 24px 0; }
    .a4-page { width: 210mm; height: 297mm; position: relative; page-break-after: always; box-shadow: 0 18px 40px rgba(15,23,42,.18); margin: 0 auto 18px; overflow: hidden; }
    @page { size: A4; margin: 0; }
    @media print {
      body { background: #fff; }
      .proposal-pages { padding: 0; display: block; }
      .a4-page { box-shadow: none; margin: 0; page-break-after: always; }
      .export-page-label { display: none !important; }
    }
  </style>
</head>
<body>${fixedHtml}</body>
</html>`;
}

const crcTable = Array.from({ length: 256 }, (_, index) => {
  let c = index;
  for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
  return c >>> 0;
});

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let index = 0; index < bytes.length; index += 1) {
    crc = crcTable[(crc ^ bytes[index]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function dosDateTime(date = new Date()) {
  const year = Math.max(1980, date.getFullYear());
  const dosTime = (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2);
  const dosDate = ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();
  return { dosTime, dosDate };
}

function buildZipBlob(entries) {
  const encoder = new TextEncoder();
  const parts = [];
  const central = [];
  let offset = 0;
  const { dosTime, dosDate } = dosDateTime();

  entries.forEach((entry) => {
    const nameBytes = encoder.encode(entry.name);
    const dataBytes = entry.bytes || encoder.encode(entry.content || '');
    const checksum = crc32(dataBytes);
    const local = new Uint8Array(30 + nameBytes.length);
    const localView = new DataView(local.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, 20, true);
    localView.setUint16(6, 0, true);
    localView.setUint16(8, 0, true);
    localView.setUint16(10, dosTime, true);
    localView.setUint16(12, dosDate, true);
    localView.setUint32(14, checksum, true);
    localView.setUint32(18, dataBytes.length, true);
    localView.setUint32(22, dataBytes.length, true);
    localView.setUint16(26, nameBytes.length, true);
    localView.setUint16(28, 0, true);
    local.set(nameBytes, 30);
    parts.push(local, dataBytes);
    central.push({ nameBytes, dataBytes, checksum, offset });
    offset += local.length + dataBytes.length;
  });

  const centralStart = offset;
  central.forEach((entry) => {
    const header = new Uint8Array(46 + entry.nameBytes.length);
    const view = new DataView(header.buffer);
    view.setUint32(0, 0x02014b50, true);
    view.setUint16(4, 20, true);
    view.setUint16(6, 20, true);
    view.setUint16(8, 0, true);
    view.setUint16(10, 0, true);
    view.setUint16(12, dosTime, true);
    view.setUint16(14, dosDate, true);
    view.setUint32(16, entry.checksum, true);
    view.setUint32(20, entry.dataBytes.length, true);
    view.setUint32(24, entry.dataBytes.length, true);
    view.setUint16(28, entry.nameBytes.length, true);
    view.setUint16(30, 0, true);
    view.setUint16(32, 0, true);
    view.setUint16(34, 0, true);
    view.setUint16(36, 0, true);
    view.setUint32(38, 0, true);
    view.setUint32(42, entry.offset, true);
    header.set(entry.nameBytes, 46);
    parts.push(header);
    offset += header.length;
  });

  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(8, central.length, true);
  endView.setUint16(10, central.length, true);
  endView.setUint32(12, offset - centralStart, true);
  endView.setUint32(16, centralStart, true);
  parts.push(end);

  return new Blob(parts, { type: 'application/zip' });
}

function normalizeHexColor(value) {
  const text = String(value || '').trim();
  if (/^#[0-9a-f]{6}$/i.test(text)) return text;
  if (/^[0-9a-f]{6}$/i.test(text)) return `#${text}`;
  if (/^#[0-9a-f]{3}$/i.test(text)) {
    return `#${text.slice(1).split('').map((char) => `${char}${char}`).join('')}`;
  }
  return '';
}

function isColorField(key) {
  return /^expose_.*_color$/.test(String(key || ''));
}

function sanitizeCssClassPart(value) {
  return String(value || 'expose')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 56) || 'expose';
}

function buildExposeColorCss(data, scopeClass) {
  const text = normalizeHexColor(data.expose_text_color);
  const heading = normalizeHexColor(data.expose_heading_color);
  const muted = normalizeHexColor(data.expose_muted_text_color);
  const accent = normalizeHexColor(data.expose_accent_color);
  const cover = normalizeHexColor(data.expose_cover_text_color);
  const scope = `.${scopeClass}`;
  const rules = [];

  if (text) {
    rules.push(`${scope} .a4-page p, ${scope} .a4-page span, ${scope} .a4-page td, ${scope} .a4-page th, ${scope} .a4-page label { color: ${text} !important; }`);
  }
  if (muted) {
    rules.push(`${scope} .a4-page [style*="#64748B"], ${scope} .a4-page [style*="#475569"], ${scope} .a4-page [style*="#415675"], ${scope} .a4-page [style*="#6B7C96"], ${scope} .a4-page [style*="#94A3B8"], ${scope} .a4-page [style*="#A4AFBF"] { color: ${muted} !important; }`);
  }
  if (heading) {
    rules.push(`${scope} .a4-page h1, ${scope} .a4-page h2, ${scope} .a4-page h3, ${scope} .a4-page [style*="Outfit"] { color: ${heading} !important; }`);
  }
  if (cover) {
    rules.push(`${scope} [data-testid^="cover-page"] p, ${scope} [data-testid^="cover-page"] span, ${scope} [data-testid^="cover-page"] h1, ${scope} [data-testid^="cover-page"] h2, ${scope} [data-testid^="cover-page"] h3 { color: ${cover} !important; }`);
  }
  if (accent) {
    rules.push(`${scope} .a4-page [style*="#E07000"], ${scope} .a4-page [style*="224,112,0"], ${scope} .a4-page [style*="#FDBA74"] { color: ${accent} !important; }`);
    rules.push(`${scope} .a4-page svg, ${scope} .a4-page svg * { color: ${accent} !important; }`);
  }

  return rules.join('\n');
}

function ExposePreview({ data, variantKey, exportMode = false, onFieldChange, hideEmptyFields = true }) {
  const isTurnkey = variantKey === 'expose-turnkey';
  const isWirtschaftlichkeit = variantKey === 'expose-wirtschaftlichkeit';
  const labelClass = exportMode ? 'export-page-label' : '';
  const inlineEditMode = !exportMode && typeof onFieldChange === 'function';
  const colorScopeClass = `expose-color-${sanitizeCssClassPart(data.id || data.projekt_id || data.projektname || variantKey)}`;
  const colorCss = buildExposeColorCss(data, colorScopeClass);

  const label = (number, title) => (
    <div className={labelClass} style={{ margin: '18px 0 8px', color: '#64748B', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
      Seite {number} · {title}
    </div>
  );

  if (isWirtschaftlichkeit) {
    return (
      <div className={`proposal-pages ${colorScopeClass}`} data-testid="proposal-canvas-expose-bulk" style={{ alignItems: 'center' }}>
        {colorCss ? <style>{colorCss}</style> : null}
        <WirtschaftlichkeitsAnalyse data={data} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} hideEmptyFields={hideEmptyFields} showLabels labelClass={labelClass} />
      </div>
    );
  }

  return (
    <div className={`proposal-pages ${colorScopeClass}`} data-testid="proposal-canvas-expose-bulk" style={{ alignItems: 'center' }}>
      {colorCss ? <style>{colorCss}</style> : null}
      {label(1, isTurnkey ? 'Deckblatt Turnkey' : 'Deckblatt Projektrechte')}
      <ProjectExposeCover data={data} variant={variantKey} editMode={false} showMarkers={false} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} hideEmptyFields={hideEmptyFields} />
      {label(2, 'Wirtschaft + Standort + Netzanschluss')}
      <ProjectExposePage2 data={data} variant={variantKey} editMode={false} showMarkers={false} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} hideEmptyFields={hideEmptyFields} />
      {label(3, 'PVSol + Prüfungen + Lageplan')}
      <ProjectExposePage3 data={data} variant={variantKey} editMode={false} showMarkers={false} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} hideEmptyFields={hideEmptyFields} />
      {isTurnkey ? (
        <>
          {label(4, 'Ertragsprognose + Cashflow + OPEX')}
          <ProjectExposePage4 data={data} variant={variantKey} editMode={false} showMarkers={false} inlineEditMode={inlineEditMode} onFieldChange={onFieldChange} hideEmptyFields={hideEmptyFields} />
        </>
      ) : null}
    </div>
  );
}

export default function ExposeRawBulkStudio({
  activeVariantKey,
  onVariantChange,
  apiBase = '/api',
  authHeaders = {},
  bitrixMapping = {},
  onOpenInTemplateEditor,
}) {
  const [restoredDraft] = useState(() => {
    const draft = loadExposeBulkDraft();
    if (Array.isArray(draft.projects)) {
      draft.projects = filterExpiredProjects(draft.projects.map(stampProject)).map((project) => {
        const next = { ...project };
        DETAIL_FIELDS.forEach((field) => {
          const val = next[field.key];
          if (typeof val === 'string' && val.includes('"')) {
            next[field.key] = cleanImportedExposeValue(val);
          }
        });
        if (next.variantData) {
          const nextVd = {};
          Object.entries(next.variantData).forEach(([vk, vv]) => {
            if (!vv || typeof vv !== 'object') { nextVd[vk] = vv; return; }
            const cleaned = { ...vv };
            DETAIL_FIELDS.forEach((field) => {
              const val = cleaned[field.key];
              if (typeof val === 'string' && val.includes('"')) {
                cleaned[field.key] = cleanImportedExposeValue(val);
              }
            });
            nextVd[vk] = cleaned;
          });
          next.variantData = nextVd;
        }
        return next;
      });
    }
    return draft;
  });
  const [projects, setProjects] = useState(() => restoredDraft.projects || []);
  const [selectedIds, setSelectedIds] = useState(() => restoredDraft.selectedIds || []);
  const [activeId, setActiveId] = useState(() => restoredDraft.activeId || '');
  const [dragActive, setDragActive] = useState(false);
  const [bulkField, setBulkField] = useState(() => restoredDraft.bulkField || 'status');
  const [bulkText, setBulkText] = useState(() => restoredDraft.bulkText || '');
  const [detailFieldSearch, setDetailFieldSearch] = useState('');
  const [detailVisibilityFilter, setDetailVisibilityFilter] = useState('all');
  const [studioTab, setStudioTab] = useState('projekte');
  const [assistantFieldFilter, setAssistantFieldFilter] = useState('open');
  const [hideEmptyFields, setHideEmptyFields] = useState(false);
  const [showExtendedFields, setShowExtendedFields] = useState(() => loadShowExtendedFieldsLocal());
  const [showFieldSources, setShowFieldSources] = useState(() => loadShowFieldSourcesLocal());
  const [fieldDefaults, setFieldDefaults] = useState({});
  const [visibilityPresets, setVisibilityPresets] = useState(() => loadVisibilityPresetsLocal());
  const [selectedPresetName, setSelectedPresetName] = useState('');
  const [exporting, setExporting] = useState(false);
  const [readingFiles, setReadingFiles] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(() => restoredDraft.savedAt || '');
  const [draftMediaLimited, setDraftMediaLimited] = useState(() => restoredDraft.mediaLimited === true);
  const [savedDrafts, setSavedDrafts] = useState(() => loadSavedExposeDrafts());
  const [selectedDraftId, setSelectedDraftId] = useState('');
  const [activeSavedDraftId, setActiveSavedDraftId] = useState('');
  const [dealIdInput, setDealIdInput] = useState('');
  const [loadingBitrixDeals, setLoadingBitrixDeals] = useState(false);
  const [reloadingPvsol, setReloadingPvsol] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [supabaseSaveStatus, setSupabaseSaveStatus] = useState('idle');
  const [dialog, setDialog] = useState(null);
  const dialogInputRef = useRef(null);
  const exportMenuRef = useRef(null);
  const supabaseAutoSaveTimer = useRef(null);
  // Letzter erfolgreich gespeicherter Stand je Deal. Verhindert, dass eine Änderung an EINEM
  // Projekt alle geladenen Projekte erneut nach Supabase schreibt.
  const supabaseSavedSnapshots = useRef(new Map());
  const restoredVariantAppliedRef = useRef(false);
  const restoredSupabaseSyncRef = useRef(false);
  const exportRefs = useRef({});

  useEffect(() => {
    if (!exportMenuOpen) return undefined;
    const handleOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [exportMenuOpen]);

  const activeProject = useMemo(
    () => projects.find((project) => project.id === activeId) || projects[0] || null,
    [activeId, projects],
  );
  const selectedDraft = useMemo(
    () => savedDrafts.find((draft) => draft.id === selectedDraftId) || null,
    [savedDrafts, selectedDraftId],
  );
  const activeEditableProject = useMemo(() => getEditableProject(activeProject, activeVariantKey), [activeProject, activeVariantKey]);
  const activeData = useMemo(() => buildExposeData(activeProject, activeVariantKey), [activeProject, activeVariantKey]);
  const activeHasTurnkeyDataset = activeProject ? hasTurnkeyDataset(activeProject) : false;
  const filteredDetailFieldGroups = useMemo(() => {
    let groups = EXPOSE_DETAIL_FIELD_GROUPS;
    const variantFields = VARIANT_DEFAULT_FIELDS[activeVariantKey];
    if (variantFields) {
      groups = groups
        .map((group) => ({ ...group, fields: group.fields.filter((field) => variantFields.has(field.key) || VARIANT_INDEPENDENT_FIELD_KEYS.has(field.key)) }))
        .filter((group) => group.fields.length > 0);
    }
    if (!showExtendedFields) {
      groups = groups
        .map((group) => ({ ...group, fields: group.fields.filter((field) => !EXPOSE_EXTENDED_FIELD_KEYS.has(field.key)) }))
        .filter((group) => group.fields.length > 0);
    }
    return groups;
  }, [activeVariantKey, showExtendedFields]);
  const visibleDetailFieldGroups = useMemo(
    () => filterExposeDetailFieldGroupsByVisibility(
      filterExposeDetailFieldGroups(filteredDetailFieldGroups, detailFieldSearch),
      activeEditableProject,
      detailVisibilityFilter,
    ),
    [activeEditableProject, detailFieldSearch, detailVisibilityFilter, filteredDetailFieldGroups],
  );
  const visibleDetailFieldCount = useMemo(
    () => visibleDetailFieldGroups.reduce((sum, group) => sum + group.fields.length, 0),
    [visibleDetailFieldGroups],
  );
  const activeAssistantReports = useMemo(
    () => {
      const variantFields = VARIANT_DEFAULT_FIELDS[activeVariantKey];
      return ASSISTANT_FIELD_GROUPS
        .map((group) => {
          const filteredFields = showExtendedFields || !variantFields
            ? group.fields
            : group.fields.filter((fieldKey) => variantFields.has(fieldKey));
          const filteredGroup = { ...group, fields: filteredFields };
          return {
            ...filteredGroup,
            report: getAssistantGroupReport(activeEditableProject, filteredGroup),
          };
        })
        .filter((group) => group.fields.length > 0);
    },
    [activeEditableProject, activeVariantKey, showExtendedFields],
  );
  const activeRequiredMissingFields = useMemo(
    () => activeAssistantReports.flatMap((group) => group.report.requiredMissing),
    [activeAssistantReports],
  );
  const activeOpenFields = useMemo(() => {
    const seen = new Set();
    const fields = activeAssistantReports
      .flatMap((group) => group.report.missing.map((field) => ({ ...field, groupTitle: group.title })))
      .filter((field) => {
        if (seen.has(field.key)) return false;
        seen.add(field.key);
        return true;
      });
    return fields;
  }, [activeAssistantReports]);
  const projectReadiness = useMemo(() => {
    if (!activeEditableProject) return 0;
    const total = activeAssistantReports.reduce((sum, group) => sum + group.fields.length, 0);
    const filled = activeAssistantReports.reduce((sum, group) => (
      sum + group.fields.filter((fieldKey) => isExposeFieldFilled(activeEditableProject, fieldKey)).length
    ), 0);
    return total ? Math.round((filled / total) * 100) : 0;
  }, [activeEditableProject, activeAssistantReports]);

  const stats = useMemo(() => {
    const complete = projects.filter((project) => completeness(project).missing.length === 0).length;
    return { complete, incomplete: projects.length - complete };
  }, [projects]);
  const draftSavedLabel = useMemo(() => {
    if (!draftSavedAt) return '';
    const date = new Date(draftSavedAt);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }, [draftSavedAt]);

  useEffect(() => {
    if (restoredVariantAppliedRef.current) return;
    restoredVariantAppliedRef.current = true;
    if (restoredDraft.activeVariantKey && restoredDraft.activeVariantKey !== activeVariantKey) {
      onVariantChange?.(restoredDraft.activeVariantKey);
    }
  }, [activeVariantKey, onVariantChange, restoredDraft.activeVariantKey]);

  useEffect(() => {
    const result = saveExposeBulkDraft({
      projects,
      selectedIds,
      activeId,
      bulkField,
      bulkText,
      activeVariantKey,
    });
    setDraftSavedAt(result?.savedAt || '');
    setDraftMediaLimited(result?.mediaLimited === true);
  }, [activeId, activeVariantKey, bulkField, bulkText, projects, selectedIds]);

  // Debounced Supabase autosave — saves projects with a dealId after 2s of inactivity
  useEffect(() => {
    if (!authHeaders?.Authorization || !projects.length) return undefined;
    const projectsWithDeal = projects.filter((p) => p.dealId);
    if (!projectsWithDeal.length) return undefined;
    clearTimeout(supabaseAutoSaveTimer.current);
    supabaseAutoSaveTimer.current = setTimeout(async () => {
      // Erst ermitteln, was sich geändert hat — dann erst schreiben. So bleibt der Status
      // unauffällig, wenn es nichts zu tun gibt, und es geht keine Anfrage unnötig raus.
      const pending = [];
      for (const project of projectsWithDeal) {
        const exposeFields = {};
        for (const key of EXPOSE_SUPABASE_SAVE_KEYS) {
          const val = project[key];
          if (val == null || val === '') continue;
          // Zweite Sicherung gegen Bildmaterial, unabhängig vom Schlüsselnamen: Data-URIs und
          // Bitrix-Dateiverweise bleiben draußen. Sonst landet ein mehrere MB großes Base64-Bild
          // im Datensatz und bläht ihn bei jedem Tastendruck erneut auf.
          const asText = String(val);
          if (asText.startsWith('data:') || /\/bitrix\//i.test(asText)) continue;
          exposeFields[key] = val;
        }
        if (!Object.keys(exposeFields).length) continue;
        const snapshot = JSON.stringify(exposeFields);
        if (supabaseSavedSnapshots.current.get(project.dealId) === snapshot) continue;
        pending.push({ dealId: project.dealId, exposeFields, snapshot });
      }
      if (!pending.length) return;

      setSupabaseSaveStatus('saving');
      let anyFailed = false;
      for (const item of pending) {
        // eslint-disable-next-line no-await-in-loop
        const ok = await saveExposeDataToSupabase(item.dealId, item.exposeFields, authHeaders);
        if (ok) supabaseSavedSnapshots.current.set(item.dealId, item.snapshot);
        else anyFailed = true;
      }
      setSupabaseSaveStatus(anyFailed ? 'error' : 'saved');
      setTimeout(() => setSupabaseSaveStatus((s) => s === 'saved' ? 'idle' : s), 4000);
    }, 2000);
    return () => clearTimeout(supabaseAutoSaveTimer.current);
  }, [projects, authHeaders]);

  // Draft-Restore: Projekte werden beim App-Start synchron aus dem localStorage-Draft
  // wiederhergestellt (useState-Initializer). Auf diesem Pfad läuft KEIN Supabase-Merge
  // (nur handleLoadBitrixDeals macht das). Deshalb hier EINMALIG pro Session – sobald Auth
  // verfügbar ist – die in Supabase gespeicherten Werte nachladen und NUR LEERE Felder füllen
  // (top-level + variantData-Reparatur). Kein Extraktions-Trigger, keine Toasts.
  // Das Flag wird bei erster Auth-Verfügbarkeit gesetzt (Restore-Projekte liegen dann bereits
  // im State) – so werden später über handleLoadBitrixDeals geladene Projekte nicht doppelt gemergt.
  useEffect(() => {
    if (restoredSupabaseSyncRef.current) return undefined;
    if (!authHeaders?.Authorization) return undefined;
    restoredSupabaseSyncRef.current = true;
    const dealProjects = projects.filter((p) => p.dealId);
    if (!dealProjects.length) return undefined;
    (async () => {
      const imageRefetchQueue = [];
      for (const dealProject of dealProjects) {
        const supabaseData = await fetchExposeDataFromSupabase(dealProject.dealId, authHeaders);
        const updates = {};
        if (supabaseData?.fields) {
          const pvsolSet = Array.isArray(supabaseData.pvsolFields)
            ? new Set(supabaseData.pvsolFields.map(String))
            : null;
          for (const [key, value] of Object.entries(supabaseData.fields)) {
            if (!value) continue;
            // Keys mit entferntem Bitrix-Mapping werden aus dem Cache nicht gefüllt – AUSSER sie
            // stammen aus PV*SOL (Key im pvsolSet des Supabase-Datensatzes). PV*SOL-Herkunft ist
            // vertrauenswürdig (z. B. netzanschluss = kumulierte Wechselrichter-Leistung); alte
            // Bitrix-Müllwerte stehen nie im pvsolSet.
            if (REMOVED_BITRIX_DATA_KEYS.has(key) && !(pvsolSet && pvsolSet.has(String(key)))) continue;
            const cleaned = cleanImportedExposeValue(value);
            if (!cleaned) continue;
            updates[key] = normalizeImportedFieldValue(key, cleaned);
          }
          if (Object.keys(updates).length) {
            setProjects((current) => current.map((p) => {
              if (p.id !== dealProject.id) return p;
              const filled = fillEmptyProjectFields(p, updates);
              const nextSources = { ...(p.__fieldSources || {}) };
              Object.keys(updates).forEach((key) => {
                if (p[key] != null && String(p[key]) !== '') return;
                const isPvsol = pvsolSet ? pvsolSet.has(String(key)) : isPvsolExposeFieldKey(key);
                nextSources[key] = isPvsol ? 'pvsol' : 'gespeichert';
              });
              return { ...filled, __fieldSources: nextSources, _supabaseExposeData: supabaseData };
            }));
          }
        }
        const isUsableImage = (v) => {
          if (!v) return false;
          const s = String(v);
          if (s.startsWith('data:image/')) return true;
          if (/\/bitrix\//i.test(s)) return false;
          return s.startsWith('http');
        };
        const hasProjectImage = isUsableImage(dealProject.projectImage) || isUsableImage(updates.projectImage);
        const hasLageplanImage = isUsableImage(dealProject.lageplanImage) || isUsableImage(updates.lageplanImage);
        if (!hasProjectImage || !hasLageplanImage) {
          imageRefetchQueue.push({ id: dealProject.id, dealId: dealProject.dealId, needProject: !hasProjectImage, needLageplan: !hasLageplanImage });
        }
      }
      for (const item of imageRefetchQueue) {
        try {
          const images = await resolveExposeImagesFromBitrix(item.dealId, authHeaders);
          const imageUpdates = {};
          if (item.needProject && images.projectImage) imageUpdates.projectImage = images.projectImage;
          if (item.needLageplan && images.lageplanImage) imageUpdates.lageplanImage = images.lageplanImage;
          if (!Object.keys(imageUpdates).length) continue;
          setProjects((current) => current.map((p) => {
            if (p.id !== item.id) return p;
            return { ...p, ...imageUpdates };
          }));
        } catch (_e) { /* ignore - component may have unmounted */ }
      }
    })();
    return undefined;
  }, [projects, authHeaders]);

  useEffect(() => {
    if (typeof window === 'undefined' || (!projects.length && !String(bulkText || '').trim())) {
      return undefined;
    }
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [bulkText, projects.length]);

  useEffect(() => {
    if (!authHeaders?.Authorization) return;
    let cancelled = false;
    axios.get(CONTRACTS_AM_URL, {
      params: { op: 'mapping', contract_key: EXPOSE_PREFERENCES_CONTRACT_KEY },
      headers: authHeaders,
    }).then((response) => {
      if (cancelled) return;
      const prefs = response.data?.mapping?.fields;
      if (prefs && typeof prefs === 'object') {
        const val = prefs.showExtendedFields === true || prefs.showExtendedFields === '1';
        setShowExtendedFields(val);
        persistShowExtendedFieldsLocal(val);
        // Supabase gewinnt über localStorage: nur wenn der Preferences-Datensatz
        // Presets enthält, überschreiben wir den lokalen Stand.
        if (prefs.visibilityPresets != null) {
          const normalized = normalizeVisibilityPresets(prefs.visibilityPresets);
          setVisibilityPresets(normalized);
          persistVisibilityPresetsLocal(normalized);
        }
      }
    }).catch(() => { /* use localStorage fallback */ });
    return () => { cancelled = true; };
  }, [authHeaders]);

  // Standardwerte laden (Mapping-Editor pflegt sie unter EXPOSE_FIELD_DEFAULTS_CONTRACT_KEY,
  // Keys sind PLATZHALTER-Namen). Wir übersetzen sie in ein dataKey→Wert-Objekt.
  useEffect(() => {
    if (!authHeaders?.Authorization) return undefined;
    let cancelled = false;
    axios.get(CONTRACTS_AM_URL, {
      params: { op: 'mapping', contract_key: EXPOSE_FIELD_DEFAULTS_CONTRACT_KEY },
      headers: authHeaders,
    }).then((response) => {
      if (cancelled) return;
      const fields = response.data?.mapping?.fields;
      if (!fields || typeof fields !== 'object') return;
      const byDataKey = {};
      EXPOSE_PLACEHOLDER_FIELDS.forEach((field) => {
        const value = fields[field.placeholder];
        if (value === undefined || value === null || String(value).trim() === '') return;
        (field.dataKeys || []).forEach((dataKey) => {
          byDataKey[dataKey] = value;
        });
      });
      setFieldDefaults(byDataKey);
    }).catch(() => { /* Fehler still schlucken */ });
    return () => { cancelled = true; };
  }, [authHeaders]);

  // Schreibt das komplette Preferences-Objekt (showExtendedFields + visibilityPresets)
  // in einem Rutsch, damit ein etwaiges Replace-Verhalten des mapping-Endpoints keine
  // der beiden Einstellungen verliert.
  const persistExposePreferencesSupabase = (fields) => {
    if (!authHeaders?.Authorization) return;
    axios.post(CONTRACTS_AM_URL, {
      op: 'mapping',
      contract_key: EXPOSE_PREFERENCES_CONTRACT_KEY,
      fields,
    }, { headers: authHeaders }).catch(() => { /* best-effort */ });
  };

  const persistShowExtendedFieldsSupabase = (value) => {
    persistShowExtendedFieldsLocal(value);
    persistExposePreferencesSupabase({ showExtendedFields: value, visibilityPresets });
  };

  // Übernimmt Presets in State, localStorage und Supabase (Preferences).
  const persistVisibilityPresets = (nextPresets) => {
    const normalized = normalizeVisibilityPresets(nextPresets);
    setVisibilityPresets(normalized);
    persistVisibilityPresetsLocal(normalized);
    persistExposePreferencesSupabase({ showExtendedFields, visibilityPresets: normalized });
  };

  // Ersetzt die __hiddenFields der angegebenen Projekte komplett durch die Preset-Liste.
  // Nutzt dieselbe setProjects-map-Mechanik wie setProjectFieldHidden, damit Autosave/Draft
  // normal greifen. variantKey wird explizit übergeben, da beim Variantenwechsel der
  // activeVariantKey-Prop noch den alten Wert hält.
  const applyPresetHiddenFieldsToIds = (ids, hiddenList, variantKey = activeVariantKey) => {
    const idSet = new Set(Array.isArray(ids) ? ids : []);
    if (!idSet.size) return;
    const sanitized = Array.isArray(hiddenList) ? hiddenList.map((entry) => String(entry)).filter(Boolean) : [];
    setProjects((current) => current.map((project) => {
      if (!idSet.has(project.id)) return project;
      const writesTurnkey = variantKey === TURNKEY_VARIANT_KEY && hasTurnkeyDataset(project);
      if (writesTurnkey) {
        return {
          ...project,
          variantData: {
            ...(project.variantData || {}),
            [TURNKEY_VARIANT_KEY]: {
              ...(project.variantData?.[TURNKEY_VARIANT_KEY] || {}),
              __hiddenFields: [...sanitized],
            },
          },
        };
      }
      return { ...project, __hiddenFields: [...sanitized] };
    }));
  };

  const handleApplyVisibilityPreset = async () => {
    if (!selectedPresetName || !activeProject) return;
    const preset = visibilityPresets[selectedPresetName];
    if (!Array.isArray(preset)) {
      toast.error('Preset nicht gefunden');
      return;
    }
    const label = presetDisplayLabel(selectedPresetName);
    if (selectedIds.length > 1) {
      const confirmed = await showConfirm(`Preset "${label}" auf alle ${selectedIds.length} ausgewählten Projekte anwenden? (Abbrechen wendet es nur auf das aktive Projekt an.)`);
      if (confirmed) {
        applyPresetHiddenFieldsToIds(selectedIds, preset);
        toast.success(`Preset "${label}" auf ${selectedIds.length} Projekte angewendet`);
        return;
      }
    }
    applyPresetHiddenFieldsToIds([activeProject.id], preset);
    toast.success(`Preset "${label}" angewendet`);
  };

  // Variantenwechsel-Handler: schaltet um und wendet – falls vorhanden – automatisch
  // das gleichnamige Preset auf das aktive Projekt an (nicht als useEffect, damit es
  // nur bei echter Nutzerinteraktion feuert, nicht beim Mount/Projektwechsel).
  const handleVariantSwitch = (key) => {
    onVariantChange?.(key);
    if (key === activeVariantKey) return;
    const preset = visibilityPresets[key];
    if (Array.isArray(preset) && activeProject) {
      applyPresetHiddenFieldsToIds([activeProject.id], preset, key);
      toast.info(`Preset "${presetDisplayLabel(key)}" angewendet`);
    }
  };

  useEffect(() => {
    if (!EXPOSE_DETAIL_FIELDS.some((field) => field.key === bulkField)) {
      setBulkField(EXPOSE_DETAIL_FIELDS[0]?.key || 'status');
    }
  }, [bulkField]);

  useEffect(() => {
    if (!dialog) return undefined;
    const handleKeyDown = (event) => { if (event.key === 'Escape') dialog.onCancel(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dialog]);

  const setProjectValue = (projectId, key, value) => {
    const normalizedValue = normalizeFixedUnitFieldValue(key, value);
    setProjects((current) => current.map((project) => (
      project.id === projectId ? { ...project, [key]: normalizedValue } : project
    )));
  };

  const setActiveProjectDetailValue = (projectId, key, value) => {
    const normalizedValue = normalizeFixedUnitFieldValue(key, value);
    setProjects((current) => current.map((project) => {
      if (project.id !== projectId) return project;
      const writesTurnkey = activeVariantKey === TURNKEY_VARIANT_KEY && hasTurnkeyDataset(project);
      // Der Wert landet im Turnkey-Modus in variantData, sonst am Top-Level. __fieldSources
      // wird aber IMMER am Top-Level geführt: getEditableProject spreadt zuerst {...project},
      // sodass die Source auch im Turnkey-Datensatz sichtbar ist (variantData enthält kein
      // __fieldSources und überschreibt es daher nie). Source nur bei echter Wertänderung.
      const previousValue = writesTurnkey
        ? (project.variantData?.[TURNKEY_VARIANT_KEY]?.[key] ?? '')
        : (project[key] ?? '');
      const nextSources = String(previousValue) === String(normalizedValue)
        ? project.__fieldSources
        : { ...(project.__fieldSources || {}), [key]: 'manuell' };
      if (writesTurnkey) {
        return {
          ...project,
          __fieldSources: nextSources,
          variantData: {
            ...(project.variantData || {}),
            [TURNKEY_VARIANT_KEY]: {
              ...(project.variantData?.[TURNKEY_VARIANT_KEY] || {}),
              [key]: normalizedValue,
            },
          },
        };
      }
      return { ...project, [key]: normalizedValue, __fieldSources: nextSources };
    }));
  };

  const mergeProjectValues = (projectId, values) => {
    setProjects((current) => current.map((project) => (
      project.id === projectId ? { ...project, ...values } : project
    )));
  };

  const mergeActiveProjectDetailValues = (projectId, values) => {
    setProjects((current) => current.map((project) => {
      if (project.id !== projectId) return project;
      if (activeVariantKey === TURNKEY_VARIANT_KEY && hasTurnkeyDataset(project)) {
        return {
          ...project,
          variantData: {
            ...(project.variantData || {}),
            [TURNKEY_VARIANT_KEY]: {
              ...(project.variantData?.[TURNKEY_VARIANT_KEY] || {}),
              ...values,
            },
          },
        };
      }
      return { ...project, ...values };
    }));
  };

  const setProjectFieldHidden = (projectId, fieldKey, hidden) => {
    setProjects((current) => current.map((project) => {
      if (project.id !== projectId) return project;
      const writesTurnkey = activeVariantKey === TURNKEY_VARIANT_KEY && hasTurnkeyDataset(project);
      const source = writesTurnkey ? (project.variantData?.[TURNKEY_VARIANT_KEY] || {}) : project;
      const currentHidden = Array.isArray(source.__hiddenFields) ? source.__hiddenFields : [];
      const aliases = hiddenFieldAliases(fieldKey);
      const nextHidden = hidden
        ? Array.from(new Set([...currentHidden, ...aliases]))
        : currentHidden.filter((key) => !aliases.includes(key));
      if (writesTurnkey) {
        return {
          ...project,
          variantData: {
            ...(project.variantData || {}),
            [TURNKEY_VARIANT_KEY]: {
              ...(project.variantData?.[TURNKEY_VARIANT_KEY] || {}),
              __hiddenFields: nextHidden,
            },
          },
        };
      }
      return { ...project, __hiddenFields: nextHidden };
    }));
  };

  const toggleFieldLock = (projectId, fieldKey) => {
    setProjects((current) => current.map((project) => {
      if (project.id !== projectId) return project;
      const locked = Array.isArray(project.__lockedFields) ? project.__lockedFields : [];
      const isLocked = locked.includes(fieldKey);
      const nextLocked = isLocked ? locked.filter((k) => k !== fieldKey) : [...locked, fieldKey];
      return { ...project, __lockedFields: nextLocked };
    }));
  };

  const handleExposeFieldChange = (fieldKey, value) => {
    if (!activeProject) return;
    const mappedKey = mapExposeFieldToProjectField(fieldKey, activeVariantKey);
    const fieldValue = normalizeFixedUnitFieldValue(fieldKey, value);
    const mappedValue = normalizeFixedUnitFieldValue(mappedKey, value);
    setProjects((current) => current.map((project) => {
      if (project.id !== activeProject.id) return project;
      if (activeVariantKey === TURNKEY_VARIANT_KEY && hasTurnkeyDataset(project)) {
        return {
          ...project,
          variantData: {
            ...(project.variantData || {}),
            [TURNKEY_VARIANT_KEY]: {
              ...(project.variantData?.[TURNKEY_VARIANT_KEY] || {}),
              [fieldKey]: fieldValue,
              [mappedKey]: mappedValue,
            },
          },
        };
      }
      return {
        ...project,
        [fieldKey]: fieldValue,
        [mappedKey]: mappedValue,
      };
    }));
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    setReadingFiles(true);
    try {
      const entries = await readRawTextEntries(files, apiBase, authHeaders);
      const parsed = entries.flatMap(createProjectsFromRawEntry).map(stampProject);
      const warnings = parsed.filter((project) => project.extractionWarning).length;

      setProjects((current) => [...current, ...parsed]);
      setSelectedIds((current) => [...new Set([...current, ...parsed.map((project) => project.id)])]);
      setActiveId((current) => current || parsed[0]?.id || '');
      toast.success(`${parsed.length} Rohdatei${parsed.length === 1 ? '' : 'en'} eingelesen${warnings ? `, ${warnings} mit Hinweis` : ''}`);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || error?.message || 'Rohdateien konnten nicht gelesen werden.');
    } finally {
      setReadingFiles(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    if (readingFiles) return;
    handleFiles(event.dataTransfer.files);
  };

  const handleLoadBitrixDeals = async () => {
    const dealIds = Array.from(new Set(
      String(dealIdInput || '')
        .split(/[\s,;]+/)
        .map((entry) => entry.trim().replace(/^(D_|DEAL_|d_|deal_)/, ''))
        .filter(Boolean)
    ));

    if (!dealIds.length) {
      toast.error('Bitte mindestens eine Bitrix-Deal-ID eingeben.');
      return;
    }

    setLoadingBitrixDeals(true);
    try {
      const fields = getBitrixExposeImportFields();
      // Keep-Empty: bitrixMapping (= mappingDraft aus ExposeAutomationStudio, dort bereits
      // legacy-bereinigt) enthält bewusst gesetzte Leerstrings für entfernte Mappings. Diese
      // müssen die Defaults überschreiben UND an den Server gehen, damit er sie als explizite
      // Entfernung erkennt (statt den Default erneut aufzulösen).
      const mappingFields = {
        ...DEFAULT_EXPOSE_BITRIX_MAPPING,
        ...normalizeExposeMappingFieldsKeepEmpty(bitrixMapping),
      };
      const loadedProjects = [];
      const failed = [];

      for (const dealId of dealIds) {
        try {
          const response = await axios.post(`${apiBase}/contracts/usage`, {
            action: 'expose-preview',
            expose_key: activeVariantKey,
            deal_id: dealId,
            mapping: { fields: mappingFields },
            fields,
          }, { headers: authHeaders });

          const project = stampProject(createProjectFromBitrixPayload(
            response.data?.proposalData || {},
            dealId,
            activeVariantKey,
            response.data?.bitrixSummary || {},
            response.data?.resolvedFields || {},
          ));

          // Quellen-Tracking: NUR Felder, die der Server wirklich aus Bitrix aufgelöst hat
          // (response.data.resolvedFields = Platzhalter → Wert), als 'bitrix' markieren. Aus den
          // aufgelösten Platzhaltern die zugehörigen dataKeys ableiten. Client-generierte Werte
          // (z. B. datum = heute, status-Fallback, PV*SOL-Felder) bekommen so kein falsches Badge.
          const resolvedPlaceholders = response.data?.resolvedFields && typeof response.data.resolvedFields === 'object'
            ? response.data.resolvedFields
            : {};
          const bitrixDataKeys = new Set();
          EXPOSE_PLACEHOLDER_FIELDS.forEach((field) => {
            const resolvedVal = resolvedPlaceholders[field.placeholder];
            if (resolvedVal == null || String(resolvedVal).trim() === '') return;
            (field.dataKeys || []).forEach((dk) => bitrixDataKeys.add(dk));
          });
          const fieldSources = {};
          DETAIL_FIELDS.forEach((field) => {
            if (!bitrixDataKeys.has(field.key)) return;
            const val = project[field.key];
            if (val != null && String(val) !== '') fieldSources[field.key] = 'bitrix';
          });

          let supabaseData = await fetchExposeDataFromSupabase(dealId, authHeaders);

          // PV*SOL-Extraktion anstoßen, wenn der Deal noch nie verarbeitet wurde
          // (keine Supabase-Daten oder fehlender _pvsol_updated_at-Zeitstempel).
          if (!supabaseData || !supabaseData.pvsolUpdatedAt) {
            toast.info('PV*SOL-Daten werden extrahiert…');
            const extraction = await triggerPvsolExtraction(dealId, authHeaders);
            if (extraction?.ok && !extraction.skipped) {
              const refreshed = await fetchExposeDataFromSupabase(dealId, authHeaders);
              if (refreshed?.fields) supabaseData = refreshed;
            } else if (extraction?.skipped && extraction.reason && extraction.reason !== 'no_pvsol_file') {
              // 'no_pvsol_file' ist normal (Deal ohne PV*SOL-Datei) → still weiter.
              toast.info(`PV*SOL (${dealId}): ${pvsolSkipLabel(extraction.reason)}`);
            }
          }

          let loaded;
          if (supabaseData?.fields) {
            // _pvsol_fields (Neudaten) markiert genau die aus PV*SOL stammenden Keys;
            // ohne diese Angabe (Altdaten) greift der isPvsolExposeFieldKey-Fallback.
            const pvsolSet = Array.isArray(supabaseData.pvsolFields)
              ? new Set(supabaseData.pvsolFields.map(String))
              : null;
            // Nur nicht-leere Supabase-Werte normalisiert als updates aufbereiten.
            // Felder mit entferntem Bitrix-Mapping nicht aus Supabase-Cache füllen.
            const updates = {};
            for (const [key, value] of Object.entries(supabaseData.fields)) {
              if (!value) continue;
              // Keys mit entferntem Bitrix-Mapping werden aus dem Cache nicht gefüllt – AUSSER sie
              // stammen aus PV*SOL (Key im pvsolSet). PV*SOL-Herkunft ist vertrauenswürdig
              // (z. B. netzanschluss = kumulierte Wechselrichter-Leistung); alte Bitrix-Müllwerte
              // stehen nie im pvsolSet.
              if (REMOVED_BITRIX_DATA_KEYS.has(key) && !(pvsolSet && pvsolSet.has(String(key)))) continue;
              const cleaned = cleanImportedExposeValue(value);
              if (!cleaned) continue;
              updates[key] = normalizeImportedFieldValue(key, cleaned);
            }
            // Nur leere Felder füllen (top-level + variantData-Reparatur), nichts überschreiben.
            const enriched = fillEmptyProjectFields(project, updates);
            // fieldSources nur für Keys pflegen, die top-level tatsächlich (neu) gefüllt wurden.
            Object.keys(updates).forEach((key) => {
              if (project[key] != null && String(project[key]) !== '') return;
              const isPvsol = pvsolSet ? pvsolSet.has(String(key)) : isPvsolExposeFieldKey(key);
              fieldSources[key] = isPvsol ? 'pvsol' : 'gespeichert';
            });
            enriched._supabaseExposeData = supabaseData;
            loaded = enriched;
          } else {
            loaded = project;
          }
          loaded.__fieldSources = fieldSources;
          // Standardwerte auf verbleibende leere Felder anwenden (setzt Source 'standard').
          loaded = applyFieldDefaults(loaded, fieldDefaults);
          await shrinkProjectImages(loaded);
          loadedProjects.push(loaded);
        } catch (error) {
          console.error(error);
          failed.push({ dealId, detail: await getAxiosErrorDetail(error, 'Deal konnte nicht geladen werden.') });
        }
      }

      if (loadedProjects.length) {
        let activatedId = loadedProjects[0].id;
        setProjects((current) => {
          const result = [...current];
          for (const loaded of loadedProjects) {
            const existingIdx = result.findIndex((p) => p.dealId && p.dealId === loaded.dealId);
            if (existingIdx >= 0) {
              const existing = result[existingIdx];
              const merged = fillEmptyProjectFields(existing, loaded);
              result[existingIdx] = { ...merged, _supabaseExposeData: loaded._supabaseExposeData || existing._supabaseExposeData };
              activatedId = existing.id;
            } else {
              result.push(loaded);
            }
          }
          return result;
        });
        setSelectedIds((current) => [...new Set([...current, ...loadedProjects.map((project) => project.id)])]);
        setActiveId(activatedId);
        setDealIdInput('');
      }

      if (loadedProjects.length && failed.length) {
        toast.warning(`${loadedProjects.length} Deal${loadedProjects.length === 1 ? '' : 's'} geladen, ${failed.length} fehlgeschlagen.`);
      } else if (loadedProjects.length) {
        toast.success(`${loadedProjects.length} Deal${loadedProjects.length === 1 ? '' : 's'} aus Bitrix geladen.`);
      } else {
        toast.error(failed[0]?.detail || 'Keine Deals konnten geladen werden.');
      }
    } finally {
      setLoadingBitrixDeals(false);
    }
  };

  const handleReloadPvsol = async () => {
    if (!activeProject?.dealId) return;
    setReloadingPvsol(true);
    try {
      const extraction = await triggerPvsolExtraction(activeProject.dealId, authHeaders, { force: true });
      if (!extraction) {
        toast.error('PV*SOL-Extraktion fehlgeschlagen. Bitte später erneut versuchen.');
        return;
      }
      if (extraction.skipped) {
        if (extraction.reason === 'no_pvsol_file') {
          toast.info('Für diesen Deal ist keine PV*SOL-Datei hinterlegt.');
        } else {
          toast.warning(`PV*SOL: ${pvsolSkipLabel(extraction.reason)}`);
        }
        return;
      }
      // Erfolgreiche Extraktion: frische Supabase-Felder überschreibend übernehmen.
      const refreshed = await fetchExposeDataFromSupabase(activeProject.dealId, authHeaders);
      if (refreshed?.fields) {
        const updates = {};
        for (const [key, value] of Object.entries(refreshed.fields)) {
          if (value !== undefined && value !== null && value !== '') {
            updates[key] = normalizeImportedFieldValue(key, value);
          }
        }
        if (Object.keys(updates).length) {
          mergeActiveProjectDetailValues(activeProject.id, updates);
          // Manueller PV*SOL-Refresh: die überschriebenen Keys stammen jetzt aus PV*SOL.
          // __fieldSources wird am Top-Level geführt (mergeActiveProjectDetailValues schreibt
          // die Werte im Turnkey-Modus zwar in variantData, die Source aber hier top-level).
          const activeIsTurnkey = activeVariantKey === TURNKEY_VARIANT_KEY;
          setProjects((current) => current.map((project) => {
            if (project.id !== activeProject.id) return project;
            // Ist der aktive Modus NICHT Turnkey, das Projekt hat aber einen Turnkey-Datensatz,
            // schrieb mergeActiveProjectDetailValues nur top-level. Damit die Turnkey-Ansicht die
            // frischen PV*SOL-Werte nicht durch alte/leere variantData-Einträge maskiert, leere
            // variantData-Felder ebenfalls füllen (nur leere – vorhandene Turnkey-Werte bleiben).
            const repaired = (!activeIsTurnkey && hasTurnkeyDataset(project))
              ? fillEmptyProjectFields(project, updates)
              : project;
            const nextSources = { ...(repaired.__fieldSources || {}) };
            Object.keys(updates).forEach((key) => { nextSources[key] = 'pvsol'; });
            return { ...repaired, __fieldSources: nextSources };
          }));
        }
        toast.success('PV*SOL-Daten neu eingelesen.');
      } else {
        toast.success('PV*SOL-Extraktion abgeschlossen.');
      }
    } finally {
      setReloadingPvsol(false);
    }
  };

  const handleRawTextReparse = () => {
    if (!activeProject) return;
    const sourceProject = activeEditableProject || activeProject;
    // Locks werden pro Exposé am Top-Level geführt (__lockedFields), nicht in der Editable-Ansicht.
    const protectedKeys = getProjectProtectedKeys(activeProject);

    // Aus Bitrix geladene Projekte tragen im rawText einen JSON-Dump ({dealId, summary,
    // resolvedFields}). Diesen NICHT in den Zeilen-Parser geben (der zog Werte samt Quotes/Komma
    // aus den JSON-Zeilen), sondern die resolvedFields durch dieselbe Import-Pipeline wie beim
    // Deal-Laden schicken – dadurch werden Quotes entfernt, Pachtkosten konsistent gerechnet und
    // entfernte Bitrix-Felder (Ansprechpartner/Telefon/Netzstatus …) nicht wiederbelebt.
    const bitrixDump = parseBitrixRawTextDump(sourceProject.rawText);
    const reparsedValues = {};
    let bitrixKeys = new Set();

    if (bitrixDump) {
      const rebuilt = rebuildImportedFieldsFromResolved(bitrixDump.resolvedFields);
      Object.assign(reparsedValues, rebuilt.fields);
      bitrixKeys = rebuilt.bitrixKeys;
    } else {
      // Echter Text-/CSV-Rohtext: zeilenbasierter Parser. Die erkannten Werte laufen jetzt zentral
      // (applyStructuredPair) durch cleanImportedExposeValue, sind also bereits quote-/kommafrei.
      const fileMeta = {
        name: sourceProject.sourceFileName || `${sourceProject.projektname || 'Projekt'}.txt`,
        type: sourceProject.sourceFileType || 'text/plain',
      };
      const reparsed = createProjectFromStructuredText(sourceProject.rawText || '', cleanBaseName(fileMeta.name))
        || createProjectFromFile(fileMeta, sourceProject.rawText || '');
      DETAIL_FIELDS.forEach((field) => {
        const value = reparsed[field.key];
        if (value != null && String(value) !== '') reparsedValues[field.key] = value;
      });
    }

    // Merge-Semantik von „Neu erkennen": ein erkannter, nicht-leerer sauberer Wert überschreibt
    // (explizite Anforderung). ABER: (a) explizit gesperrte Felder bleiben unangetastet, (b) leere/
    // nicht erkannte Felder lassen den vorhandenen Wert stehen, (c) rawText/Bilder bleiben erhalten.
    const mergedValues = {};
    const nextSourceUpdates = {};
    DETAIL_FIELDS.forEach((field) => {
      const key = field.key;
      const existing = sourceProject[key] || '';
      if (protectedKeys.has(key)) {
        mergedValues[key] = existing;
        return;
      }
      const reparsedVal = reparsedValues[key];
      if (reparsedVal != null && String(reparsedVal) !== '') {
        mergedValues[key] = reparsedVal;
        // Quelle nur für echt aus Bitrix aufgelöste Keys und nur bei tatsächlicher Wertänderung
        // umsetzen; unveränderte Werte behalten ihr bestehendes Badge.
        if (bitrixKeys.has(key) && String(existing) !== String(reparsedVal)) {
          nextSourceUpdates[key] = 'bitrix';
        }
      } else {
        mergedValues[key] = existing;
      }
    });

    mergeActiveProjectDetailValues(activeProject.id, {
      ...mergedValues,
      rawText: sourceProject.rawText,
      projectImage: sourceProject.projectImage,
      lageplanImage: sourceProject.lageplanImage,
    });

    // __fieldSources wird am Top-Level geführt (mergeActiveProjectDetailValues fasst sie nicht an) –
    // separat nachziehen, damit die Herkunfts-Badges nicht verfälscht werden.
    if (Object.keys(nextSourceUpdates).length) {
      setProjects((current) => current.map((project) => (
        project.id === activeProject.id
          ? { ...project, __fieldSources: { ...(project.__fieldSources || {}), ...nextSourceUpdates } }
          : project
      )));
    }

    toast.success(bitrixDump ? 'Bitrix-Daten neu ausgewertet' : 'Rohtext neu ausgewertet');
  };

  const toggleSelection = (projectId) => {
    setSelectedIds((current) => (
      current.includes(projectId)
        ? current.filter((id) => id !== projectId)
        : [...current, projectId]
    ));
  };

  const removeProject = (projectId) => {
    setProjects((current) => current.filter((p) => p.id !== projectId));
    setSelectedIds((current) => current.filter((id) => id !== projectId));
    if (activeId === projectId) setActiveId('');
  };

  const removeSelectedProjects = async () => {
    if (!selectedIds.length) return;
    const count = selectedIds.length;
    const confirmed = await showConfirm(`${count} ausgewählte${count === 1 ? 's' : ''} Projekt${count === 1 ? '' : 'e'} wirklich löschen?`);
    if (!confirmed) return;
    const idsToRemove = new Set(selectedIds);
    setProjects((current) => current.filter((p) => !idsToRemove.has(p.id)));
    if (idsToRemove.has(activeId)) setActiveId('');
    setSelectedIds([]);
    toast.success(`${count} Projekt${count === 1 ? '' : 'e'} gelöscht`);
  };

  const toggleProjectPinned = (projectId) => {
    setProjects((current) => current.map((p) =>
      p.id === projectId ? { ...p, pinned: !p.pinned, cachedAt: p.cachedAt || new Date().toISOString() } : p
    ));
  };

  const selectAll = () => {
    setSelectedIds(projects.map((project) => project.id));
  };

  const hasCurrentProcessData = projects.length > 0;

  const showConfirm = (message) => new Promise((resolve) => {
    setDialog({ type: 'confirm', message, onConfirm: () => { setDialog(null); resolve(true); }, onCancel: () => { setDialog(null); resolve(false); } });
  });

  const showPrompt = (message, defaultValue = '') => new Promise((resolve) => {
    setDialog({ type: 'prompt', message, defaultValue, onConfirm: (val) => { setDialog(null); resolve(val); }, onCancel: () => { setDialog(null); resolve(null); } });
  });

  const showTextPrompt = (message, defaultValue = '') => new Promise((resolve) => {
    setDialog({ type: 'textarea', message, defaultValue, onConfirm: (val) => { setDialog(null); resolve(val); }, onCancel: () => { setDialog(null); resolve(null); } });
  });

  const resetCurrentProcess = ({ keepBulkText = false } = {}) => {
    removeExposeBulkDraft();
    setProjects([]);
    setSelectedIds([]);
    setActiveId('');
    if (!keepBulkText) setBulkText('');
    setDraftSavedAt('');
    setDraftMediaLimited(false);
    setSelectedDraftId('');
    setActiveSavedDraftId('');
    exportRefs.current = {};
  };

  const createNewExposeProcess = async () => {
    if (hasCurrentProcessData) {
      const confirmed = await showConfirm('Aktuellen Exposé-Vorgang verlassen und einen neuen leeren Vorgang starten? Gespeicherte Vorgänge bleiben erhalten.');
      if (!confirmed) return;
    }
    resetCurrentProcess();
    toast.success('Neuer Exposé-Vorgang angelegt');
  };

  const saveNamedDraft = async () => {
    const hasText = String(bulkText || '').trim().length > 0;
    if (!projects.length && !hasText) {
      toast.error('Keine Projektdaten oder Textdaten zum Speichern vorhanden.');
      return;
    }
    const currentDraft = savedDrafts.find((item) => item.id === activeSavedDraftId);
    const defaultName = currentDraft
      ? getSavedDraftDisplayName(currentDraft, activeVariantKey)
      : (activeProject
        ? getExposeDocumentTitle(getEditableProject(activeProject, activeVariantKey) || activeProject, activeVariantKey)
        : `Exposé-Vorgang ${new Date().toLocaleDateString('de-DE')}`);
    const name = await showPrompt('Name für diesen gespeicherten Vorgang:', defaultName);
    if (name === null) return;
    const trimmedName = String(name || '').trim();
    if (!trimmedName) {
      toast.error('Bitte einen Namen für den Vorgang angeben.');
      return;
    }

    const createDraft = (keepImages) => normalizeSavedDraftEntry({
      id: currentDraft?.id || `saved-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      name: trimmedName,
      savedAt: new Date().toISOString(),
      mediaLimited: !keepImages,
      projects: projects.map((project) => compactProjectForDraft(project, keepImages)).filter(Boolean),
      selectedIds,
      activeId,
      bulkField,
      bulkText,
      activeVariantKey,
    });

    let draft = createDraft(true);
    let nextDrafts = [draft, ...savedDrafts.filter((item) => item.id !== draft.id)].slice(0, 25);
    let persisted = persistSavedExposeDrafts(nextDrafts);

    if (!persisted) {
      draft = createDraft(false);
      nextDrafts = [draft, ...savedDrafts.filter((item) => item.id !== draft.id)].slice(0, 25);
      persisted = persistSavedExposeDrafts(nextDrafts);
    }

    if (!persisted) {
      toast.error('Vorgang konnte nicht gespeichert werden. Wahrscheinlich ist der Browser-Speicher voll.');
      return;
    }

    setSavedDrafts(nextDrafts);
    setSelectedDraftId(draft.id);
    setActiveSavedDraftId(draft.id);
    const actionLabel = currentDraft ? 'aktualisiert' : 'gespeichert';
    toast.success(draft.mediaLimited ? `Vorgang ohne Medien ${actionLabel}` : `Vorgang ${actionLabel}`);

    for (const project of projects) {
      if (!project.dealId) continue;
      const exposeFields = {};
      for (const field of EXPOSE_DETAIL_FIELDS) {
        const val = project[field.key];
        if (val != null && val !== '') exposeFields[field.key] = val;
      }
      if (Object.keys(exposeFields).length) {
        saveExposeDataToSupabase(project.dealId, exposeFields, authHeaders).catch(() => {});
      }
    }
  };

  const loadNamedDraft = (draftId) => {
    const draft = savedDrafts.find((item) => item.id === draftId);
    if (!draft) return;
    setProjects((draft.projects || []).map(stampProject));
    setSelectedIds(draft.selectedIds || []);
    setActiveId(draft.activeId || draft.projects?.[0]?.id || '');
    setBulkField(draft.bulkField || 'status');
    setBulkText(draft.bulkText || '');
    setSelectedDraftId(draft.id);
    setActiveSavedDraftId(draft.id);
    if (draft.activeVariantKey) onVariantChange?.(draft.activeVariantKey);
    toast.success(`Vorgang "${getSavedDraftDisplayName(draft, activeVariantKey)}" geladen`);
  };

  const deleteNamedDraft = async (draftId) => {
    const draft = savedDrafts.find((item) => item.id === draftId);
    const draftName = getSavedDraftDisplayName(draft, activeVariantKey);
    if (draft) {
      const confirmed = await showConfirm(`Vorgang "${draftName}" wirklich löschen?`);
      if (!confirmed) return;
    }
    const nextDrafts = savedDrafts.filter((item) => item.id !== draftId);
    if (persistSavedExposeDrafts(nextDrafts)) {
      setSavedDrafts(nextDrafts);
      if (selectedDraftId === draftId) setSelectedDraftId('');
      if (activeSavedDraftId === draftId) setActiveSavedDraftId('');
      toast.success(draft ? `"${draftName}" gelöscht` : 'Vorgang gelöscht');
    } else {
      toast.error('Vorgang konnte nicht gelöscht werden.');
    }
  };

  const copyActiveProjectToTurnkeyDataset = () => {
    if (!activeProject) return;
    setProjects((current) => current.map((project) => {
      if (project.id !== activeProject.id) return project;
      return {
        ...project,
        turnkeyDatasetEnabled: true,
        variantData: {
          ...(project.variantData || {}),
          [TURNKEY_VARIANT_KEY]: cloneProjectValuesForVariant(project, activeVariantKey),
        },
      };
    }));
    onVariantChange?.(TURNKEY_VARIANT_KEY);
    toast.success('Aktuelle Daten in eigenen Turnkey-Datensatz übernommen');
  };

  const removeTurnkeyDataset = () => {
    if (!activeProject) return;
    setProjects((current) => current.map((project) => {
      if (project.id !== activeProject.id) return project;
      const nextVariantData = { ...(project.variantData || {}) };
      delete nextVariantData[TURNKEY_VARIANT_KEY];
      return {
        ...project,
        turnkeyDatasetEnabled: false,
        variantData: nextVariantData,
      };
    }));
    toast.success('Turnkey nutzt wieder die normalen Projektdaten');
  };

  const copyActiveTurnkeyToProjectRights = () => {
    if (!activeProject) return;
    setProjects((current) => current.map((project) => {
      if (project.id !== activeProject.id) return project;
      const source = cloneProjectValuesForVariant(project, TURNKEY_VARIANT_KEY);
      const updates = {};
      DETAIL_FIELDS.forEach((field) => {
        updates[field.key] = source[field.key] ?? '';
      });
      [
        'rawText',
        'projectImage',
        'lageplanImage',
        'extractionWarning',
        'sourceFileName',
        'sourceFileType',
        '__hiddenFields',
      ].forEach((key) => {
        if (source[key] !== undefined) updates[key] = Array.isArray(source[key]) ? [...source[key]] : source[key];
      });
      return {
        ...project,
        ...updates,
      };
    }));
    onVariantChange?.('expose-projektverkauf');
    toast.success('Turnkey-Daten in Projektrechte übernommen');
  };

  const handleStructuredBulkText = (mode = 'merge', textOverride = null) => {
    const text = String((textOverride != null ? textOverride : bulkText) || '').trim();
    if (!text) {
      toast.error('Bitte zuerst vorbereitete Feld/Wert-Daten in das Textfeld einfügen.');
      return;
    }

    const parsed = parseStructuredExposeText(text);
    if (!parsed) {
      toast.error('Im Textfeld konnten keine bekannten Exposé-Felder erkannt werden.');
      return;
    }

    if (mode === 'new' || !activeProject) {
      const project = stampProject(createProjectFromStructuredText(text, parsed.values.projektname || 'Textfeld'));
      if (!project) {
        toast.error('Aus dem Text konnte kein Projekt angelegt werden.');
        return;
      }
      setProjects((current) => [...current, project]);
      setSelectedIds((current) => [...new Set([...current, project.id])]);
      setActiveId(project.id);
      toast.success(`${parsed.recognized.length} Feld${parsed.recognized.length === 1 ? '' : 'er'} erkannt und als neues Projekt angelegt`);
      return;
    }

    const sourceProject = activeEditableProject || activeProject;
    const previousRawText = String(sourceProject.rawText || '');
    const nextRawText = previousRawText && !previousRawText.includes(text)
      ? `${previousRawText}\n\n--- Ergänzter Exposé-Text ---\n${text}`
      : (previousRawText || text);
    mergeActiveProjectDetailValues(activeProject.id, {
      ...parsed.values,
      rawText: nextRawText,
      extractionWarning: parsed.unrecognized.length
        ? `${parsed.unrecognized.length} Feld${parsed.unrecognized.length === 1 ? '' : 'er'} nicht zugeordnet`
        : '',
    });
    toast.success(`${parsed.recognized.length} Feld${parsed.recognized.length === 1 ? '' : 'er'} ins aktive Projekt übernommen`);
  };

  const importProjectsFromRawText = async () => {
    const text = await showTextPrompt('Rohtext einfügen — Felder wie "Projektname: PVA 135" werden automatisch erkannt:');
    if (!text || !text.trim()) return;
    handleStructuredBulkText('new', text);
  };

  const handleTablePaste = (event, startProjectId, startFieldKey) => {
    const text = event.clipboardData?.getData('text/plain') || '';
    if (!text.includes('\t') && !text.includes('\n')) return;
    event.preventDefault();
    const rows = text.replace(/\r/g, '').split('\n').filter((row) => row.length).map((row) => row.split('\t'));
    const startProjectIndex = projects.findIndex((project) => project.id === startProjectId);
    const startFieldIndex = TABLE_FIELDS.findIndex((field) => field.key === startFieldKey);
    if (startProjectIndex < 0 || startFieldIndex < 0) return;

    setProjects((current) => current.map((project, projectIndex) => {
      const pastedRow = rows[projectIndex - startProjectIndex];
      if (!pastedRow) return project;
      const updates = {};
      pastedRow.forEach((cell, cellIndex) => {
        const field = TABLE_FIELDS[startFieldIndex + cellIndex];
        if (field) updates[field.key] = normalizeFixedUnitFieldValue(field.key, cell.trim());
      });
      return Object.keys(updates).length ? { ...project, ...updates } : project;
    }));
  };

  const handleMediaUpload = (projectId, key, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const original = String(reader.result || '');
      if (!original) return;
      const stored = await shrinkUploadedImage(original);
      setActiveProjectDetailValue(projectId, key, stored);
      const label = key === 'projectImage' ? 'Titelbild' : 'Lageplan';
      const mb = (bytes) => (bytes / 1024 / 1024).toFixed(1);
      toast.success(stored.length < original.length * 0.9
        ? `${label} übernommen (${mb(original.length)} MB → ${mb(stored.length)} MB)`
        : `${label} übernommen`);
    };
    reader.readAsDataURL(file);
  };

  // Wirtschaftlichkeit neu berechnen aus den aktuellen Projektfeldern
  // silent=true: keine Toast-Meldungen, still fehlende Felder ignorieren
  const handleRecalculate = (project, { silent = false, overrides = {} } = {}) => {
    if (!project) return;

    const economics = deriveEconomics({ ...project, ...overrides });
    if (!economics) {
      if (!silent) toast.error('Für die Berechnung werden mindestens Einspeisung, kWp und ein Preis benötigt.');
      return;
    }
    const { result, updates: derived } = economics;

    // Bei silent: nur abgeleitete Kennzahlen aktualisieren, Preis-Eingaben nicht ueberschreiben.
    // opex wird bewusst NICHT mehr geschrieben/als 'manuell' markiert — es ist ein
    // abgeleiteter Wert (Versicherung + Wartung) und soll den Komponenten weiter folgen.
    const updates = silent ? derived : {
      ...derived,
      turnkey_kaufpreis: String(Math.round(result.turnkey_kaufpreis)),
      anlagenpreis: String(Math.round(result.anlagenpreis)),
      turnkey_preis_kwp: String(Math.round(result.turnkey_preis_kwp * 100) / 100),
      betrachtungszeitraum: String(result.betrachtungszeitraum),
    };

    setProjects((current) => current.map((proj) => {
      if (proj.id !== project.id) return proj;
      const next = { ...proj, ...overrides };
      const nextSources = { ...(proj.__fieldSources || {}) };
      Object.entries(updates).forEach(([key, value]) => {
        next[key] = value;
        nextSources[key] = 'manuell';
      });
      next.__fieldSources = nextSources;
      // Auch in allen variantData-Varianten überschreiben, sonst maskieren
      // alte Varianten-Werte die frisch berechneten top-level-Werte.
      return overwriteFieldsInAllVariants(next, updates);
    }));

    if (!silent) toast.success('Wirtschaftlichkeit berechnet');
  };

  // Auto-Neuberechnung bei Aenderungen im Investition-Panel
  const autoRecalc = (fieldOverrides) => {
    if (!activeEditableProject) return;
    handleRecalculate(activeEditableProject, { silent: true, overrides: fieldOverrides });
  };

  const copyTable = async () => {
    try {
      await navigator.clipboard.writeText(formatTsv(projects, activeVariantKey));
      toast.success('Tabelle in die Zwischenablage kopiert');
    } catch (error) {
      toast.error('Kopieren nicht möglich');
    }
  };

  const getExportHtml = (project) => {
    const node = exportRefs.current[project.id];
    if (!node) return '';
    return buildStandaloneHtml(project, node.innerHTML, activeVariantKey);
  };

  const getExportFileBaseName = (project) => {
    const computedProject = getEditableProject(project, activeVariantKey) || project || {};
    return sanitizeFileName(getExposeDocumentTitle(computedProject, activeVariantKey));
  };

  const downloadActiveHtml = () => {
    if (!activeProject) return;
    const html = getExportHtml(activeProject);
    downloadBlob(new Blob([html], { type: 'text/html;charset=utf-8' }), `${getExportFileBaseName(activeProject)}.html`);
  };

  const downloadActivePdf = async () => {
    if (!activeProject || exporting) return;
    const html = getExportHtml(activeProject);
    if (!html) {
      toast.error('PDF konnte nicht vorbereitet werden.');
      return;
    }
    setExporting(true);
    try {
      const filename = `${getExportFileBaseName(activeProject)}.pdf`;
      const pdfBlob = await requestExposePdfBlob(apiBase, authHeaders, html, filename);
      downloadBlob(pdfBlob, filename);
    } catch (error) {
      console.error(error);
      toast.error(await getAxiosErrorDetail(error, 'PDF konnte nicht erstellt werden.'));
    } finally {
      setExporting(false);
    }
  };

  const downloadZipHtml = async () => {
    if (!projects.length) return;
    setExporting(true);
    await new Promise((resolve) => window.setTimeout(resolve, 50));
    try {
      const entries = projects.map((project) => ({
        name: `${getExportFileBaseName(project)}.html`,
        content: getExportHtml(project),
      }));
      entries.push({ name: 'Projektdaten.tsv', content: formatTsv(projects, activeVariantKey) });
      const zip = buildZipBlob(entries);
      downloadBlob(zip, `ENPOWER_Exposes_HTML_${new Date().toISOString().slice(0, 10)}.zip`);
    } finally {
      setExporting(false);
    }
  };

  const downloadZipPdf = async () => {
    if (!projects.length || exporting) return;
    setExporting(true);
    try {
      await new Promise((resolve) => window.setTimeout(resolve, 50));
      const entries = [];
      for (const project of projects) {
        const filename = `${getExportFileBaseName(project)}.pdf`;
        const html = getExportHtml(project);
        if (!html) continue;
        const pdfBlob = await requestExposePdfBlob(apiBase, authHeaders, html, filename);
        entries.push({
          name: filename,
          bytes: new Uint8Array(await pdfBlob.arrayBuffer()),
        });
      }
      entries.push({ name: 'Projektdaten.tsv', content: formatTsv(projects, activeVariantKey) });
      const zip = buildZipBlob(entries);
      downloadBlob(zip, `ENPOWER_Exposes_PDF_${new Date().toISOString().slice(0, 10)}.zip`);
    } catch (error) {
      console.error(error);
      toast.error(await getAxiosErrorDetail(error, 'PDF-ZIP konnte nicht erstellt werden.'));
    } finally {
      setExporting(false);
    }
  };

  const printPreview = () => {
    if (!activeProject) return;
    const html = getExportHtml(activeProject).replace('</body>', '<script>window.addEventListener("load", function(){ setTimeout(function(){ window.print(); }, 250); });</script></body>');
    const printWindow = window.open('', '_blank', 'noopener,noreferrer');
    if (!printWindow) {
      downloadActiveHtml();
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const openActiveProjectInTemplateEditor = () => {
    if (!activeProject || typeof onOpenInTemplateEditor !== 'function') return;
    onOpenInTemplateEditor({
      variantKey: activeVariantKey,
      project: activeEditableProject || activeProject,
      data: activeData,
    });
  };

  const assistantStatusChip = (status, label) => {
    const palette = {
      ok: { bg: '#EAFBF1', fg: '#15803D', border: '#B7F0C9' },
      warn: { bg: '#FFF7ED', fg: '#C15A00', border: '#FED7AA' },
      bad: { bg: '#FEF2F2', fg: '#B91C1C', border: '#FECACA' },
      empty: { bg: '#F8FAFC', fg: '#64748B', border: '#E2E8F0' },
    }[status] || { bg: '#F8FAFC', fg: '#64748B', border: '#E2E8F0' };
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 24,
        padding: '4px 9px',
        borderRadius: 999,
        border: `1px solid ${palette.border}`,
        background: palette.bg,
        color: palette.fg,
        fontSize: 11,
        fontWeight: 900,
        whiteSpace: 'nowrap',
      }}>
        {label}
      </span>
    );
  };

  const assistantPanelStyle = {
    border: '1px solid #D8E1EC',
    borderRadius: 8,
    background: '#FFFFFF',
    boxShadow: '0 1px 0 rgba(15, 23, 42, 0.02)',
  };

  const assistantMutedTextStyle = {
    margin: 0,
    color: '#64748B',
    fontSize: 12,
    lineHeight: 1.45,
  };

  // Rendert ein Herkunfts-Chip für ein Feld (liest project.__fieldSources[fieldKey]).
  // Gibt null zurück, wenn der Toggle aus ist oder keine Quelle bekannt ist.
  const renderFieldSourceBadge = (project, fieldKey) => {
    if (!showFieldSources) return null;
    const source = project?.__fieldSources?.[fieldKey];
    const config = source ? FIELD_SOURCE_BADGES[source] : null;
    if (!config) return null;
    return (
      <span
        title={config.title}
        style={{ padding: '1px 6px', borderRadius: 4, background: config.bg, color: config.color, fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap' }}
      >
        {config.label}
      </span>
    );
  };

  const renderAssistantField = (field, { compact = false } = {}) => {
    const value = activeEditableProject?.[field.key] || '';
    const filled = isExposeFieldFilled(activeEditableProject, field.key);
    const required = ASSISTANT_REQUIRED_FIELD_KEYS.has(field.key);
    const hidden = isProjectFieldHidden(activeEditableProject, field.key);
    const isMediaField = field.key === 'projectImage' || field.key === 'lageplanImage';
    const colorField = isColorField(field.key);
    const colorValue = String(value || '');
    const normalizedColor = normalizeHexColor(colorValue) || '#122A5C';
    const isCalc = CALCULATED_FIELD_KEYS.has(field.key);
    const sourceLabel = filled ? (isCalc ? 'berechnet' : 'gefüllt') : required ? 'fehlt' : 'optional';
    const sourceStatus = filled ? 'ok' : required ? 'bad' : 'warn';
    const disabled = !activeProject;

    return (
      <div
        key={field.key}
        style={{
          border: `1px solid ${required && !filled ? '#FED7AA' : '#E2E8F0'}`,
          borderRadius: 8,
          background: required && !filled ? '#FFFBF5' : '#FFFFFF',
          padding: compact ? 10 : 12,
          display: 'grid',
          gap: 8,
          minWidth: 0,
          opacity: hidden ? 0.62 : 1,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
          <label htmlFor={`assistant-${activeProject?.id || 'empty'}-${field.key}`} style={{ color: '#122A5C', fontSize: 12, fontWeight: 900 }}>
            {field.label}{required ? ' *' : ''}
          </label>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {renderFieldSourceBadge(activeEditableProject, field.key)}
            {isCalc && filled ? <span style={{ padding: '2px 6px', borderRadius: 4, background: '#DBEAFE', color: '#1D4ED8', fontSize: 9, fontWeight: 900 }}>auto</span> : null}
            {assistantStatusChip(sourceStatus, sourceLabel)}
            {activeProject && <button type="button" title={(Array.isArray(activeProject.__lockedFields) && activeProject.__lockedFields.includes(field.key)) ? 'Geschützt (Klick zum Entsperren)' : 'Vor Überschreiben schützen'} onClick={() => toggleFieldLock(activeProject.id, field.key)} style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', fontSize: 11, lineHeight: 1, opacity: (Array.isArray(activeProject.__lockedFields) && activeProject.__lockedFields.includes(field.key)) ? 1 : 0.3 }}>{(Array.isArray(activeProject.__lockedFields) && activeProject.__lockedFields.includes(field.key)) ? '\u{1F512}' : '\u{1F513}'}</button>}
          </div>
        </div>
        {isMediaField ? (
          <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : '120px 1fr', gap: 10, alignItems: 'center' }}>
            <div style={{ height: 74, borderRadius: 8, border: '1px solid #D8E1EC', background: '#F8FAFC', overflow: 'hidden', display: 'grid', placeItems: 'center', color: '#94A3B8', fontSize: 11, fontWeight: 700 }}>
              {value ? <img src={value} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (field.key === 'projectImage' ? 'Titelbild offen' : 'Lageplan offen')}
            </div>
            <label style={{ ...buttonStyle, justifySelf: 'stretch', opacity: disabled ? 0.45 : 1 }}>
              {field.key === 'projectImage' ? <Image size={14} /> : <MapIcon size={14} />}
              {field.key === 'projectImage' ? 'Titelbild wählen' : 'Lageplan wählen'}
              <input
                type="file"
                accept="image/*"
                disabled={disabled}
                onChange={(event) => handleMediaUpload(activeProject.id, field.key, event.target.files?.[0])}
                style={{ display: 'none' }}
              />
            </label>
          </div>
        ) : colorField ? (
          <div style={{ display: 'grid', gridTemplateColumns: '38px minmax(0, 1fr)', gap: 8, alignItems: 'center' }}>
            <input
              type="color"
              aria-label={field.label}
              disabled={disabled}
              value={normalizedColor}
              onChange={(event) => setActiveProjectDetailValue(activeProject.id, field.key, event.target.value)}
              style={{ width: 38, height: 36, padding: 2, border: '1px solid #CBD5E1', borderRadius: 7, background: '#FFFFFF', cursor: disabled ? 'not-allowed' : 'pointer' }}
            />
            <input
              id={`assistant-${activeProject?.id || 'empty'}-${field.key}`}
              value={colorValue}
              disabled={disabled}
              placeholder="#122A5C"
              onChange={(event) => setActiveProjectDetailValue(activeProject.id, field.key, event.target.value)}
              style={inputStyle}
            />
          </div>
        ) : STATUS_FIELD_OPTIONS[field.key] ? (
          <select
            id={`assistant-${activeProject?.id || 'empty'}-${field.key}`}
            value={value || ''}
            disabled={disabled}
            onChange={(event) => setActiveProjectDetailValue(activeProject.id, field.key, event.target.value)}
            style={{ ...inputStyle, background: '#FFFFFF', cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            <option value="">— nicht angegeben —</option>
            {STATUS_FIELD_OPTIONS[field.key].map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
            {value && !STATUS_FIELD_OPTIONS[field.key].includes(value) ? (
              <option value={value}>{value}</option>
            ) : null}
          </select>
        ) : (
          <UnitAwareInput
            id={`assistant-${activeProject?.id || 'empty'}-${field.key}`}
            fieldKey={field.key}
            value={value}
            disabled={disabled}
            placeholder={required ? 'Pflichtwert ergänzen' : 'Optional ergänzen'}
            onChange={(nextValue) => setActiveProjectDetailValue(activeProject.id, field.key, nextValue)}
            style={inputStyle}
          />
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: hidden ? '#94A3B8' : '#122A5C', fontSize: 10, fontWeight: 700 }}>
            <input type="checkbox" checked={!hidden} disabled={disabled} onChange={(event) => setProjectFieldHidden(activeProject.id, field.key, !event.target.checked)} />
            {hidden ? 'ausgeblendet' : 'sichtbar'}
          </label>
        </div>
      </div>
    );
  };

  // Render-Helfer fuer das Investition-Panel mit Preiseingabe-Modus
  const renderInvestitionPanel = (group) => {
    const { report } = group;
    const label = report.status === 'ok'
      ? 'vollstaendig'
      : `${report.filled}/${report.total}`;
    const disabled = !activeProject;
    const project = activeEditableProject || {};
    const modus = project.preiseingabe_modus || 'gesamt';
    const bare = (key) => {
      const cfg = fixedUnitConfigForField(key);
      return cfg ? stripFixedUnit(project[key], cfg) : String(project[key] ?? '');
    };

    const pachtartValue = (project.pachtart || '').replace(/[,;]+$/, '').replace(/^["'«»""„‟]+|["'«»""„‟]+$/g, '').trim();
    const pachtartLower = pachtartValue.toLowerCase();
    const pachtartNormalized = pachtartLower === 'ez' || pachtartLower.startsWith('einmal') ? 'einmal'
      : pachtartLower.startsWith('jaehr') || pachtartLower.startsWith('jährl') ? 'jaehrlich'
      : pachtartLower.includes('sanierung') ? 'sanierung'
        : pachtartValue;

    const turnkeyRaw = parseCalculationNumber(project.turnkey_kaufpreis || '') || 0;
    const anlagenpreisRaw = parseCalculationNumber(project.anlagenpreis || '') || 0;
    let pachtRaw = parseCalculationNumber(project.pachtkosten || '') || 0;
    const opexRaw = parseCalculationNumber(project.opex || '') || 0;
    const versicherungRaw = parseCalculationNumber(project.versicherung || '') || 0;
    const wartungRaw = parseCalculationNumber(project.wartung || '') || 0;
    const opexManualOverride = project.__fieldSources?.opex === 'manuell' && String(project.opex || '').trim() !== '';
    const kwpRaw = parseCalculationNumber(project.kwp_leistung || project.pvsol_generatorleistung || '') || 0;
    // Pachtkosten aus Einzelfeldern befuellen wenn leer
    if (!pachtRaw) {
      if (pachtartNormalized === 'einmal') {
        pachtRaw = parseCalculationNumber(project.pacht_einmal_gesamt || '') || ((parseCalculationNumber(project.pacht_einmal_kwp || '') || 0) * kwpRaw);
      } else if (pachtartNormalized === 'jaehrlich') {
        pachtRaw = parseCalculationNumber(project.jaehrliche_zahlung || '') || 0;
      }
    }
    const pachtEinmal = pachtartNormalized === 'einmal' ? pachtRaw : 0;

    // Berechnete Anzeige
    const displayAnlagenpreis = modus === 'gesamt' ? (turnkeyRaw - pachtEinmal) : anlagenpreisRaw;
    const displayGesamt = modus === 'anlage' ? (anlagenpreisRaw + pachtEinmal) : turnkeyRaw;
    const displayProKwp = kwpRaw > 0 ? Math.round((displayGesamt / kwpRaw) * 100) / 100 : 0;

    const pillStyle = (active) => ({
      padding: '5px 12px',
      border: 'none',
      borderRadius: 5,
      background: active ? '#122A5C' : '#F1F5F9',
      color: active ? '#FFFFFF' : '#475569',
      fontSize: 11,
      fontWeight: 700,
      cursor: disabled ? 'not-allowed' : 'pointer',
    });

    // Betriebskosten-Komponente (Versicherung / Wartung & Betriebsführung) — gleiches
    // Gesamt/pro-kWp-Umschalter-Muster wie die übrigen Panel-Felder. Schreibt in
    // fieldKey (versicherung/wartung); opex wird als Summe automatisch nachgeführt
    // (deriveEconomics/applyAutoCalculations), außer opex ist manuell überschrieben.
    const renderOpexKomponente = (fieldKey, label) => {
      const modeKey = `${fieldKey}_eingabe_modus`;
      const perKwpKey = `${fieldKey}_kwp`;
      const mode = project[modeKey] || 'kwp';
      const compRaw = parseCalculationNumber(project[fieldKey] || '') || 0;
      return (
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label style={{ color: '#122A5C', fontSize: 12, fontWeight: 900 }}>{label}</label>
            <div style={{ display: 'flex', gap: 3 }}>
              <button type="button" disabled={disabled} onClick={() => !disabled && setActiveProjectDetailValue(activeProject.id, modeKey, 'gesamt')} style={pillStyle(mode === 'gesamt')}>Gesamt</button>
              <button type="button" disabled={disabled} onClick={() => !disabled && setActiveProjectDetailValue(activeProject.id, modeKey, 'kwp')} style={pillStyle(mode === 'kwp')}>pro kWp</button>
            </div>
          </div>
          {mode === 'kwp' ? (
            <>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  value={kwpRaw > 0 && compRaw > 0 ? String(Math.round((compRaw / kwpRaw) * 100) / 100) : (bare(perKwpKey) || '')}
                  disabled={disabled}
                  placeholder={`${label} pro kWp`}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setActiveProjectDetailValue(activeProject.id, perKwpKey, raw);
                    const perKwp = parseCalculationNumber(raw);
                    if (perKwp !== null && kwpRaw > 0) {
                      const total = String(Math.round(perKwp * kwpRaw));
                      setActiveProjectDetailValue(activeProject.id, fieldKey, total);
                      autoRecalc({ [fieldKey]: total });
                    }
                  }}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <span style={{ color: '#64748B', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>EUR/kWp p.a.</span>
              </div>
              {kwpRaw > 0 && compRaw > 0 && (
                <p style={{ margin: 0, color: '#475569', fontSize: 11 }}>
                  = {compRaw.toLocaleString('de-DE')} EUR p.a. ({kwpRaw.toLocaleString('de-DE')} kWp)
                </p>
              )}
            </>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  value={bare(fieldKey)}
                  disabled={disabled}
                  placeholder={`${label} gesamt`}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setActiveProjectDetailValue(activeProject.id, fieldKey, raw);
                    const total = parseCalculationNumber(raw);
                    if (total !== null && kwpRaw > 0) {
                      setActiveProjectDetailValue(activeProject.id, perKwpKey, String(Math.round((total / kwpRaw) * 100) / 100));
                    }
                    autoRecalc({ [fieldKey]: raw });
                  }}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <span style={{ color: '#64748B', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>EUR p.a.</span>
              </div>
              {kwpRaw > 0 && compRaw > 0 && (
                <p style={{ margin: 0, color: '#475569', fontSize: 11 }}>
                  = {(Math.round((compRaw / kwpRaw) * 100) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2 })} EUR/kWp p.a.
                </p>
              )}
            </>
          )}
        </div>
      );
    };

    return (
      <div key={group.key} style={{ ...assistantPanelStyle, overflow: 'hidden' }}>
        <div style={{
          padding: '13px 14px',
          borderBottom: '1px solid #E2E8F0',
          background: '#F8FAFC',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'center',
        }}>
          <div>
            <h3 style={{ margin: 0, color: '#122A5C', fontSize: 15, fontFamily: 'Outfit, sans-serif', fontWeight: 900 }}>
              {group.title}
            </h3>
            <p style={{ ...assistantMutedTextStyle, marginTop: 2 }}>{group.description}</p>
          </div>
          {assistantStatusChip(report.status, label)}
        </div>
        <div style={{ padding: 14, display: 'grid', gap: 14 }}>
          {/* Anlagenpreis — Gesamt oder pro kWp */}
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ color: '#122A5C', fontSize: 12, fontWeight: 900 }}>Anlagenpreis (ohne Pacht)</label>
              <div style={{ display: 'flex', gap: 3 }}>
                <button type="button" disabled={disabled} onClick={() => !disabled && setActiveProjectDetailValue(activeProject.id, 'preiseingabe_modus', 'gesamt')} style={pillStyle(modus === 'gesamt')}>Gesamt</button>
                <button type="button" disabled={disabled} onClick={() => !disabled && setActiveProjectDetailValue(activeProject.id, 'preiseingabe_modus', 'kwp')} style={pillStyle(modus === 'kwp')}>pro kWp</button>
              </div>
            </div>
            {modus === 'kwp' ? (
              <>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    value={bare('turnkey_preis_kwp')}
                    disabled={disabled}
                    placeholder="Preis pro kWp"
                    onChange={(e) => {
                      const raw = e.target.value;
                      setActiveProjectDetailValue(activeProject.id, 'turnkey_preis_kwp', raw);
                      const perKwp = parseCalculationNumber(raw);
                      if (perKwp !== null && kwpRaw > 0) {
                        const total = Math.round(perKwp * kwpRaw);
                        setActiveProjectDetailValue(activeProject.id, 'anlagenpreis', String(total));
                        setActiveProjectDetailValue(activeProject.id, 'turnkey_kaufpreis', String(total + Math.round(pachtEinmal)));
                        autoRecalc({ anlagenpreis: String(total), turnkey_kaufpreis: String(total + Math.round(pachtEinmal)) });
                      }
                    }}
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <span style={{ color: '#64748B', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>EUR/kWp</span>
                </div>
                <p style={{ margin: 0, color: '#122A5C', fontSize: 12, fontWeight: 700 }}>
                  = Anlagenpreis: {anlagenpreisRaw > 0 ? anlagenpreisRaw.toLocaleString('de-DE') : '---'} EUR
                  {kwpRaw > 0 ? ` (${kwpRaw.toLocaleString('de-DE')} kWp)` : ''}
                </p>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    value={bare('anlagenpreis')}
                    disabled={disabled}
                    placeholder="Anlagenpreis gesamt"
                    onChange={(e) => {
                      const raw = e.target.value;
                      setActiveProjectDetailValue(activeProject.id, 'anlagenpreis', raw);
                      const total = parseCalculationNumber(raw);
                      if (total !== null) {
                        setActiveProjectDetailValue(activeProject.id, 'turnkey_kaufpreis', String(total + Math.round(pachtEinmal)));
                        if (kwpRaw > 0) setActiveProjectDetailValue(activeProject.id, 'turnkey_preis_kwp', String(Math.round((total / kwpRaw) * 100) / 100));
                        autoRecalc({ anlagenpreis: raw, turnkey_kaufpreis: String(total + Math.round(pachtEinmal)) });
                      }
                    }}
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <span style={{ color: '#64748B', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>EUR</span>
                </div>
                {kwpRaw > 0 && anlagenpreisRaw > 0 && (
                  <p style={{ margin: 0, color: '#475569', fontSize: 11 }}>
                    = {(Math.round((anlagenpreisRaw / kwpRaw) * 100) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2 })} EUR/kWp
                  </p>
                )}
              </>
            )}
          </div>

          {/* Separator */}
          <div style={{ borderTop: '1px solid #E2E8F0' }} />

          {/* Pachtart */}
          <div style={{ display: 'grid', gap: 4 }}>
            <label style={{ color: '#122A5C', fontSize: 12, fontWeight: 900 }}>Pachtart</label>
            <select
              value={pachtartNormalized}
              disabled={disabled}
              onChange={(e) => { setActiveProjectDetailValue(activeProject.id, 'pachtart', e.target.value); autoRecalc({ pachtart: e.target.value }); }}
              style={{ ...inputStyle, background: '#FFFFFF', cursor: disabled ? 'not-allowed' : 'pointer' }}
            >
              <option value="">-- nicht angegeben --</option>
              <option value="einmal">Einmalzahlung</option>
              <option value="jaehrlich">Jährlich</option>
            </select>
          </div>

          {/* Pachtkosten — Gesamt oder pro kWp */}
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ color: '#122A5C', fontSize: 12, fontWeight: 900 }}>
                {pachtartNormalized === 'jaehrlich' ? 'Pachtkosten (jährlich)' : pachtartNormalized === 'einmal' ? 'Pachtkosten (einmalig)' : 'Pachtkosten'}
              </label>
              <div style={{ display: 'flex', gap: 3 }}>
                <button type="button" disabled={disabled} onClick={() => !disabled && setActiveProjectDetailValue(activeProject.id, 'pacht_eingabe_modus', 'gesamt')} style={pillStyle((project.pacht_eingabe_modus || 'gesamt') === 'gesamt')}>Gesamt</button>
                <button type="button" disabled={disabled} onClick={() => !disabled && setActiveProjectDetailValue(activeProject.id, 'pacht_eingabe_modus', 'kwp')} style={pillStyle((project.pacht_eingabe_modus || 'gesamt') === 'kwp')}>pro kWp</button>
              </div>
            </div>
            {(project.pacht_eingabe_modus || 'gesamt') === 'kwp' ? (
              <>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    value={bare('pacht_einmal_kwp') || (kwpRaw > 0 && pachtRaw > 0 ? String(Math.round((pachtRaw / kwpRaw) * 100) / 100) : '')}
                    disabled={disabled}
                    placeholder="Pacht pro kWp"
                    onChange={(e) => {
                      const raw = e.target.value;
                      setActiveProjectDetailValue(activeProject.id, 'pacht_einmal_kwp', raw);
                      const perKwp = parseCalculationNumber(raw);
                      if (perKwp !== null && kwpRaw > 0) {
                        const total = Math.round(perKwp * kwpRaw);
                        setActiveProjectDetailValue(activeProject.id, 'pachtkosten', String(total));
                        autoRecalc({ pachtkosten: String(total) });
                      }
                    }}
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <span style={{ color: '#64748B', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>EUR/kWp</span>
                </div>
                {kwpRaw > 0 && pachtRaw > 0 && (
                  <p style={{ margin: 0, color: '#475569', fontSize: 11 }}>
                    = {pachtRaw.toLocaleString('de-DE')} EUR {pachtartNormalized === 'jaehrlich' ? 'p.a.' : 'gesamt'} ({kwpRaw.toLocaleString('de-DE')} kWp)
                  </p>
                )}
              </>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    value={bare('pachtkosten')}
                    disabled={disabled}
                    placeholder="Pachtkosten gesamt"
                    onChange={(e) => {
                      const raw = e.target.value;
                      setActiveProjectDetailValue(activeProject.id, 'pachtkosten', raw);
                      const total = parseCalculationNumber(raw);
                      if (total !== null && kwpRaw > 0) {
                        setActiveProjectDetailValue(activeProject.id, 'pacht_einmal_kwp', String(Math.round((total / kwpRaw) * 100) / 100));
                      }
                      autoRecalc({ pachtkosten: raw });
                    }}
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <span style={{ color: '#64748B', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>EUR{pachtartNormalized === 'jaehrlich' ? ' p.a.' : ''}</span>
                </div>
                {kwpRaw > 0 && pachtRaw > 0 && (
                  <p style={{ margin: 0, color: '#475569', fontSize: 11 }}>
                    = {(Math.round((pachtRaw / kwpRaw) * 100) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2 })} EUR/kWp{pachtartNormalized === 'jaehrlich' ? ' p.a.' : ''}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Gesamtpreis Turnkey — editierbar */}
          <div style={{ display: 'grid', gap: 4 }}>
            <label style={{ color: '#122A5C', fontSize: 12, fontWeight: 900 }}>Gesamtpreis Turnkey</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                value={String((anlagenpreisRaw + pachtEinmal) || bare('turnkey_kaufpreis') || '')}
                disabled={disabled}
                placeholder="Gesamtpreis"
                onChange={(e) => {
                  const raw = e.target.value;
                  const gesamt = parseCalculationNumber(raw);
                  setActiveProjectDetailValue(activeProject.id, 'turnkey_kaufpreis', raw);
                  if (gesamt !== null) {
                    const anlage = Math.max(0, gesamt - pachtEinmal);
                    setActiveProjectDetailValue(activeProject.id, 'anlagenpreis', String(anlage));
                    if (kwpRaw > 0) setActiveProjectDetailValue(activeProject.id, 'turnkey_preis_kwp', String(Math.round((gesamt / kwpRaw) * 100) / 100));
                    autoRecalc({ anlagenpreis: String(anlage), turnkey_kaufpreis: raw });
                  }
                }}
                style={{ ...inputStyle, flex: 1 }}
              />
              <span style={{ color: '#64748B', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>EUR</span>
            </div>
            {kwpRaw > 0 && (anlagenpreisRaw + pachtEinmal) > 0 && (
              <p style={{ margin: 0, color: '#475569', fontSize: 11 }}>
                = {((anlagenpreisRaw + pachtEinmal) / kwpRaw).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR/kWp
              </p>
            )}
          </div>

          {/* Zusammenfassung */}
          <div style={{ background: '#F1F5F9', borderRadius: 6, padding: '8px 10px', display: 'grid', gap: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#475569' }}>
              <span>Anlagenpreis</span>
              <span>{anlagenpreisRaw > 0 ? `${anlagenpreisRaw.toLocaleString('de-DE')} EUR` : '---'}</span>
            </div>
            {pachtEinmal > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#475569' }}>
                <span>+ Pacht (einmalig)</span>
                <span>{pachtEinmal.toLocaleString('de-DE')} EUR</span>
              </div>
            )}
            {pachtartNormalized === 'jaehrlich' && pachtRaw > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94A3B8', fontStyle: 'italic' }}>
                <span>Pacht (jährlich, nicht im Turnkey)</span>
                <span>{pachtRaw.toLocaleString('de-DE')} EUR p.a.</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#122A5C', fontWeight: 900, borderTop: '1px solid #CBD5E1', paddingTop: 4, marginTop: 2 }}>
              <span>= Gesamtpreis Turnkey</span>
              <span>{(anlagenpreisRaw + pachtEinmal) > 0 ? `${(anlagenpreisRaw + pachtEinmal).toLocaleString('de-DE')} EUR` : '---'}</span>
            </div>
          </div>

          {/* Separator */}
          <div style={{ borderTop: '1px solid #E2E8F0' }} />

          {/* Betriebskosten (OPEX) — aufgeteilt in Versicherung + Wartung & Betriebsführung */}
          {renderOpexKomponente('versicherung', 'Versicherung')}
          {renderOpexKomponente('wartung', 'Wartung & Betriebsführung')}

          {/* Summenzeile Betriebskosten */}
          {(() => {
            const pachtJaehrlich = pachtartNormalized === 'jaehrlich' ? pachtRaw : 0;
            const betriebskostenGesamt = opexRaw + pachtJaehrlich;
            return (
              <div style={{ background: '#F1F5F9', borderRadius: 6, padding: '8px 10px', display: 'grid', gap: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#475569' }}>
                  <span>Versicherung</span>
                  <span>{versicherungRaw > 0 ? `${versicherungRaw.toLocaleString('de-DE')} EUR p.a.` : '---'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#475569' }}>
                  <span>+ Wartung & Betriebsführung</span>
                  <span>{wartungRaw > 0 ? `${wartungRaw.toLocaleString('de-DE')} EUR p.a.` : '---'}</span>
                </div>
                {pachtJaehrlich > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#475569' }}>
                    <span>+ Pacht (jährlich)</span>
                    <span>{pachtJaehrlich.toLocaleString('de-DE')} EUR p.a.</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#122A5C', fontWeight: 900, borderTop: '1px solid #CBD5E1', paddingTop: 4, marginTop: 2 }}>
                  <span>Betriebskosten gesamt</span>
                  <span>
                    {betriebskostenGesamt > 0
                      ? `${betriebskostenGesamt.toLocaleString('de-DE')} EUR p.a.${kwpRaw > 0 ? ` (= ${(Math.round((betriebskostenGesamt / kwpRaw) * 100) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2 })} EUR/kWp)` : ''}`
                      : '---'}
                  </span>
                </div>
                {opexManualOverride && (
                  <div style={{ fontSize: 11, color: '#B45309', fontWeight: 700 }}>
                    OPEX manuell überschrieben
                  </div>
                )}
              </div>
            );
          })()}

          {/* Betrachtungszeitraum */}
          <div style={{ display: 'grid', gap: 4 }}>
            <label style={{ color: '#122A5C', fontSize: 12, fontWeight: 900 }}>Betrachtungszeitraum</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                value={String(project.betrachtungszeitraum || '20').replace(/\s*Jahre\s*/gi, '')}
                disabled={disabled}
                placeholder="20"
                type="number"
                min="1"
                max="40"
                onChange={(e) => { setActiveProjectDetailValue(activeProject.id, 'betrachtungszeitraum', e.target.value); autoRecalc({ betrachtungszeitraum: e.target.value }); }}
                style={{ ...inputStyle, flex: 1, maxWidth: 80 }}
              />
              <span style={{ color: '#64748B', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>Jahre</span>
            </div>
          </div>

          {/* Degradation */}
          <div style={{ display: 'grid', gap: 4 }}>
            <label style={{ color: '#122A5C', fontSize: 12, fontWeight: 900 }}>Degradation</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                value={String(project.degradation || '0,5')}
                disabled={disabled}
                placeholder="0,5"
                inputMode="decimal"
                onChange={(e) => { setActiveProjectDetailValue(activeProject.id, 'degradation', e.target.value); autoRecalc({ degradation: e.target.value }); }}
                style={{ ...inputStyle, flex: 1, maxWidth: 80 }}
              />
              <span style={{ color: '#64748B', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>% p.a.</span>
            </div>
          </div>

          {/* Neu berechnen Button */}
          <button
            type="button"
            disabled={disabled}
            onClick={() => handleRecalculate(activeEditableProject)}
            style={{
              minHeight: 36,
              padding: '9px 18px',
              border: 'none',
              borderRadius: 8,
              background: disabled ? '#94A3B8' : '#122A5C',
              color: '#FFFFFF',
              fontSize: 13,
              fontWeight: 900,
              fontFamily: 'Outfit, sans-serif',
              cursor: disabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <RefreshCw size={14} />
            Neu berechnen
          </button>
        </div>
      </div>
    );
  };

  const renderAssistantGroup = (group) => {
    // Investition-Gruppe bekommt ein eigenes Panel
    if (group.key === 'investition' && activeProject) {
      return renderInvestitionPanel(group);
    }

    const { report } = group;
    const visibleFields = report.fields.filter((field) => (
      assistantFieldFilter === 'all' || !isExposeFieldFilled(activeEditableProject, field.key)
    ));
    const label = report.status === 'ok'
      ? 'vollstaendig'
      : report.status === 'bad'
        ? `${report.requiredMissing.length} Pflichtfeld${report.requiredMissing.length === 1 ? '' : 'er'}`
        : `${report.filled}/${report.total}`;
    return (
      <div key={group.key} style={{ ...assistantPanelStyle, overflow: 'hidden' }}>
        <div style={{
          padding: '13px 14px',
          borderBottom: '1px solid #E2E8F0',
          background: '#F8FAFC',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'center',
        }}>
          <div>
            <h3 style={{ margin: 0, color: '#122A5C', fontSize: 15, fontFamily: 'Outfit, sans-serif', fontWeight: 900 }}>
              {group.title}
            </h3>
            <p style={{ ...assistantMutedTextStyle, marginTop: 2 }}>{group.description}</p>
          </div>
          {assistantStatusChip(report.status, label)}
        </div>
        <div style={{ padding: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 10 }}>
          {visibleFields.length ? visibleFields.map((field) => renderAssistantField(field)) : (
            <div style={{ gridColumn: '1 / -1', border: '1px dashed #C7D2E1', borderRadius: 8, padding: 14, color: '#15803D', background: '#F7FEFA', fontSize: 12, fontWeight: 700 }}>
              Alle Felder dieser Gruppe sind ausgefuellt.
            </div>
          )}
        </div>
      </div>
    );
  };

  const tabStyle = (key) => ({
    minHeight: 38,
    padding: '9px 18px',
    border: 'none',
    borderBottom: studioTab === key ? '2.5px solid #F28A0D' : '2.5px solid transparent',
    background: 'transparent',
    color: studioTab === key ? '#122A5C' : '#64748B',
    fontSize: 13,
    fontWeight: studioTab === key ? 900 : 700,
    fontFamily: 'Outfit, sans-serif',
    cursor: 'pointer',
    letterSpacing: '-0.01em',
  });

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: '#EEF3F9' }}>

      {/* ── Tab bar + Variante + Vorgänge + Export ── */}
      <div style={{ borderBottom: '1px solid #D8E1EC', background: '#FFFFFF', padding: '0 18px', display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" onClick={() => setStudioTab('projekte')} style={tabStyle('projekte')}>
          Projekte{projects.length ? ` (${projects.length})` : ''}
        </button>
        <button type="button" onClick={() => setStudioTab('bearbeiten')} style={tabStyle('bearbeiten')}>
          Bearbeiten{activeProject ? ` · ${activeEditableProject ? getExposeDocumentTitle(activeEditableProject, activeVariantKey).slice(0, 30) : ''}` : ''}
        </button>

        <span style={{ width: 1, height: 22, background: '#E2E8F0', flex: 'none', margin: '0 4px' }} />

        {/* Vorgänge (Draft management) */}
        <select
          value={selectedDraftId}
          onChange={(event) => setSelectedDraftId(event.target.value)}
          disabled={!savedDrafts.length}
          style={{ ...inputStyle, width: 180, flex: 'none', fontSize: 11, opacity: savedDrafts.length ? 1 : 0.55, margin: '5px 0' }}
        >
          <option value="">{savedDrafts.length ? 'Vorgänge...' : 'Keine Vorgänge'}</option>
          {savedDrafts.map((draft) => {
            const savedAt = new Date(draft.savedAt);
            const labelTime = Number.isNaN(savedAt.getTime()) ? '' : savedAt.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
            return (
              <option key={draft.id} value={draft.id}>
                {getSavedDraftDisplayName(draft, activeVariantKey)} · {draft.projects.length}P{labelTime ? ` · ${labelTime}` : ''}
              </option>
            );
          })}
        </select>
        <button type="button" title="Vorgang laden" onClick={() => selectedDraft && loadNamedDraft(selectedDraft.id)} disabled={!selectedDraft} style={{ ...buttonStyle, opacity: selectedDraft ? 1 : 0.45, minHeight: 30, padding: '5px 8px' }}>
          <FolderOpen size={13} />
        </button>
        <button type="button" title="Vorgang löschen" onClick={() => selectedDraft && deleteNamedDraft(selectedDraft.id)} disabled={!selectedDraft} style={{ ...buttonStyle, opacity: selectedDraft ? 1 : 0.45, minHeight: 30, padding: '5px 8px' }}>
          <Trash2 size={13} />
        </button>
        <button type="button" title="Vorgang speichern" onClick={saveNamedDraft} disabled={!hasCurrentProcessData} style={{ ...buttonStyle, opacity: hasCurrentProcessData ? 1 : 0.45, minHeight: 30, padding: '5px 8px' }}>
          <Save size={13} />
        </button>
        <button type="button" title="Neuer Vorgang" onClick={createNewExposeProcess} style={{ ...buttonStyle, minHeight: 30, padding: '5px 8px' }}>
          <FileText size={13} />
        </button>

        {supabaseSaveStatus !== 'idle' ? (
          <span style={{
            fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', padding: '3px 8px', borderRadius: 6, margin: '5px 0',
            background: supabaseSaveStatus === 'saving' ? '#FEF3C7' : supabaseSaveStatus === 'saved' ? '#D1FAE5' : '#FEE2E2',
            color: supabaseSaveStatus === 'saving' ? '#92400E' : supabaseSaveStatus === 'saved' ? '#065F46' : '#991B1B',
          }}>
            {supabaseSaveStatus === 'saving' ? '⟳ Speichern...' : supabaseSaveStatus === 'saved' ? '✓ Gespeichert' : '✗ Fehler'}
          </span>
        ) : null}

        <span style={{ flex: 1 }} />

        {/* PV*SOL manuell neu einlesen — nur für Deals aus Bitrix */}
        {activeProject?.dealId ? (
          <button
            type="button"
            onClick={handleReloadPvsol}
            disabled={reloadingPvsol}
            title="PV*SOL-Projektdatei aus Bitrix erneut einlesen und Technik-Felder (Modultyp, Modulanzahl, Wechselrichter …) aktualisieren"
            style={{
              ...buttonStyle,
              minHeight: 30,
              padding: '6px 11px',
              fontSize: 11,
              margin: '5px 0',
              opacity: reloadingPvsol ? 0.7 : 1,
              cursor: reloadingPvsol ? 'wait' : 'pointer',
            }}
          >
            {reloadingPvsol ? <Loader2 size={13} /> : <RefreshCw size={13} />}
            {reloadingPvsol ? 'Liest ein…' : 'PV*SOL neu einlesen'}
          </button>
        ) : null}

        {/* Varianten-Umschalter — bestimmt Seitenumfang und Preisfelder */}
        <div style={{ display: 'inline-flex', border: '1px solid #CBD5E1', borderRadius: 8, overflow: 'hidden', background: '#FFFFFF', margin: '5px 0' }}>
          {[
            ['expose-projektverkauf', 'Projektrechte'],
            ['expose-turnkey', 'Turnkey'],
            ['expose-wirtschaftlichkeit', 'Wirtschaftlichkeit'],
          ].map(([key, label], index) => {
            const selected = activeVariantKey === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleVariantSwitch(key)}
                style={{
                  minHeight: 30,
                  padding: '6px 12px',
                  border: 'none',
                  borderLeft: index === 0 ? 'none' : '1px solid #CBD5E1',
                  background: selected ? '#122A5C' : '#FFFFFF',
                  color: selected ? '#FFFFFF' : '#334155',
                  fontSize: 11,
                  fontWeight: 900,
                  fontFamily: 'Outfit, sans-serif',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Sichtbarkeits-Presets — nur im Bearbeiten-Tab, direkt neben dem Varianten-Umschalter */}
        {studioTab === 'bearbeiten' ? (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, margin: '5px 0' }}>
            <select
              value={selectedPresetName}
              onChange={(event) => setSelectedPresetName(event.target.value)}
              title="Sichtbarkeits-Preset auswählen"
              style={{ ...inputStyle, width: 150, flex: 'none', fontSize: 11, margin: 0 }}
            >
              <option value="">— Preset —</option>
              {Object.keys(visibilityPresets)
                .sort((a, b) => presetDisplayLabel(a).localeCompare(presetDisplayLabel(b), 'de'))
                .map((name) => (
                  <option key={name} value={name}>{presetDisplayLabel(name)}</option>
                ))}
            </select>
            <button
              type="button"
              title="Ausgewähltes Preset auf das aktive Projekt anwenden (bei Mehrfachauswahl optional auf alle ausgewählten)"
              onClick={handleApplyVisibilityPreset}
              disabled={!selectedPresetName || !activeProject}
              style={{ ...buttonStyle, opacity: (selectedPresetName && activeProject) ? 1 : 0.45, minHeight: 30, padding: '5px 10px', fontSize: 11 }}
            >
              Anwenden
            </button>
            <button
              type="button"
              title="Feldquellen-Badges (Bitrix, PV*SOL, Standard, Gespeichert, Manuell) ein- oder ausblenden"
              onClick={() => { const next = !showFieldSources; setShowFieldSources(next); persistShowFieldSourcesLocal(next); }}
              style={{
                ...buttonStyle,
                minHeight: 30,
                padding: '5px 10px',
                fontSize: 11,
                background: showFieldSources ? '#122A5C' : '#FFFFFF',
                color: showFieldSources ? '#FFFFFF' : '#334155',
                borderColor: showFieldSources ? '#122A5C' : '#CBD5E1',
              }}
            >
              Quellen
            </button>
          </div>
        ) : null}

        {/* Export-Dropdown - erst, wenn es etwas zu exportieren gibt. Bis zum
            23.09.2026 stand es ausgegraut ueber dem leeren Startbildschirm. */}
        {activeProject || projects.length ? (
        <div ref={exportMenuRef} style={{ position: 'relative', margin: '5px 0' }}>
          <button
            type="button"
            onClick={() => setExportMenuOpen((open) => !open)}
            style={primaryButtonStyle}
          >
            {exporting ? <Loader2 size={14} /> : <Download size={14} />}
            Exportieren
            <ChevronDown size={13} style={{ transform: exportMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
          </button>
          {exportMenuOpen ? (
            <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 60, minWidth: 230, border: '1px solid #D8E1EC', borderRadius: 10, background: '#FFFFFF', boxShadow: '0 10px 28px rgba(15, 32, 68, 0.16)', padding: 6, display: 'grid', gap: 2 }}>
              {[
                { label: 'Druckvorschau', icon: Eye, onClick: printPreview, disabled: !activeProject },
                { label: 'PDF herunterladen', icon: Download, onClick: downloadActivePdf, disabled: !activeProject || exporting },
                { label: 'HTML herunterladen', icon: Download, onClick: downloadActiveHtml, disabled: !activeProject },
                ...(typeof onOpenInTemplateEditor === 'function'
                  ? [{ label: 'Im Vorlagen-Editor öffnen', icon: Wand2, onClick: openActiveProjectInTemplateEditor, disabled: !activeProject }]
                  : []),
                { divider: true },
                { label: `Alle als ZIP (PDF)${projects.length > 1 ? ` · ${projects.length} Projekte` : ''}`, icon: Archive, onClick: downloadZipPdf, disabled: !projects.length || exporting },
                { label: 'Alle als ZIP (HTML)', icon: Archive, onClick: downloadZipHtml, disabled: !projects.length || exporting },
              ].map((item, index) => (item.divider ? (
                <div key={`divider-${index}`} style={{ height: 1, background: '#E2E8F0', margin: '4px 2px' }} />
              ) : (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => { setExportMenuOpen(false); item.onClick(); }}
                  disabled={item.disabled}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 9,
                    width: '100%',
                    minHeight: 34,
                    padding: '8px 10px',
                    border: 'none',
                    borderRadius: 7,
                    background: 'transparent',
                    color: item.disabled ? '#94A3B8' : '#1E293B',
                    fontSize: 12,
                    fontWeight: 700,
                    textAlign: 'left',
                    cursor: item.disabled ? 'default' : 'pointer',
                  }}
                  onMouseEnter={(event) => { if (!item.disabled) event.currentTarget.style.background = '#F1F5F9'; }}
                  onMouseLeave={(event) => { event.currentTarget.style.background = 'transparent'; }}
                >
                  <item.icon size={14} style={{ flex: 'none' }} />
                  {item.label}
                </button>
              )))}
            </div>
          ) : null}
        </div>
        ) : null}
      </div>

      {/* ── Tab content ── */}

      {/* ════ PROJEKTE TAB ════ */}
      {studioTab === 'projekte' ? (
        <div
          onDragOver={(event) => { event.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 18, background: dragActive ? '#FFF7ED' : undefined }}
        >
          {projects.length ? (
            /* Unified project card — merged toolbar header + scrollable grid */
            <div style={{ borderRadius: 14, background: '#FFFFFF', overflow: 'hidden', boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 8px 24px -8px rgba(16,24,40,0.10), 0 0 0 1px rgba(18,42,92,0.08)' }}>
              {/* Card header bar — import + selection actions + stats */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderBottom: '1px solid #EEF2F7', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flex: 'none' }}>
                  <input
                    value={dealIdInput}
                    onChange={(event) => setDealIdInput(event.target.value)}
                    onKeyDown={(event) => { if (event.key === 'Enter') handleLoadBitrixDeals(); }}
                    placeholder="Deal-ID (z. B. 7080, 7314)"
                    disabled={loadingBitrixDeals}
                    style={{ ...inputStyle, width: 188, flex: 'none', borderRadius: '8px 0 0 8px', borderRight: 'none' }}
                  />
                  <button type="button" onClick={handleLoadBitrixDeals} disabled={loadingBitrixDeals} style={{ ...primaryButtonStyle, fontFamily: 'Outfit, sans-serif', borderRadius: '0 8px 8px 0', opacity: loadingBitrixDeals ? 0.72 : 1 }}>
                    {loadingBitrixDeals ? <Loader2 size={14} /> : <Wand2 size={14} />}
                    {loadingBitrixDeals ? 'Lädt...' : 'Deal laden'}
                  </button>
                </div>
                <label style={{ ...buttonStyle, fontFamily: 'Outfit, sans-serif', borderRadius: 8, cursor: readingFiles ? 'wait' : 'pointer', opacity: readingFiles ? 0.7 : 1 }}>
                  {readingFiles ? <Loader2 size={14} /> : <Upload size={14} />}
                  {readingFiles ? 'Liest...' : 'Dateien'}
                  <input type="file" multiple disabled={readingFiles} accept=".pdf,.txt,.csv,.tsv,.json,.md,image/*" onChange={(event) => { handleFiles(event.target.files); event.target.value = ''; }} style={{ display: 'none' }} />
                </label>
                <button type="button" onClick={importProjectsFromRawText} style={{ ...buttonStyle, fontFamily: 'Outfit, sans-serif', borderRadius: 8 }}>
                  <FileText size={14} /> Text einfügen
                </button>
                <span style={{ width: 1, height: 20, background: '#E5EAF1', flex: 'none', margin: '0 2px' }} />
                <button type="button" onClick={selectAll} disabled={!projects.length} style={{ ...buttonStyle, fontFamily: 'Outfit, sans-serif', borderRadius: 8, opacity: projects.length ? 1 : 0.45 }}>
                  <CheckCircle2 size={14} /> Alle
                </button>
                <button type="button" onClick={removeSelectedProjects} disabled={!selectedIds.length} style={{ ...buttonStyle, fontFamily: 'Outfit, sans-serif', borderRadius: 8, opacity: selectedIds.length ? 1 : 0.45, color: selectedIds.length ? '#DC2626' : '#334155', borderColor: selectedIds.length ? '#FCA5A5' : '#CBD5E1' }}>
                  <Trash2 size={14} /> Löschen
                </button>
                <button type="button" onClick={copyTable} disabled={!projects.length} style={{ ...buttonStyle, fontFamily: 'Outfit, sans-serif', borderRadius: 8, opacity: projects.length ? 1 : 0.45 }}>
                  <Clipboard size={14} /> Kopieren
                </button>
                <span style={{ flex: 1 }} />
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 10.5, fontWeight: 700, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap', fontFamily: 'Outfit, sans-serif', letterSpacing: '0.01em', background: '#F1F5F9', color: '#475569' }}>
                    {projects.length} Projekt{projects.length === 1 ? '' : 'e'}
                  </span>
                  <span style={{ fontSize: 10.5, fontWeight: 700, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap', fontFamily: 'Outfit, sans-serif', letterSpacing: '0.01em', background: '#ECFDF3', color: '#15803D' }}>
                    {stats.complete} OK
                  </span>
                  {selectedIds.length ? (
                    <span style={{ fontSize: 10.5, fontWeight: 700, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap', fontFamily: 'Outfit, sans-serif', letterSpacing: '0.01em', background: '#EFF6FF', color: '#2563EB' }}>
                      {selectedIds.length} ausgewählt
                    </span>
                  ) : null}
                  {draftSavedLabel ? (
                    <span style={{ color: draftMediaLimited ? '#B45309' : '#94A3B8', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', fontFamily: 'Outfit, sans-serif' }}>
                      {draftSavedLabel}{draftMediaLimited ? ' · Medien neu wählen' : ''}
                    </span>
                  ) : null}
                </div>
              </div>
              <style>{`
                .epg tbody tr:not(.epg-active):hover { --row-bg: #F5F8FF !important; }
                .epg tbody tr:hover > td:first-child { box-shadow: inset 3px 0 0 #2563EB; }
                .epg tbody tr td { transition: background 0.15s ease; }
                .epg input:not([type="checkbox"]):focus { border-color: #2563EB !important; background: #FFFFFF !important; box-shadow: 0 0 0 2px rgba(37,99,235,0.12); }
                .epg .epg-uai:focus-within { border-color: #2563EB !important; background: #FFFFFF !important; box-shadow: 0 0 0 2px rgba(37,99,235,0.12); }
                .epg .epg-actions { opacity: 0.35; transition: opacity 0.15s; }
                .epg tbody tr:hover .epg-actions { opacity: 1; }
                .epg input[type="checkbox"] { width: 15px; height: 15px; accent-color: #2563EB; cursor: pointer; }
                .epg .epg-sticky-edge { transition: box-shadow 0.2s ease; }
                .epg-scroll.epg-scrolled .epg-sticky-edge { box-shadow: 6px 0 12px -8px rgba(16,24,40,0.16); }
                .epg-scroll::-webkit-scrollbar { height: 8px; width: 8px; }
                .epg-scroll::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 4px; }
                .epg-scroll::-webkit-scrollbar-thumb:hover { background: #94A3B8; }
                .epg-scroll::-webkit-scrollbar-track { background: transparent; }
              `}</style>
              <div
                className="epg-scroll"
                onScroll={(event) => { event.currentTarget.classList.toggle('epg-scrolled', event.currentTarget.scrollLeft > 0); }}
                style={{ overflow: 'auto', maxHeight: 'calc(100vh - 240px)' }}
              >
                <table className="epg" style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', width: '100%', minWidth: TABLE_MIN_WIDTH }}>
                  <colgroup>
                    <col style={{ width: TABLE_STICKY_WIDTHS[0] }} />
                    <col style={{ width: TABLE_STICKY_WIDTHS[1] }} />
                    <col style={{ width: TABLE_STICKY_WIDTHS[2] }} />
                    {TABLE_FIELDS.map((field, fidx) => (
                      /* Letzte Spalte ohne Breite: nimmt Restbreite auf, Sticky-Geometrie bleibt exakt */
                      <col key={field.key} style={fidx === TABLE_FIELDS.length - 1 ? undefined : { width: TABLE_COLUMN_WIDTHS[field.key] || 132 }} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={{ position: 'sticky', top: 0, left: 0, zIndex: 5, height: 44, background: '#122A5C', padding: '0 8px', borderBottom: 'none' }} />
                      <th style={{ position: 'sticky', top: 0, left: 44, zIndex: 5, height: 44, background: '#122A5C', padding: '0 4px', borderBottom: 'none' }} />
                      <th className="epg-sticky-edge" style={{ position: 'sticky', top: 0, left: 98, zIndex: 5, height: 44, background: '#122A5C', padding: '0 12px', color: 'rgba(255,255,255,0.7)', fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: 'left', verticalAlign: 'middle', fontFamily: 'Outfit, sans-serif', borderBottom: 'none' }}>Erkennung</th>
                      {TABLE_FIELDS.map((field) => (
                        <th key={field.key} title={field.label} style={{ position: 'sticky', top: 0, zIndex: 3, height: 44, background: '#122A5C', padding: '4px 12px', color: 'rgba(255,255,255,0.7)', fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: NUMERIC_TABLE_FIELDS.has(field.key) ? 'right' : 'left', verticalAlign: 'middle', fontFamily: 'Outfit, sans-serif', borderBottom: 'none' }}>
                          <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'normal', lineHeight: 1.3 }}>
                            {field.label}{field.required ? <span style={{ color: '#FBBF24', marginLeft: 3, fontSize: 11 }}>*</span> : ''}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project) => {
                      const computedProject = getEditableProject(project, activeVariantKey) || project;
                      const report = completeness(project);
                      const isActive = activeProject?.id === project.id;
                      const isSelected = selectedIds.includes(project.id);
                      const requiredMissing = TABLE_FIELDS.some((field) => field.required && !String(project[field.key] || '').trim());
                      const recognitionHint = project.extractionWarning || (report.missing.length ? report.missing.slice(0, 3).join(', ') : 'vollständig');
                      const pct = report.total ? Math.round((report.filled / report.total) * 100) : 0;
                      const rowBg = isActive ? '#EFF6FF' : isSelected ? '#F6F9FF' : '#FFFFFF';
                      return (
                        <tr
                          key={project.id}
                          className={isActive ? 'epg-active' : undefined}
                          onClick={() => { setActiveId(project.id); setStudioTab('bearbeiten'); }}
                          style={{ '--row-bg': rowBg, background: 'var(--row-bg)', cursor: 'pointer' }}
                        >
                          <td onClick={(event) => event.stopPropagation()} style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--row-bg)', borderBottom: '1px solid #EEF2F7', boxShadow: isActive ? 'inset 3px 0 0 #2563EB' : undefined, padding: '10px 8px', textAlign: 'center', verticalAlign: 'middle' }}>
                            <input type="checkbox" checked={selectedIds.includes(project.id)} onClick={(event) => event.stopPropagation()} onChange={(event) => { event.stopPropagation(); toggleSelection(project.id); }} />
                          </td>
                          <td onClick={(event) => event.stopPropagation()} style={{ position: 'sticky', left: 44, zIndex: 1, background: 'var(--row-bg)', borderBottom: '1px solid #EEF2F7', padding: '6px 2px', textAlign: 'center', verticalAlign: 'middle' }}>
                            <div className="epg-actions" style={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                              <button
                                type="button"
                                title={project.pinned ? 'Dauerhaft gespeichert — klicken zum Lösen' : 'Dauerhaft speichern (kein automatisches Löschen)'}
                                onClick={(event) => { event.stopPropagation(); toggleProjectPinned(project.id); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, borderRadius: 4, color: project.pinned ? '#2563EB' : '#94A3B8', display: 'flex' }}
                              >
                                {project.pinned ? <Pin size={13} /> : <PinOff size={13} />}
                              </button>
                              <button
                                type="button"
                                title="Projekt löschen"
                                onClick={(event) => { event.stopPropagation(); removeProject(project.id); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, borderRadius: 4, color: '#94A3B8', display: 'flex' }}
                                onMouseEnter={(event) => { event.currentTarget.style.color = '#DC2626'; }}
                                onMouseLeave={(event) => { event.currentTarget.style.color = '#94A3B8'; }}
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                          <td className="epg-sticky-edge" style={{ position: 'sticky', left: 98, zIndex: 1, background: 'var(--row-bg)', borderBottom: '1px solid #EEF2F7', borderRight: '1px solid #E5EAF1', padding: '8px 12px', verticalAlign: 'middle' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                              <svg width="26" height="26" viewBox="0 0 26 26" style={{ flex: 'none' }}>
                                <circle cx="13" cy="13" r="10.5" fill="none" stroke="#E5EAF1" strokeWidth="2.5" />
                                <circle cx="13" cy="13" r="10.5" fill="none" stroke={requiredMissing ? '#F59E0B' : '#22C55E'} strokeWidth="2.5" strokeLinecap="round" strokeDasharray={`${(pct / 100) * 65.97} 65.97`} transform="rotate(-90 13 13)" />
                              </svg>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: requiredMissing ? '#B45309' : '#15803D', fontFamily: 'Outfit, sans-serif', lineHeight: 1.25 }}>
                                  {report.filled}/{report.total}
                                </div>
                                <div title={project.extractionWarning || report.missing.join(', ')} style={{ maxWidth: 90, color: project.extractionWarning ? '#B45309' : '#94A3B8', fontSize: 9.5, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {recognitionHint}
                                </div>
                              </div>
                            </div>
                          </td>
                          {TABLE_FIELDS.map((field) => (
                            <td key={field.key} style={{ borderBottom: '1px solid #EEF2F7', borderLeft: '1px solid #F1F5F9', padding: 4, verticalAlign: 'middle' }}>
                              <UnitAwareInput
                                fieldKey={field.key}
                                value={computedProject[field.key] || ''}
                                onClick={(event) => event.stopPropagation()}
                                onPaste={(event) => handleTablePaste(event, project.id, field.key)}
                                onChange={(nextValue) => setProjectValue(project.id, field.key, nextValue)}
                                style={{ ...tableInputStyle, background: field.required && !String(project[field.key] || '').trim() ? '#FFF7ED' : 'transparent', ...(NUMERIC_TABLE_FIELDS.has(field.key) ? { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } : {}) }}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Empty state — prominent landing */
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 280px)' }}>
              <div style={{ border: '2px dashed #CBD5E1', borderRadius: 16, background: '#FFFFFF', padding: '56px 48px', textAlign: 'center', maxWidth: 480 }}>
                <FileText size={40} style={{ color: '#94A3B8', marginBottom: 12 }} />
                <h3 style={{ margin: '0 0 6px', color: '#122A5C', fontSize: 18, fontFamily: 'Outfit, sans-serif', fontWeight: 900 }}>Exposé-Projekt starten</h3>
                <p style={{ margin: '0 0 24px', color: '#64748B', fontSize: 13, lineHeight: 1.5 }}>
                  Lade einen Deal aus Bitrix oder importiere Dateien, um loszulegen.
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <div style={{ display: 'grid', gap: 8, textAlign: 'left' }}>
                    <input
                      value={dealIdInput}
                      onChange={(event) => setDealIdInput(event.target.value)}
                      onKeyDown={(event) => { if (event.key === 'Enter') handleLoadBitrixDeals(); }}
                      placeholder="Deal-ID eingeben..."
                      disabled={loadingBitrixDeals}
                      style={{ ...inputStyle, width: 200 }}
                    />
                    <button type="button" onClick={handleLoadBitrixDeals} disabled={loadingBitrixDeals} style={{ ...primaryButtonStyle, width: '100%', justifyContent: 'center', opacity: loadingBitrixDeals ? 0.72 : 1 }}>
                      {loadingBitrixDeals ? <Loader2 size={14} /> : <Wand2 size={14} />}
                      {loadingBitrixDeals ? 'Lädt...' : 'Deal laden'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', color: '#94A3B8', fontSize: 12, fontWeight: 700, padding: '0 8px' }}>oder</div>
                  <label style={{ ...primaryButtonStyle, cursor: readingFiles ? 'wait' : 'pointer', opacity: readingFiles ? 0.7 : 1, background: '#FFFFFF', color: '#334155', border: '1px solid #CBD5E1', alignSelf: 'center' }}>
                    {readingFiles ? <Loader2 size={14} /> : <Upload size={14} />}
                    {readingFiles ? 'Liest...' : 'Dateien importieren'}
                    <input type="file" multiple disabled={readingFiles} accept=".pdf,.txt,.csv,.tsv,.json,.md,image/*" onChange={(event) => { handleFiles(event.target.files); event.target.value = ''; }} style={{ display: 'none' }} />
                  </label>
                </div>
                <button
                  type="button"
                  onClick={importProjectsFromRawText}
                  style={{ margin: '18px auto 0', display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#2563EB', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0 }}
                >
                  <FileText size={13} /> Stattdessen Rohtext einfügen
                </button>
                <p style={{ margin: '20px 0 0', color: '#94A3B8', fontSize: 11 }}>
                  Dateien können auch per Drag & Drop abgelegt werden.
                </p>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* ════ BEARBEITEN TAB ════ */}
      {studioTab === 'bearbeiten' ? (
        <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'minmax(480px, 0.52fr) minmax(420px, 0.48fr)' }}>
          {/* Left: Editor */}
          <section style={{ minHeight: 0, overflow: 'auto', padding: 18, borderRight: '1px solid #D8E1EC' }}>
            {!activeProject ? (
              <div style={{ border: '1px dashed #CBD5E1', borderRadius: 8, background: '#FFFFFF', padding: 40, color: '#64748B', fontSize: 13, fontWeight: 700, textAlign: 'center' }}>
                <FileText size={28} style={{ marginBottom: 8 }} />
                <div>Kein Projekt ausgewählt. Wechsle zum Tab "Projekte" und klicke eine Zeile an.</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 14 }}>
                {/* Status bar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <h3 style={{ margin: 0, color: '#122A5C', fontSize: 17, fontFamily: 'Outfit, sans-serif', fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {getExposeDocumentTitle(activeEditableProject, activeVariantKey)}
                    </h3>
                    {activeVariantKey === TURNKEY_VARIANT_KEY ? (
                      <p style={{ margin: '3px 0 0', color: activeHasTurnkeyDataset ? '#15803D' : '#B45309', fontSize: 11, fontWeight: 700 }}>
                        {activeHasTurnkeyDataset ? 'Eigener Turnkey-Datensatz aktiv' : 'Turnkey nutzt die Projektrechte-Daten'}
                      </p>
                    ) : null}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {assistantStatusChip(activeRequiredMissingFields.length ? 'bad' : 'ok', activeRequiredMissingFields.length ? `${activeRequiredMissingFields.length} Pflicht offen` : 'OK')}
                    {assistantStatusChip(activeEditableProject?.projectImage ? 'ok' : 'bad', activeEditableProject?.projectImage ? 'Bild' : 'Bild fehlt')}
                    <span style={{ color: '#64748B', fontSize: 11, fontWeight: 700, padding: '4px 6px' }}>{projectReadiness}%</span>
                  </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                  <button type="button" onClick={handleRawTextReparse} style={buttonStyle}><Wand2 size={14} /> Neu erkennen</button>
                  <button type="button" onClick={copyActiveProjectToTurnkeyDataset} style={buttonStyle}><Copy size={14} /> Nach Turnkey</button>
                  {activeVariantKey === TURNKEY_VARIANT_KEY && activeHasTurnkeyDataset ? (
                    <button type="button" onClick={copyActiveTurnkeyToProjectRights} style={buttonStyle}><Copy size={14} /> Nach Projektrechte</button>
                  ) : null}
                  {activeHasTurnkeyDataset ? (
                    <button type="button" onClick={removeTurnkeyDataset} style={buttonStyle}><Trash2 size={14} /> Turnkey trennen</button>
                  ) : null}
                </div>

                {/* Field filter */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ display: 'inline-flex', border: '1px solid #CBD5E1', borderRadius: 8, overflow: 'hidden', background: '#FFFFFF' }}>
                    {[['open', 'Nur offene'], ['all', 'Alle Felder']].map(([mode, label]) => {
                      const selected = assistantFieldFilter === mode;
                      return (
                        <button key={mode} type="button" onClick={() => setAssistantFieldFilter(mode)} style={{ minHeight: 32, padding: '7px 12px', border: 'none', borderLeft: mode === 'open' ? 'none' : '1px solid #CBD5E1', background: selected ? '#122A5C' : '#FFFFFF', color: selected ? '#FFFFFF' : '#334155', fontSize: 11, fontWeight: 900, cursor: 'pointer' }}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <span style={{ color: '#64748B', fontSize: 11, fontWeight: 700 }}>{activeOpenFields.length} offene Felder</span>
                  <span style={{ width: 1, height: 18, background: '#E2E8F0', flex: 'none' }} />
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 11, fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>
                    <span
                      role="switch"
                      aria-checked={showExtendedFields}
                      tabIndex={0}
                      onClick={() => { const next = !showExtendedFields; setShowExtendedFields(next); persistShowExtendedFieldsSupabase(next); }}
                      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); const next = !showExtendedFields; setShowExtendedFields(next); persistShowExtendedFieldsSupabase(next); } }}
                      style={{ position: 'relative', display: 'inline-block', width: 34, height: 18, borderRadius: 9, background: showExtendedFields ? '#E07000' : '#CBD5E1', transition: 'background 0.2s' }}
                    >
                      <span style={{ position: 'absolute', top: 2, left: showExtendedFields ? 18 : 2, width: 14, height: 14, borderRadius: '50%', background: '#FFFFFF', boxShadow: '0 1px 2px rgba(0,0,0,0.18)', transition: 'left 0.2s' }} />
                    </span>
                    {showExtendedFields ? 'Alle Felder' : 'Nur Vorlagen-Felder'}
                  </label>
                </div>

                {/* Assistant field groups + Detail fields — unified flow */}
                <div style={{ display: 'grid', gap: 12 }}>
                  {activeAssistantReports.map(renderAssistantGroup)}

                  {/* Detail fields header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid #D8E1EC', paddingTop: 12 }}>
                    <p style={{ margin: 0, color: '#64748B', fontSize: 11, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Felder — {EXPOSE_VARIANT_PRESET_LABELS[activeVariantKey] || 'Alle'}</p>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <label style={{ position: 'relative', display: 'block' }}>
                        <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#64748B', pointerEvents: 'none' }} />
                        <input value={detailFieldSearch} onChange={(event) => setDetailFieldSearch(event.target.value)} placeholder="Suchen..." style={{ ...inputStyle, paddingLeft: 28, width: 150, fontSize: 11 }} />
                      </label>
                      <div style={{ display: 'inline-flex', border: '1px solid #CBD5E1', borderRadius: 6, overflow: 'hidden', background: '#FFFFFF' }}>
                        {[['all', 'Alle'], ['active', 'Aktiv'], ['hidden', 'Aus']].map(([mode, label]) => {
                          const selected = detailVisibilityFilter === mode;
                          return (
                            <button key={mode} type="button" onClick={() => setDetailVisibilityFilter(mode)} style={{ minHeight: 28, padding: '5px 8px', border: 'none', borderLeft: mode === 'all' ? 'none' : '1px solid #CBD5E1', background: selected ? '#122A5C' : '#FFFFFF', color: selected ? '#FFFFFF' : '#334155', fontSize: 10, fontWeight: 900, cursor: 'pointer' }}>
                              {label}
                            </button>
                          );
                        })}
                      </div>
                      <span style={{ color: '#64748B', fontSize: 10, fontWeight: 700 }}>{visibleDetailFieldCount}</span>
                      <span style={{ width: 1, height: 18, background: '#E2E8F0', flex: 'none' }} />
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 10, fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>
                        <span
                          role="switch"
                          aria-checked={showExtendedFields}
                          tabIndex={0}
                          onClick={() => { const next = !showExtendedFields; setShowExtendedFields(next); persistShowExtendedFieldsSupabase(next); }}
                          onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); const next = !showExtendedFields; setShowExtendedFields(next); persistShowExtendedFieldsSupabase(next); } }}
                          style={{ position: 'relative', display: 'inline-block', width: 34, height: 18, borderRadius: 9, background: showExtendedFields ? '#E07000' : '#CBD5E1', transition: 'background 0.2s' }}
                        >
                          <span style={{ position: 'absolute', top: 2, left: showExtendedFields ? 18 : 2, width: 14, height: 14, borderRadius: '50%', background: '#FFFFFF', boxShadow: '0 1px 2px rgba(0,0,0,0.18)', transition: 'left 0.2s' }} />
                        </span>
                        Erweiterte Felder
                      </label>
                    </div>
                  </div>

                  {/* Detail field groups as cards */}
                  {visibleDetailFieldGroups.length ? visibleDetailFieldGroups.map((group) => (
                    <div key={group.title} style={{ ...assistantPanelStyle, overflow: 'hidden' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 60px', gap: 6, padding: '8px 10px', background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', color: '#122A5C', fontSize: 10, fontWeight: 900 }}>
                        <span>{group.title}</span><span>Wert</span><span style={{ textAlign: 'center' }}>Ein</span>
                      </div>
                      {group.fields.map((field) => {
                        const hidden = isProjectFieldHidden(activeEditableProject, field.key);
                        const fieldLocked = Array.isArray(activeProject?.__lockedFields) && activeProject.__lockedFields.includes(field.key);
                        const colorField = isColorField(field.key);
                        const colorValue = String(activeEditableProject?.[field.key] || '');
                        const normalizedColor = normalizeHexColor(colorValue) || '#122A5C';
                        return (
                          <div key={field.key} style={{ display: 'grid', gridTemplateColumns: '140px minmax(0, 1fr) 60px', gap: 6, alignItems: 'center', minHeight: 34, padding: '4px 10px', borderTop: '1px solid #F1F5F9' }}>
                            <label htmlFor={`${activeProject.id}-${field.key}`} style={{ color: '#475569', fontSize: 10.5, fontWeight: 700, lineHeight: 1.25, display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 3 }}>
                              {field.label}
                              {CALCULATED_FIELD_KEYS.has(field.key) && String(activeEditableProject?.[field.key] || '').trim() ? <span style={{ padding: '1px 4px', borderRadius: 3, background: '#DBEAFE', color: '#1D4ED8', fontSize: 8, fontWeight: 900 }}>auto</span> : null}
                              {renderFieldSourceBadge(activeEditableProject, field.key)}
                              <button type="button" title={fieldLocked ? 'Feld ist geschützt (Klick zum Entsperren)' : 'Feld vor Überschreiben schützen'} onClick={() => toggleFieldLock(activeProject.id, field.key)} style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', fontSize: 10, lineHeight: 1, opacity: fieldLocked ? 1 : 0.3 }}>{fieldLocked ? '\u{1F512}' : '\u{1F513}'}</button>
                            </label>
                            {colorField ? (
                              <div style={{ display: 'grid', gridTemplateColumns: '30px minmax(0, 1fr)', gap: 6, alignItems: 'center', opacity: hidden ? 0.58 : 1 }}>
                                <input type="color" value={normalizedColor} onChange={(event) => setActiveProjectDetailValue(activeProject.id, field.key, event.target.value)} style={{ width: 30, height: 28, padding: 2, border: '1px solid #CBD5E1', borderRadius: 5, background: '#FFFFFF', cursor: 'pointer' }} />
                                <input id={`${activeProject.id}-${field.key}`} value={colorValue} placeholder="#122A5C" onChange={(event) => setActiveProjectDetailValue(activeProject.id, field.key, event.target.value)} style={inputStyle} />
                              </div>
                            ) : STATUS_FIELD_OPTIONS[field.key] ? (
                              <select
                                id={`${activeProject.id}-${field.key}`}
                                value={activeEditableProject?.[field.key] || ''}
                                onChange={(event) => setActiveProjectDetailValue(activeProject.id, field.key, event.target.value)}
                                style={{ ...inputStyle, background: '#FFFFFF', opacity: hidden ? 0.58 : 1, fontSize: 11, cursor: 'pointer' }}
                              >
                                <option value="">— nicht angegeben —</option>
                                {STATUS_FIELD_OPTIONS[field.key].map((option) => (
                                  <option key={option} value={option}>{option}</option>
                                ))}
                                {activeEditableProject?.[field.key] && !STATUS_FIELD_OPTIONS[field.key].includes(activeEditableProject[field.key]) ? (
                                  <option value={activeEditableProject[field.key]}>{activeEditableProject[field.key]}</option>
                                ) : null}
                              </select>
                            ) : (
                              <UnitAwareInput fieldKey={field.key} id={`${activeProject.id}-${field.key}`} value={activeEditableProject?.[field.key] || ''} onChange={(nextValue) => setActiveProjectDetailValue(activeProject.id, field.key, nextValue)} style={{ ...inputStyle, opacity: hidden ? 0.58 : 1, fontSize: 11 }} />
                            )}
                            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, color: hidden ? '#94A3B8' : '#122A5C', fontSize: 10, fontWeight: 700 }}>
                              <input type="checkbox" checked={!hidden} onChange={(event) => setProjectFieldHidden(activeProject.id, field.key, !event.target.checked)} />
                              {hidden ? 'Aus' : 'Ein'}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  )) : (
                    <div style={{ ...assistantPanelStyle, padding: 14, color: '#64748B', fontSize: 11, fontWeight: 750, textAlign: 'center' }}>
                      Keine passenden Felder.
                    </div>
                  )}
                </div>

                {/* Raw text — hidden by default, internal data */}
              </div>
            )}
          </section>

          {/* Right: Preview */}
          <section style={{ minHeight: 0, overflow: 'auto', padding: '18px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 11, fontWeight: 700, color: '#475569', userSelect: 'none' }}>
                Leere Felder ausblenden
                <span
                  role="switch"
                  aria-checked={hideEmptyFields}
                  tabIndex={0}
                  onClick={() => setHideEmptyFields((v) => !v)}
                  onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setHideEmptyFields((v) => !v); } }}
                  style={{ position: 'relative', display: 'inline-block', width: 34, height: 18, borderRadius: 9, background: hideEmptyFields ? '#E07000' : '#CBD5E1', transition: 'background 0.2s' }}
                >
                  <span style={{ position: 'absolute', top: 2, left: hideEmptyFields ? 18 : 2, width: 14, height: 14, borderRadius: '50%', background: '#FFFFFF', boxShadow: '0 1px 2px rgba(0,0,0,0.18)', transition: 'left 0.2s' }} />
                </span>
              </label>
            </div>
            <div className="proposal-staging" data-proposal-canvas="true" style={{ background: 'transparent', padding: 0 }}>
              {activeProject ? (
                <ExposePreview data={activeData} variantKey={activeVariantKey} onFieldChange={handleExposeFieldChange} hideEmptyFields={hideEmptyFields} />
              ) : (
                <div style={{ minHeight: 420, border: '1px dashed #CBD5E1', borderRadius: 8, background: '#FFFFFF', display: 'grid', placeItems: 'center', color: '#64748B', fontWeight: 700 }}>
                  <div style={{ textAlign: 'center' }}><FileText size={28} /><div style={{ marginTop: 8 }}>Keine Vorschau</div></div>
                </div>
              )}
            </div>
          </section>
        </div>
      ) : null}

      {/* Hidden export renders */}
      <div aria-hidden="true" style={{ position: 'fixed', left: -20000, top: 0, width: '210mm', height: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        {projects.map((project) => (
          <div key={project.id} ref={(node) => { if (node) exportRefs.current[project.id] = node; }} data-bulk-export-id={project.id}>
            <ExposePreview data={buildExposeData(project, activeVariantKey)} variantKey={activeVariantKey} exportMode hideEmptyFields={hideEmptyFields} />
          </div>
        ))}
      </div>

      {/* Inline dialog modal — replaces window.confirm / window.prompt */}
      {dialog ? (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15, 23, 42, 0.45)' }} onClick={dialog.onCancel}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#FFFFFF', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: '28px 32px', maxWidth: dialog.type === 'textarea' ? 560 : 420, width: '90vw' }}>
            <p style={{ margin: '0 0 18px', color: '#1E293B', fontSize: 14, fontWeight: 600, lineHeight: 1.5 }}>{dialog.message}</p>
            {dialog.type === 'prompt' ? (
              <input
                ref={dialogInputRef}
                autoFocus
                defaultValue={dialog.defaultValue}
                onKeyDown={(e) => { if (e.key === 'Enter') dialog.onConfirm(e.target.value); if (e.key === 'Escape') dialog.onCancel(); }}
                style={{ ...inputStyle, width: '100%', marginBottom: 18, fontSize: 13 }}
              />
            ) : null}
            {dialog.type === 'textarea' ? (
              <textarea
                ref={dialogInputRef}
                autoFocus
                rows={8}
                defaultValue={dialog.defaultValue}
                style={{ ...inputStyle, width: '100%', marginBottom: 18, fontSize: 13, resize: 'vertical', lineHeight: 1.5 }}
              />
            ) : null}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={dialog.onCancel} style={{ ...buttonStyle, minWidth: 80, justifyContent: 'center' }}>Abbrechen</button>
              <button
                type="button"
                autoFocus={dialog.type === 'confirm'}
                onClick={() => (dialog.type === 'prompt' || dialog.type === 'textarea') ? dialog.onConfirm(dialogInputRef.current?.value ?? '') : dialog.onConfirm()}
                style={{ ...primaryButtonStyle, minWidth: 80, justifyContent: 'center' }}
              >
                {dialog.type === 'prompt' ? 'Speichern' : dialog.type === 'textarea' ? 'Erkennen' : 'Bestätigen'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
